import React, { useEffect, useRef } from "react";
import { Routes, Route, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import MenuPage from "./pages/menu/main/MenuPage.js";
import CartPage from "./pages/cart/main/CartPage.js";
import OrderStatusPage from "./pages/order-status/main/OrderStatusPage.js";
import Messages from "./common/ui/Messages.js"
import * as CommonUtilities from "./common/utils/CommonUtils.js";
import moment from"moment";
import brandColourRequest from "./common/JSONs/BrandColour.js";
import { getYear, getMonth, addDays, subDays, setHours, setMinutes, getDay, getHours } from "date-fns";

// Always redirect /orderstatus and /cart to their ?s8view= equivalents so routes work in both
// standalone and injection mode. Uses currentpage param for injection base path if present.
const _pathname = window.location.pathname.toLowerCase();
if (_pathname === '/orderstatus' || _pathname === '/cart') {
    const _redirectParams = new URLSearchParams(window.location.search);
    const _currentpage = _redirectParams.get('currentpage');
    const _basePath = _currentpage ? '/' + _currentpage : '/';
    _redirectParams.set('s8view', _pathname === '/cart' ? 'cart' : 'orderstatus');
    window.location.replace(_basePath + '?' + _redirectParams.toString());
}

export default function App() {

    // --------------------------------------------------------
    // Sections are divided by area in system, ordered via:
    // State > useEffects > Functions (with some exceptions)
    // 2025-07-02_1
    // --------------------------------------------------------

    // ====================== begin: Generic ====================== 
    
    // ---------- State: Location Alert Modal ----------
    const [alertModal, setAlertModal] = React.useState({
        ShowAlertModal: false,
        LocationIdIfConfirm: "",
        PreviousLocationClearCart: "",
        ClearCart: false, // Triggers useEffect that calls clearCart()
        ClearMemberDetails: false,
        StartNewOrder: false,
        Message: Messages.LocationWarnChange,
        IsInitialLocation: true
    });

    // ---------- State: Generic - Error Modal ----------
    const [errorModal, setErrorModal] = React.useState({
        Message: Messages.ErrorGeneric,
        IsShown: false,
        IsError: false
    });

    // ---------- Function - Generic: Close Error Modal ----------
    function handleCloseErrorModal() {
        setErrorModal((prevState) => {
            return {
                ...prevState,
                IsShown: false
            }
        })
    }

    // ---------- State: Generic - Offline Modal ----------
    const [offlineModal, setOfflineModal] = React.useState({
        IsShown: false,
    });
    
    // ---------- Function - Generic: Close Offline Modal ----------
    function handleCloseOfflineModal() {
        setOfflineModal({
            IsShown: false
        });
    }

    // ====================== end: Generic ====================== 

    // ====================== begin: Required at beginning ====================== 

    const [locationList, setLocationList] = React.useState({
        original: [],
        filtered: [],
        listLoaded: false,
        deliveryListLoaded: false,
        searchFilterText: "",
        showOpenOnly: false,
        showClearButton: false
    });
    
    // ====================== end: Required at beginning ====================== 

    // ====================== begin: Pickup / Delivery ======================
    
    // ---------- State: Pickup / Delivery ----------
    const [pickupOrDelivery, setPickupOrDelivery] = React.useState({
        Config: {
            EnableDoorDashWhiteLabelDelivery: CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery, // Brand Settings
        },
        SwitchMenuNav: {
            SwitchPickupDeliveryValue: null, // pickup / delivery
            MenuTypeEndpointParam: null // pickup = app / webordering; delivery = delivery (adds to API Endpoints)
        },
        PickupDeliveryModal: {
            DeliveryAvailabilityHasBeenChecked: false,
            ModalIsShown: false,
            ModalPageShown: 'Method', // Method / Address
            ShowBackArrowPickupLocations: false
        },
        DeliveryAddressFields: {
            streetnumber: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            unitnumber: {
                Value: '',
                IsRequired: false,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            streetname: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            suburb: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            state: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            postcode: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            deliveryinstructions: {
                Value: '',
                IsRequired: false,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            }
        },
        DeliveryAddressError: {
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: false,
            GetDoordashLocations: false
        },
        MethodChangeModal: {
            ModalIsShown: false,
            ConfirmMethodChange: false // Confirmation modal to switch between collection method - will be used in refinement
        },
        DeliveryStatus: {
            EstimateResponseCode: 0,
            LocationIndex: 0,
            LocationID: 0,
            EstimtePickupTime: null,
            EstimateDeliveryTime: null,
            //EstimateDeliveryMinutes: null,
            DoordashDeliveryFee: null,
            GetProductMenu: false,
            DoordashObject: null
        },
        ServerCalls: {
            PatchCustomerDeliveryAddress: false // Triggers useEffect that calls patchCustomerDeliveryAddress()
        }
    });

    // ---------- useEffect: Check if Web & has Delivery ----------
    React.useEffect(() => {
        setUrlConfig((prevState) => {
            let memberIdIsNull = urlConfig.MemberId == null || urlConfig.MemberId == 'null';
            return ({
                ...prevState,
                ServerCalls: {
                    ...prevState.ServerCalls,
                    GetUserCart: memberIdIsNull // Triggers useEffect to call getCartDetails()
                }
            })
        })
        if (pickupOrDelivery.PickupDeliveryModal.DeliveryAvailabilityHasBeenChecked === false) {
            if (urlConfig.Type == '' || urlConfig.Type == null || urlConfig.Type == 'null' || urlConfig.Type == 'web') {
                if (CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery) {
                    let hasMenuType = urlConfig.MenuType !== null && urlConfig.MenuType !== 'null'; // Method not set - (will be in initial run)
                    let showModal;
                    let menuTypeParam;
                    if (hasMenuType) {
                        if (urlConfig.MenuType === 'pickup') {
                            showModal = false;
                            menuTypeParam = CommonUtilities.StaticFields.PickupMenuType;
                        } else if (urlConfig.MenuType === 'delivery') {
                            showModal = true;
                            menuTypeParam = CommonUtilities.StaticFields.DeliveryMenuType;
                        }
                    } else {
                        showModal = true;
                    }
                    // Show Delivery Modal
                    setPickupOrDelivery((prevState) => {
                        return({
                            ...prevState,
                            SwitchMenuNav: {
                                ...prevState.SwitchMenuNav,
                                SwitchPickupDeliveryValue: urlConfig.MenuType,
                                MenuTypeEndpointParam: menuTypeParam
                            },
                            PickupDeliveryModal: {
                                ...prevState.PickupDeliveryModal,
                                ModalIsShown: showModal,
                                DeliveryAvailabilityHasBeenChecked: true,
                                ShowBackArrowPickupLocations: true
                            }
                        })
                    });
                } else {
                    setPickupOrDelivery((prevState) => {
                        return({
                            ...prevState,
                            SwitchMenuNav: {
                                ...prevState.SwitchMenuNav,
                                SwitchPickupDeliveryValue: 'pickup', // Default pickup
                                MenuTypeEndpointParam: CommonUtilities.StaticFields.PickupMenuType
                            },
                            PickupDeliveryModal: {
                                ...prevState.PickupDeliveryModal,
                                ModalIsShown: false,
                                DeliveryAvailabilityHasBeenChecked: true
                            }
                        })
                    });
                    setUrlConfig((prevState) => {
                        return({
                            ...prevState,
                            MenuType: 'pickup'
                        })
                    });
                }
            } else {
                setPickupOrDelivery((prevState) => {
                    return({
                        ...prevState,
                        SwitchMenuNav: {
                            ...prevState.SwitchMenuNav,
                            SwitchPickupDeliveryValue: 'pickup', // Default pickup
                            MenuTypeEndpointParam: CommonUtilities.StaticFields.PickupMenuType
                        },
                        PickupDeliveryModal: {
                            ...prevState.PickupDeliveryModal,
                            ModalIsShown: false,
                            DeliveryAvailabilityHasBeenChecked: true
                        }
                    })
                });
                setUrlConfig((prevState) => {
                    return({
                        ...prevState,
                        MenuType: 'pickup'
                    })
                });
            }
        } 
    }, []);

    // ---------- Functions: Pickup / Delivery ----------
    function handleSelectMethodPickupDeliveryModal(e) {
        let buttonValue = e.currentTarget.getAttribute('data-value'); // pickup / delivery
        let pageShown = buttonValue === 'delivery' ? 'Address' : 'Method';
        let modalShown = buttonValue === 'delivery' ? true : false;
        let menuTypeParam = buttonValue === 'delivery' ? CommonUtilities.StaticFields.DeliveryMenuType : CommonUtilities.StaticFields.PickupMenuType;

        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                SwitchMenuNav: {
                    ...prevState.SwitchMenuNav,
                    SwitchPickupDeliveryValue: buttonValue,
                    MenuTypeEndpointParam: menuTypeParam
                },
                PickupDeliveryModal: {
                    ...prevState.PickupDeliveryModal,
                    ModalIsShown: modalShown,
                    ModalPageShown: pageShown
                }
            })
        });
        setUrlConfig((prevState) => {
            return({
                ...prevState,
                MenuType: buttonValue
            })
        });
        if (buttonValue === 'pickup') {
            checkLocationParams();
            fetchLocationList();
        }
    }

    function handleBackPickupLocationsToMethodClick() {
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                PickupDeliveryModal: {
                    ...prevState.PickupDeliveryModal,
                    ModalIsShown: true,
                    ModalPageShown: 'Method'
                }
            })
        })
        setShowLocModal(false);
    }

    function handleBackAddressToMethodClick() {
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                PickupDeliveryModal: {
                    ...prevState.PickupDeliveryModal,
                    ModalPageShown: 'Method'
                }
            })
        })
    }

    // Delivery Address Fields
    function handleChangeAddressFields(e) {
        const { name, value } = e.currentTarget;
        
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                DeliveryAddressFields: {
                    ...prevState.DeliveryAddressFields,
                    [name]: {
                        ...prevState.DeliveryAddressFields[name],
                        Value: value
                    }
                }
            })
        })
    }

    function handleSubmitAddressFields() {
        let allFieldsHaveError = false;
        let allFieldsHaveErrorMessage = Messages.ErrorProductForm;
        // Reset General Error
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                DeliveryAddressError: {
                    ...prevState.DeliveryAddressError,
                    HasError: false,
                    ButtonsDisabled: true,
                    GetDoordashLocations: false
                }
            })
        })

        // Validate Delivery Address Fields
        let addressFieldsObj = pickupOrDelivery.DeliveryAddressFields;
        let addressFieldsArr = Object.entries(addressFieldsObj).map((parentField) => {
            let childFieldsObj = parentField[1];
            // Check Validation
            let hasError = false;
            let errorMessage = Messages.ErrorFieldRequired;
            if (childFieldsObj.IsRequired) {
                if (childFieldsObj.Value === '' || childFieldsObj.Value === null) {
                    hasError = true;
                    allFieldsHaveError = true;
                    errorMessage = Messages.ErrorFieldRequired;
                }
            }
            
            // Map fields
            let innerFields = Object.entries(childFieldsObj).map((childFields) => {
               if (childFields[0] === "HasError") {
                return ["HasError", hasError]
               } else {
                   return childFields
               }
            })

            return [
                parentField[0], 
                Object.fromEntries(innerFields)
            ]
        })
        let validatedParentObj = Object.fromEntries(addressFieldsArr);
        
        // Update State
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                DeliveryAddressFields: validatedParentObj,
                DeliveryAddressError: {
                    ...prevState.DeliveryAddressError,
                    HasError: allFieldsHaveError,
                    ErrorMessage: allFieldsHaveErrorMessage,
                    ButtonsDisabled: !allFieldsHaveError,
                    GetDoordashLocations: !allFieldsHaveError // Triggers useEffect to call fetchDoordashLocations()
                }
            })
        })
    }

    // Call Doordash Location List
    React.useEffect(() => {
        if (pickupOrDelivery.DeliveryAddressError.GetDoordashLocations) {
            setPickupOrDelivery((prevState) => {
                return({
                    ...prevState,
                    DeliveryAddressError: {
                        ...prevState.DeliveryAddressError,
                        HasError: false
                    }
                })
            })
            fetchDoordashLocations();
        }
    }, [pickupOrDelivery.DeliveryAddressError.GetDoordashLocations])

    function fetchDoordashLocations() {
        // Reset location call trigger
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                DeliveryAddressError: {
                    ...prevState.DeliveryAddressError,
                    GetDoordashLocations: false
                }
            })
        })

        //fetch(`${CommonUtilities.StaticFields.APIDomain}LocationList?BrandCode=${CommonUtilities.StaticFields.BrandCode}${geoLocation.LocationURL}&ViewMenuStore=1&Num=10&Radius=10&DoorDashDeliveryOnly&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
        fetch(`${CommonUtilities.StaticFields.APIDomain}LocationList?BrandCode=${CommonUtilities.StaticFields.BrandCode}${geoLocation.LocationURL}&ViewMenuStore=1&DoorDashDeliveryOnly=1&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "BrandUID": CommonUtilities.StaticFields.BrandUID,
                "BrowserSecUID": "CBD3A11B-D3A7-4695-B1A3-891678B8307D"
            },
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                const responseLocationList = data.locationList;
                // Delivery - no catering check
                let locationListExclude = responseLocationList.filter((item) => item.locationNumber !== 9988 && item.locationNumber !== 9977 && item.tyroPaymentS8AppLocationID !== "" && item.isAllowOnlineOrder); // All locations excluding 9988, 9977, blank Tyro IDs & online Stores
                //let locationListExclude = responseLocationList; // Useful to test looping through locations
                setLocationList((prevState) => {
                    return({
                        ...prevState,
                        original: locationListExclude,
                        filtered: locationListExclude,
                        listLoaded: true, // This triggers CheckParams Location only if pickup
                        deliveryListLoaded: true, // Trigger useEffect loop through delivery locations - loopinghere()
                        searchFilterText: "",
                        showOpenOnly: false,
                        showClearButton: false
                    })
                });
            } else {
                setPickupOrDelivery((prevState) => {
                    return({
                        ...prevState,
                        DeliveryAddressError: {
                            ...prevState.DeliveryAddressError,
                            HasError: true,
                            ErrorMessage: Messages.ErrorGeneric,
                            ButtonsDisabled: false
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setPickupOrDelivery((prevState) => {
                return({
                    ...prevState,
                    DeliveryAddressError: {
                        ...prevState.DeliveryAddressError,
                        HasError: true,
                        ErrorMessage: Messages.ErrorGeneric,
                        ButtonsDisabled: false
                    }
                })
            })
        })
    }

    // Controls looping through delivery location list loopinghere()
    React.useEffect(() => {
        if (locationList.deliveryListLoaded) {
            if (pickupOrDelivery.DeliveryStatus.EstimateResponseCode !== 200) { // *** For Doordash URL ***
                if (pickupOrDelivery.DeliveryStatus.LocationIndex < locationList.original.length) {
                    let pickupTimeDateUtc = `${moment().utc().format('YYYY-MM-DDTHH:mm:ss')}`;
                    let pickupTimeZone = `${moment().format('Z').replace(':', '')}`;
                    let pickupTime = `${pickupTimeDateUtc}${pickupTimeZone}`;
                    let doordashEstimateObj = {
                        pickup_address: {
                            city: locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationSuburb,
                            state: locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationState,
                            street: locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationAddress1,
                            unit: locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationAddress2,
                            zip_code: locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationPostCode,
                            full_address: `${locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationAddress1}, ${locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationAddress2}, ${locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationSuburb}, ${locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationState} ${locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationPostCode}`
                        },
                        dropoff_address: {
                            city: pickupOrDelivery.DeliveryAddressFields.suburb.Value,
                            state: pickupOrDelivery.DeliveryAddressFields.state.Value,
                            street: `${pickupOrDelivery.DeliveryAddressFields.streetnumber.Value} ${pickupOrDelivery.DeliveryAddressFields.streetname.Value}`,
                            unit: pickupOrDelivery.DeliveryAddressFields.unitnumber.Value,
                            zip_code: pickupOrDelivery.DeliveryAddressFields.postcode.Value,
                            full_address: `${pickupOrDelivery.DeliveryAddressFields.streetnumber.Value} ${pickupOrDelivery.DeliveryAddressFields.streetname.Value}, ${pickupOrDelivery.DeliveryAddressFields.suburb.Value}, ${pickupOrDelivery.DeliveryAddressFields.state.Value} ${pickupOrDelivery.DeliveryAddressFields.postcode.Value}`
                        },
                        order_value: 0,
                        pickup_time: pickupTime,
                        external_store_id: locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationNumber ?? -1,
                        external_business_name: locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].doorDashExternalBusinessName
                    }
                    
                    let doorDashUrl = locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].doorDashWhiteLabelDeliveryAPIUrl;
                    let doordashOauth = locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].doorDashWhiteLabelDeliveryOauthToken;
                    let userAgent = locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].doorDashWhiteLabelDeliveryUserAgent;
                    let lastLocationInList = pickupOrDelivery.DeliveryStatus.LocationIndex == locationList.original.length - 1 ? true : false;
                    let locationId = locationList.original[pickupOrDelivery.DeliveryStatus.LocationIndex].locationNumber ?? -1;
                    getDoordashEstimate(doorDashUrl, doordashOauth, userAgent, doordashEstimateObj, lastLocationInList, locationId);
                } else if (locationList.original.length === 0) {
                    setPickupOrDelivery((prevState) => {
                        return({
                            ...prevState,
                            DeliveryAddressError: {
                                ...prevState.DeliveryAddressError,
                                HasError: true,
                                ErrorMessage: Messages.ErrorDeliveryUnavailable,
                                ButtonsDisabled: false
                            }
                        })
                    })
                }
            }
        }
    }, [locationList.deliveryListLoaded, pickupOrDelivery.DeliveryStatus.LocationIndex])

    // === Post - Get Doordash Estimate response ===
    function getDoordashEstimate(doordashUrl, doordashOauth, userAgent, doordashEstimateObj, lastLocationInList, locationId) {
        //let estimateStatus; // *** For Doordash URL ***
        //fetch(`${doordashUrl}estimates`, { // *** For Doordash URL ***
        fetch(`${CommonUtilities.StaticFields.APIDomain}DoorDashEstimates?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${locationId}&MemberId=${urlConfig.MemberId !== null ? urlConfig.MemberId : 0}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, { 
            method: "POST", 
            // headers: { // *** For Doordash URL ***
            //     Accept: "application/json",
            //     "Content-Type": "application/json",
            //     "Authorization": `Bearer ${doordashOauth}`,
            //     "User-Agent": userAgent
            // },
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            },
            body: JSON.stringify(doordashEstimateObj)
        })
        .then((response) => {
            //estimateStatus = response.status; // *** For Doordash URL ***
            return response.json()
        })
        .then((data) => {
            if (data.isRequestSuccessful) {
            //if (estimateStatus === 200) { // *** For Doordash URL ***

                // === Successful Doordash Estimate Found (Stop loop) ===

                // Create post object for Customer Details
                setCustomerDetailsObjToPost((prevState) => {
                    return ({
                        ...prevState,
                        PostObject: {
                            ...prevState.PostObject,
                            DoorDashEstimatePayload: doordashEstimateObj
                        }
                    })
                })
                
                let doordashResponseObj = JSON.parse(data.doorDashResponse);
                // Trigger close

                // Calculate Estimate in minutes
                // let timeNow = `${moment().format('YYYY-MM-DDTHH:mm:ss')}Z`;
                // let deliveryTime = moment(doordashResponseObj.delivery_time);
                // let estimateInMinutes = deliveryTime.diff(timeNow, 'minutes');
                
                let utcPickupTime = doordashResponseObj.pickup_time.replace('.000000Z','').replace('T',' ');
                let utcDeliveryTime = doordashResponseObj.delivery_time.replace('.000000Z','').replace('T',' ');

                let convertUtcToMomentPickup = moment.utc(utcPickupTime).toDate();
                let convertUtcToMomentDelivery = moment.utc(utcDeliveryTime).toDate();

                let localPickupTime = moment(convertUtcToMomentPickup, 'YYYY-MM-DD HH:mm:ss').local().format('YYYY-MM-DD HH:mm:ss');
                let localDeliveryTime = moment(convertUtcToMomentDelivery, 'YYYY-MM-DD HH:mm:ss').local().format('YYYY-MM-DD HH:mm:ss');

                let currentLocation;
                setPickupOrDelivery((prevState) => {
                    let currentLocIndex = prevState.DeliveryStatus.LocationIndex;
                    currentLocation = locationList.original[currentLocIndex].locationNumber
                    return({
                        ...prevState,
                        DeliveryAddressError: {
                            ...prevState.DeliveryAddressError,
                            HasError: false,
                            ErrorMessage: Messages.ErrorGeneric,
                            ButtonsDisabled: false
                        },
                        DeliveryStatus: {
                            ...prevState.DeliveryStatus,
                            //EstimateResponseCode: 0, // Reset as this may need to be called again *** For Doordash URL ***
                            EstimateResponseCode: 200, // For S8 Server Response with Doordash
                            GetProductMenu: true, // Triggers useEffect to get delivery product menu
                            LocationID: locationList.original[currentLocIndex].locationNumber,
                            EstimtePickupTime: localPickupTime,
                            EstimateDeliveryTime: localDeliveryTime,
                            DoordashDeliveryFee: doordashResponseObj.fee,
                            //EstimateDeliveryMinutes: deliveryTime,
                            DoordashObject: doordashEstimateObj
                        },
                        PickupDeliveryModal: {
                            ...prevState.PickupDeliveryModal,
                            ModalIsShown: false
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PatchCustomerDeliveryAddress: true  // Triggers useEffect that calls patchCustomerDeliveryAddress()
                        }
                    })
                })
                setUrlConfig((prevState) => {
                    return({
                        ...prevState,
                        LocationQuery: currentLocation,
                        Type: 'web'
                    })
                });
                setLocationList((prevState) => {
                    return({
                        ...prevState,
                        deliveryListLoaded: false // Reset looping through locations
                    })
                });
            } else {
                let errorMessage;
                if (lastLocationInList) {

                    // === Last Location in the list for Doordash Estimate ===

                    // *** For S8 Doordash URL ***
                    let str = data.technicalMessage;
                    let start = str.indexOf("{");
                    let end = str.lastIndexOf("}");
                    let inside = (start !== -1 && end !== -1 && start < end) ? str.substring(start + 1, end) : null;
                    if (inside !== null) {
                        let stringObj = "{" + inside + "}";
                        let jsonObj = JSON.parse(stringObj);
                        errorMessage = jsonObj.field_errors?.[0]?.error ?? Messages.ErrorDeliveryUnavailable;
                    } else {
                        errorMessage = Messages.ErrorDeliveryUnavailable; // For S8 Server returning Doordash URL
                    }

                    // *** For Doordash URL ***
                    // if (estimateStatus === 400) { 
                    //     errorMessage = Messages.ErrorFieldErrors;
                    // } else {
                    //     errorMessage = Messages.ErrorDeliveryUnavailable;
                    // }
                    // ***

                    setPickupOrDelivery((prevState) => {
                        return({
                            ...prevState,
                            DeliveryAddressError: {
                                ...prevState.DeliveryAddressError,
                                HasError: true,
                                ErrorMessage: errorMessage,
                                ButtonsDisabled: false
                            },
                            DeliveryStatus: {
                                ...prevState.DeliveryStatus,
                                //EstimateResponseCode: estimateStatus // *** For Doordash URL ***
                                EstimateResponseCode: 0 // *** For S8 Doordash URL ***
                            }
                        })
                    });
                    setLocationList((prevState) => {
                        return({
                            ...prevState,
                            deliveryListLoaded: false // Reset looping through locations
                        })
                    });
                } else {
                    // === Continue Looping Locations for Doordash Estimate ===
                    setPickupOrDelivery((prevState) => {
                        let currentCount = prevState.DeliveryStatus.LocationIndex;
                        return({
                            ...prevState,
                            DeliveryAddressError: {
                                ...prevState.DeliveryAddressError,
                                HasError: false,
                                ErrorMessage: Messages.ErrorGeneric,
                                ButtonsDisabled: true
                            },
                            DeliveryStatus: {
                                ...prevState.DeliveryStatus,
                                LocationIndex: currentCount + 1
                            }
                        })

                    })
                }
            }
        })
    }

    // --- Pickup or Delivery - Server Calls ---
    React.useEffect(() => {
        if (pickupOrDelivery.ServerCalls.PatchCustomerDeliveryAddress) {
            // Patch Customer address (after successful estimate)
            patchCustomerDeliveryAddress();
        }
    }, [pickupOrDelivery.ServerCalls])

    function patchCustomerDeliveryAddress() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}CustomerForWebOrder?DeviceUID=${urlConfig.DeviceUID}&BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "PUT",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID
            },
            body: JSON.stringify(customerDetailsObjToPost.PostObject)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setPickupOrDelivery((prevState) => {
                    return ({
                        ...prevState,
                        DeliveryStatus: {
                            ...prevState.DeliveryStatus,
                            EstimateResponseCode: 0, // Reset Response so this can be called again 
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PatchCustomerDeliveryAddress: false // Reset useEffect
                        }
                    })
                })
            } else {
                setPickupOrDelivery((prevState) => {
                    return ({
                        ...prevState,
                        DeliveryStatus: {
                            ...prevState.DeliveryStatus,
                            EstimateResponseCode: 0, // Reset Response so this can be called again
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PatchCustomerDeliveryAddress: false // Reset useEffect
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setPickupOrDelivery((prevState) => {
                return ({
                    ...prevState,
                    DeliveryStatus: {
                        ...prevState.DeliveryStatus,
                        EstimateResponseCode: 0, // Reset Response so this can be called again
                    },
                    ServerCalls: {
                        ...prevState.ServerCalls,
                        PatchCustomerDeliveryAddress: false // Reset useEffect
                    }
                })
            })
        })
    }

    React.useEffect(() => {
        if (pickupOrDelivery.DeliveryStatus.GetProductMenu) {
            getCurrentLocationDetails(pickupOrDelivery.DeliveryStatus.LocationID);
        }
    }, [pickupOrDelivery.DeliveryStatus.GetProductMenu])

    // Menu Toggle
    function handlePickupDeliverySwitch(e) {
        let prevSwitchValue = pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue;  // pickup / delivery
        let switchValue = e.currentTarget.getAttribute('data-value'); // pickup / delivery
        if (prevSwitchValue !== switchValue) {
            setPickupOrDelivery((prevState) => {
                return({
                    ...prevState,
                    MethodChangeModal: {
                        ...prevState.MethodChangeModal,
                        ModalIsShown: true
                    }
                })
            })
        }
    }

    // Method Change Modal
    function handleCloseMethodChangeModal() {
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                MethodChangeModal: {
                    ...prevState.MethodChangeModal,
                    ModalIsShown: false
                }
            })
        })
    }

    

    function handleConfirmMethodChange(){
        // Set previous location to clear cart
        setAlertModal((prevState) => {
            return({
                ...prevState,
                PreviousLocationClearCart: currentLocation.LocationID,
                ClearCart: true, // Triggers useEffect that calls clearCart()
                ClearMemberDetails: true,
                IsInitialLocation: true
            })
        });
        setPickupOrDelivery((prevState) => {
            return({
                ...prevState,
                SwitchMenuNav: {
                    ...prevState.SwitchMenuNav,
                    SwitchPickupDeliveryValue: null, // Reset method
                    MenuTypeEndpointParam: null // Reset menu type param
                },
                PickupDeliveryModal: {
                    ...prevState.PickupDeliveryModal,
                    ModalIsShown: true,
                    ModalPageShown: 'Method' // Start order flow again
                },
                DeliveryAddressFields: {
                    ...prevState.DeliveryAddressFields,
                    streetnumber: {
                        ...prevState.DeliveryAddressFields.streetnumber,
                        Value: ''
                    },
                    unitnumber: {
                        ...prevState.DeliveryAddressFields.unitnumber,
                        Value: ''
                    },
                    streetname: {
                        ...prevState.DeliveryAddressFields.streetname,
                        Value: ''
                    },
                    suburb: {
                        ...prevState.DeliveryAddressFields.suburb,
                        Value: ''
                    },
                    state: {
                        ...prevState.DeliveryAddressFields.state,
                        Value: ''
                    },
                    postcode: {
                        ...prevState.DeliveryAddressFields.postcode,
                        Value: ''
                    },
                    deliveryinstructions: {
                        ...prevState.DeliveryAddressFields.deliveryinstructions,
                        Value: ''
                    }
                },
                DeliveryAddressError: {
                    ...prevState.DeliveryAddressError,
                    HasError: false, // Reset error but display address if previously entered
                    ButtonsDisabled: false
                },
                MethodChangeModal: {
                    ...prevState.MethodChangeModal,
                    ModalIsShown: false
                },
                DeliveryStatus: { // Reset delivery
                    ...prevState.DeliveryStatus,
                    EstimateResponseCode: 0,
                    LocationIndex: 0,
                    LocationID: 0,
                    EstimtePickupTime: null,
                    EstimateDeliveryTime: null,
                    //EstimateDeliveryMinutes: null,
                    DoordashDeliveryFee: null,
                    GetProductMenu: false,
                    DoordashObject: null
                }
            })
        });
        setProductsLoading(true); // Show loading skeleton for product
        setProductData([]); // Reset product menu
        setUrlConfig((prevState) => {
            // Reset Location and Table Number
            return({
                ...prevState,
                LocationQuery: '',
                TableNumber: '',
                MenuType: null,
            })
        })
        // Reset Location List
        setLocationList((prevState) => {
            return({
                ...prevState,
                original: [],
                filtered: [],
                listLoaded: false,
                deliveryListLoaded: false,
                searchFilterText: "",
                showOpenOnly: false,
                showClearButton: false
            })
        })
        // Reset Selected Location
        setCurrentLocation((prevState) => {
            return({
                ...prevState,
                LocationID: "",
                LocationName: "",
                LocationAddressLine1: "",
                LocationAddressLine2: "",
                LocationSuburb: "",
                LocationState: "",
                LocationPostcode: "",
                LocationPhoneNumber: "",
                LocationDistance: "",
                ProductPriceTierID: -999,
                ModPriceTierID: -999,
                AllowOnlineOrder: false,
                PickupTimeValue: 0,
                CalendarValue: "",
                CalendarValueServer: "",
                MondayOpenTime: "",
                MondayCloseTime: "",
                TuesdayOpenTime: "",
                TuesdayCloseTime: "",
                WednesdayOpenTime: "",
                WednesdayCloseTime: "",
                ThursdayOpenTime: "",
                ThursdayCloseTime: "",
                FridayOpenTime: "",
                FridayCloseTime: "",
                SaturdayOpenTime: "",
                SaturdayCloseTime: "",
                SundayOpenTime: "",
                SundayOpenTime: "",
                Validation: {
                    ...prevState.Validation,
                    IsValidTableNumber: true,
                    CalendarHasError: false,
                    CalendarErrorMessage: Messages.ErrorFieldRequired
                },
                CalendarConfig: {
                    ...prevState.CalendarConfig,
                    CalendarMinDate: "",
                    CalendarMaxDate: "",
                    CalendarMinDays: "",
                    CalendarMaxDays: ""                    
                }
            })
        })
    }

    // ====================== end: Pickup / Delivery ======================

    // ====================== begin: Ward Details ======================
    const [wardDetails, setWardDetails] = React.useState({
        WardModal: {
            ModalIsShown: false
        },
        WardModalFields: {
            floornumber: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            wardnumber: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            bednumber: {
                Value: '',
                IsRequired: true,
                HasError: false,
                ErrorMessage: Messages.ErrorFieldRequired
            }
        },
        WardDetailsPayload: '',
        WardModalError: {
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: false,
            IsLoading: false
        }
    });

    // Ward Fields - Change
    function handleChangeWardModalFields(e) {
        const { name, value } = e.currentTarget;
        
        setWardDetails((prevState) => {
            return({
                ...prevState,
                WardModalFields: {
                    ...prevState.WardModalFields,
                    [name]: {
                        ...prevState.WardModalFields[name],
                        Value: value
                    }
                }
            })
        })
    }

    // Submit Ward Fields
    function handleSubmitWardFields() {
        let allFieldsHaveError = false;
        let allFieldsHaveErrorMessage = Messages.ErrorProductForm;
        // Reset General Error
        setWardDetails((prevState) => {
            return({
                ...prevState,
                WardModalError: {
                    ...prevState.WardModalError,
                    HasError: false,
                    ButtonsDisabled: true,
                    IsLoading: true
                }
            })
        })
        
        // Validate Fields
        let wardFieldsObj = wardDetails.WardModalFields;
        let wardFieldsArr = Object.entries(wardFieldsObj).map((parentField) => {
            let childFieldsObj = parentField[1];
            // Check Validation
            let hasError = false;
            let errorMessage = Messages.ErrorFieldRequired;
            if (childFieldsObj.IsRequired) {
                if (childFieldsObj.Value === '' || childFieldsObj.Value === null) {
                    hasError = true;
                    allFieldsHaveError = true;
                    errorMessage = Messages.ErrorFieldRequired;
                }
            }

            // Map fields
            let innerFields = Object.entries(childFieldsObj).map((childFields) => {
               if (childFields[0] === "HasError") {
                return ["HasError", hasError]
               } else {
                   return childFields
               }
            })

            return [
                parentField[0], 
                Object.fromEntries(innerFields)
            ]
        })
        let validatedParentObj = Object.fromEntries(wardFieldsArr);

        // Update State
        setWardDetails((prevState) => {
            let wardPayload = `Floor: ${validatedParentObj.floornumber.Value} | Ward: ${validatedParentObj.wardnumber.Value} | Bed ${validatedParentObj.bednumber.Value}`;
            return({
                ...prevState,
                WardModal: {
                    ...prevState.WardModal,
                    ModalIsShown: allFieldsHaveError
                },
                WardModalFields: validatedParentObj,
                WardDetailsPayload: wardPayload,
                WardModalError: {
                    ...prevState.WardModalError,
                    HasError: allFieldsHaveError,
                    ErrorMessage: allFieldsHaveErrorMessage,
                    ButtonsDisabled: !allFieldsHaveError,
                    IsLoading: !allFieldsHaveError
                },
                ServerCalls: {
                    PutUpdateCustomerRecord: true
                }
            })
        })

        // Update Customer Details in Server for Ward Payload
        setCustomerDetailsObjToPost((prevState) => {
            let wardPayload = `Floor: ${validatedParentObj.floornumber.Value} | Ward: ${validatedParentObj.wardnumber.Value} | Bed ${validatedParentObj.bednumber.Value}`;
            return ({
                ...prevState,
                PutDetailsAfterWard: true,
                PostObject: {
                    ...prevState.PostObject,
                    WardInfoPayload: wardPayload
                }
            })
        })
    }

    function putCustomerWardDetails() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}CustomerForWebOrder?DeviceUID=${urlConfig.DeviceUID}&BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "PUT",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID
            },
            body: JSON.stringify(customerDetailsObjToPost.PostObject)
        })
        .then((response) => response.json())
        .then((data) => {
            setCustomerDetailsObjToPost((prevState) => {
                return ({
                    ...prevState,
                    PutDetailsAfterWard: false // Reset useEffect trigger
                })
            })
            if (data.isRequestSuccessful) {
                // Do nothing
            } else {
                // Do nothing
            }
        })
        .catch((error) => {
            setCustomerDetailsObjToPost((prevState) => {
                return ({
                    ...prevState,
                    PutDetailsAfterWard: false // Reset useEffect trigger
                })
            })
        })
    }
    // ====================== end: Ward Details ======================

    // ====================== begin: URL, Geo Location & React-Router ====================== 
    
    // ---------- useSearchParams - react-router-dom - Token / URL ----------
    const [searchParams, setSearchParams] = useSearchParams();
    
    // ---------- State: URL Config ----------
    const [urlConfig, setUrlConfig] = React.useState({
        LocationQuery: searchParams.get('loc'),
        MemberId: searchParams.get('memberid'),
        LoyaltyMemberId: searchParams.get('loyaltymemberid'),
        TableNumber: Number.parseInt(searchParams.get('tablenum')) ? Number.parseInt(searchParams.get('tablenum')) : "",
        DeviceUID: searchParams.get('token'),
        Type: searchParams.get('type'),
        MenuType: searchParams.get('menutype'), // pickup / delivery 
        WardInfo: searchParams.get('wardinfo'), // 1 = Is Ward, 0 or none = Not Ward
        Ref: searchParams.get('ref'), // (catering uses this as the order number)
        SaleOrderTypeId: 1, // 1 = Dine In, 3 = Delivery, 4 = Pick Up (default)
        ServerCalls: {
            GetUserCart: false // Triggers useEffect to call getCartDetails()
        }
    });

    React.useEffect(() => {
        if (urlConfig.ServerCalls.GetUserCart) {
            if (urlConfig.MemberId != null && urlConfig.MemberId != 'null') {
                getCartDetails();
            }
        }
    }, [urlConfig.ServerCalls])

    // ---------- useEffect - General > Geo Location & Set Title ----------
    React.useEffect(function() {
        //console.log('basePath:', basePath.current);
        //console.log('currentpage:', basePath.current.replace(/^\//, ''));
        document.title = "Web Ordering";
        if (searchParams.get('loc') === '' || searchParams.get('loc') === null || searchParams.get('loc') === 'null') {
            // Get geolocation permission if location number is not set in URL
            getGeoLocation();
        }
        getOrderStatusDetails();
        
      }, []);

    // ---------- Initialize Hook useLocation - react-router-dom - Path Name ----------
    const locationPath = useLocation();

    // ---------- Initialize Hook useNavigate - react-router-dom - Navigate between pages ----------
    const navigate = useNavigate();

    const basePath = useRef(window.location.pathname.replace(/\/(cart|orderstatus)$/i, '') || '/');

    // ---------- useEffect - URL: Existing Token/Location Check ----------
    React.useEffect(function() {
        if (pickupOrDelivery.PickupDeliveryModal.DeliveryAvailabilityHasBeenChecked && !pickupOrDelivery.PickupDeliveryModal.ModalIsShown && pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue == "pickup") {
            checkLocationParams();
        }
    }, [locationList.listLoaded]);

    function checkLocationParams() {
        // Location
        const locationParam = searchParams.get('loc');
        let setLocation = "";
        if (locationParam === "" || locationParam === null) {
            setLocation = "";
            setShowLocModal(true);
        } else {
            setLocation = locationParam;
        }
        setUrlConfig((prevState) => {
            return {
                ...prevState,
                LocationQuery: setLocation
            }
        });
        if (locationList.original.length > 0) {
            if (locationParam !== "" && locationParam !== null) {
                getCurrentLocationDetails(locationParam);
            }
        }
    }

    // ---------- useEffect - General: URL Change ----------
    React.useEffect(function() {
        setSearchParams({...(searchParams.get('s8view') ? {s8view: searchParams.get('s8view')} : {}), token: urlConfig.DeviceUID, loc: urlConfig.LocationQuery, memberid: urlConfig.MemberId, tablenum: urlConfig.TableNumber, type: urlConfig.Type, menutype: urlConfig.MenuType, wardinfo: urlConfig.WardInfo, ref: urlConfig.Ref})

        const currentPath = locationPath.pathname.toLowerCase();
        if (currentPath == "/orderstatus" || searchParams.get('s8view') === 'orderstatus') {
            getOrderStatusDetails();
            if (urlConfig.Type === '' || urlConfig.Type === null || urlConfig.Type === 'null' || urlConfig.Type == 'web') {
                // --- Web Ordering ---
                // Reset Location List
                setLocationList((prevState) => {
                    return({
                        ...prevState,
                        original: [],
                        filtered: [],
                        listLoaded: false,
                        deliveryListLoaded: false,
                        searchFilterText: "",
                        showOpenOnly: false,
                        showClearButton: false
                    })
                });
                // Reset Selected Location
                setCurrentLocation((prevState) => {
                    return({
                        ...prevState,
                        LocationID: "",
                        LocationName: "",
                        LocationAddressLine1: "",
                        LocationAddressLine2: "",
                        LocationSuburb: "",
                        LocationState: "",
                        LocationPostcode: "",
                        LocationPhoneNumber: "",
                        LocationDistance: "",
                        ProductPriceTierID: -999,
                        ModPriceTierID: -999,
                        AllowOnlineOrder: false,
                        PickupTimeValue: 0,
                        CalendarValue: "",
                        CalendarValueServer: "",
                        MondayOpenTime: "",
                        MondayCloseTime: "",
                        TuesdayOpenTime: "",
                        TuesdayCloseTime: "",
                        WednesdayOpenTime: "",
                        WednesdayCloseTime: "",
                        ThursdayOpenTime: "",
                        ThursdayCloseTime: "",
                        FridayOpenTime: "",
                        FridayCloseTime: "",
                        SaturdayOpenTime: "",
                        SaturdayCloseTime: "",
                        SundayOpenTime: "",
                        SundayCloseTime: "",
                        Validation: {
                            ...prevState.Validation,
                            IsValidTableNumber: true,
                            CalendarHasError: false,
                            CalendarErrorMessage: Messages.ErrorFieldRequired
                        },
                        CalendarConfig: {
                            ...prevState.CalendarConfig,
                            CalendarMinDate: "",
                            CalendarMaxDate: "",
                            CalendarMinDays: "",
                            CalendarMaxDays: ""
                        }
                    })

                });
                // Update location to clear the cart
                setAlertModal((prevState) => {
                    return({
                        ...prevState,
                        PreviousLocationClearCart: urlConfig.LocationQuery
                    })
                });
            } else {
                if (currentLocation.LocationID === '') {
                    getCurrentLocationDetails(urlConfig.LocationQuery);
                } 
            }
            setPickupOrDelivery((prevState) => {
                let isWebOrder = urlConfig.Type === '' || urlConfig.Type === null || urlConfig.Type === 'null' || urlConfig.Type == 'web';
                let switchValue;
                let menuTypeParam;
                if (isWebOrder) {
                    switchValue = CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery ? null : 'pickup';
                    menuTypeParam = CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery ? null : CommonUtilities.StaticFields.PickupMenuType;
                } else {
                    switchValue = 'pickup';
                    menuTypeParam = CommonUtilities.StaticFields.PickupMenuType;
                }
                return({
                    ...prevState,
                    SwitchMenuNav: {
                        ...prevState.SwitchMenuNav,
                        SwitchPickupDeliveryValue: switchValue // Reset Switch
                    }
                })
            })
        }
        if (alertModal.StartNewOrder) {
            navigate(`${basePath.current}?s8view=menu&token=${urlConfig.DeviceUID}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${urlConfig.Ref}`);
            if (urlConfig.Type === '' || urlConfig.Type === null || urlConfig.Type === 'null' || urlConfig.Type == 'web') {
                if (CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery === false) {
                    // Web Ordering - Pickup only
                    setLocationLoading(true);
                    fetchLocationList();
                } else {
                    // Web Ordering - Delivery & Pickup
                }
            } else {
                // Other Ordering Variations (Kiosk, QR, Table, Static web)
                if (currentLocation.LocationID !== "") {
                    getTokenDetails();
                    fetchProductMenu(currentLocation.LocationID);
                }
                if (urlConfig.WardInfo == 1) {
                    setWardDetails((prevState) => {
                        return ({
                            ...prevState,
                            WardModal: {
                                ...prevState.WardModal,
                                ModalIsShown: true
                            }
                        })
                    })
                }
            }
            setAlertModal((prevState) => {
                return({
                    ...prevState,
                    StartNewOrder: false
                })
            })
        }
    }, [urlConfig]);

    // ---------- Function: URL - Token / Customer Details ----------
    function getTokenDetails() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}CustomerForWebOrder?DeviceUID=${urlConfig.DeviceUID}&BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
            }
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                let memberIsLoggedIn = data.customerRecord.brandMemberID == -1 ? false : true;
                setUrlConfig((prevState) => {
                    let loyaltyMemberId = data.customerRecord.brandMemberID == -1 ? null : data.customerRecord.brandMemberID;
                    // Check if URL contains Ward Info
                    let urlConfigHasWard = urlConfig.WardInfo == 1 || urlConfig.WardInfo == 0 ? true : false;
                    // Action based on if URL contains Ward Info
                    let isWard = urlConfigHasWard ? urlConfig.WardInfo : (data.customerRecord.isWardInfo ? "1" : "0");
                    return {
                        ...prevState,
                        LoyaltyMemberId: loyaltyMemberId,
                        MemberId: data.memberIDForCart,
                        DeviceUID: data.customerRecord.deviceUID,
                        WardInfo: isWard,
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetUserCart: true // Triggers useEffect to call getCartDetails()
                        }
                    }
                });
                if (memberIsLoggedIn) {
                    setLoyaltyMember((prevState) => {
                        return ({
                            ...prevState,
                            MenuNav: {
                                ...prevState.MenuNav,
                                LoyaltyMemberButtonValue: data.customerRecord.firstName,
                                IsLoggedIn: true,
                                CardNumber: data.customerRecord.memberCardNumber
                            },
                            LoyaltyModal: {
                                ...prevState.LoyaltyModal,
                                ModalPageShow: 'Vouchers'
                            },
                            VoucherModalPage: {
                                ...prevState.VoucherModalPage,
                                ShowVouchersLoading: true
                            },
                            ServerCalls: {
                                ...prevState.ServerCalls,
                                GetVouchers: true // Triggers useEffect that calls getVoucherList()
                            }
                        })
                    })
                }
                setCustomerDetailsFormData((prevState) => {
                    return ({
                        ...prevState,
                        formData: {
                            ...prevState.formData,
                            MemberID: data.memberIDForCart,
                            firstname: data.customerRecord.firstName,
                            lastname: data.customerRecord.lastName,
                            email: data.customerRecord.email,
                            mobile: data.customerRecord.mobile,
                            cardnumber: data.customerRecord.memberCardNumber,
                            membercardnumberforcart: data.memberCardNumberForCart
                        }
                    })
                });
                setWardDetails((prevState) => {
                    return({
                        ...prevState,
                        WardDetailsPayload: data.customerRecord.wardInfoPayload
                    })
                })
                // Update Customer Record in Server
                setCustomerDetailsObjToPost((prevState) => {
                    // Check if URL contains Ward Info
                    let urlConfigHasWard = urlConfig.WardInfo == 1 || urlConfig.WardInfo == 0 ? true : false;
                    // Action based on if URL contains Ward Info
                    let isWard = urlConfigHasWard ? (urlConfig.WardInfo == 1 ? true : false) : (data.customerRecord.isWardInfo ? true : false);
                    return ({
                        ...prevState,
                        PutDetailsAfterWard: true,
                        PostObject: {
                            ...prevState.PostObject,
                            LocationNumber: currentLocation.LocationID,
                            TableNumber: urlConfig.TableNumber == "null" ? null : urlConfig.TableNumber,
                            BrandCode: CommonUtilities.StaticFields.BrandCode,
                            DeviceUID: urlConfig.DeviceUID ? urlConfig.DeviceUID : "",
                            IsWardInfo: isWard, // Main action
                            WardInfoPayload: data.customerRecord.wardInfoPayload, // Replace with existing what is already on the server
                            Type: urlConfig.Type !== "" && urlConfig.Type !== null && urlConfig.Type !== "null" ? urlConfig.Type : "web",
                            BrandMemberID: urlConfig.LoyaltyMemberId == null ? -1 : urlConfig.LoyaltyMemberId,
                        }

                    })
                })
            } else {
                setErrorModal((prevState) => {
                    return ({
                        ...prevState,
                        Message: data.responseMessage,
                        IsShown: true,
                        IsError: true
                    })
                });
            }
        })
        .catch((error) => {
            setErrorModal((prevState) => {
                return ({
                    ...prevState,
                    Message: Messages.ErrorGeneric,
                    IsShown: true,
                    IsError: true
                })
            });
        })
    }

    // ---------- Function - Menu: Geolocation ----------
    function getGeoLocation() {
        if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(showCoords, showError);
        } else {
            setGeoLocation((prevState) => {
                return({
                    ...prevState,
                    LocationURL: ""
                })
            })
        }
    }

    function showCoords (position) {
        setGeoLocation({
            LocationURL: '&Latitude=' + position.coords.latitude + '&Longitude='+ position.coords.longitude,
            GPSLatitude: position.coords.latitude,
            GPSLongitude: position.coords.longitude
        })
    }

    // Function - Error for Geolocation
    function showError (error) {
        setGeoLocation((prevState) => {
            return({
                ...prevState,
                LocationURL: ""
            })
        })
    }

    // ====================== end: URL, Geo Location & React-Router ====================== 
    
    // ====================== begin: Navigation Bar & Location Modal ====================== 
    
    // ---------- State: Location List ----------
    const [geoLocation, setGeoLocation] = React.useState({
        LocationURL: "",
        GPSLatitude: "",
        GPSLongitude: ""
    });
    const [locationLoading, setLocationLoading] = React.useState(true);
    
    const [showLocModal, setShowLocModal] = React.useState(false);
    const [locationError, setLocationError] = React.useState({
        HasError: false,
        ErrorMessage: Messages.ErrorGeneric
    });
    const handleShowLocModal = () => setShowLocModal(true);
    const handleCloseLocModal = () => setShowLocModal(false);

    // ---------- State: Navigation / Current Location For Menu ----------
    const [currentLocation, setCurrentLocation] = React.useState({
        LocationID: "",
        LocationName: "",
        LocationAddressLine1: "",
        LocationAddressLine2: "",
        LocationSuburb: "",
        LocationState: "",
        LocationPostcode: "",
        LocationPhoneNumber: "",
        LocationDistance: "",
        ProductPriceTierID: -999,
        ModPriceTierID: -999,
        AllowOnlineOrder: false,
        PickupTimeValue: 0,
        CalendarValue: "",
        CalendarValueServer: "",
        MondayOpenTime: "",
        MondayCloseTime: "",
        TuesdayOpenTime: "",
        TuesdayCloseTime: "",
        WednesdayOpenTime: "",
        WednesdayCloseTime: "",
        ThursdayOpenTime: "",
        ThursdayCloseTime: "",
        FridayOpenTime: "",
        FridayCloseTime: "",
        SaturdayOpenTime: "",
        SaturdayCloseTime: "",
        SundayOpenTime: "",
        SundayCloseTime: "",
        Validation: {
            IsValidTableNumber: true,
            CalendarHasError: false,
            CalendarErrorMessage: Messages.ErrorFieldRequired
        },
        CalendarConfig: {
            CalendarMinDate: "",
            CalendarMaxDate: "",
            CalendarMinDays: "",
            CalendarMaxDays: ""
        }
    });

    // ---------- Initialize Hook useRef - React - Location: Search Field Ref ----------
    const locationSearchInputRef = useRef(null);

    // ---------- State: Amount in Cart ----------
    const [amountInCart, setAmountInCart] = React.useState(0);

    // ---------- useEffect - Location: Get Location List ----------
    React.useEffect(function() {
        setLocationLoading(true);
        fetchLocationList();
    }, [geoLocation.LocationURL]);

    // ---------- Function - Menu: Location List (Pick up) ----------
    function fetchLocationList() {
        setLocationError({
          HasError: false,
          ErrorMessage: Messages.ErrorGeneric
        });
        fetch(`${CommonUtilities.StaticFields.APIDomain}LocationList?BrandCode=${CommonUtilities.StaticFields.BrandCode}${geoLocation.LocationURL}&ViewMenuStore=1&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, { // Real Data > Need to update with Store Hours
        //fetch(`${process.env.PUBLIC_URL}/JSONs/LocationListWithStoreHours.json`, { // Dummy Data
          method: "GET",
          headers: {
            Accept: "application/json",
            "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
            "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
            "BrandUID": CommonUtilities.StaticFields.BrandUID,
            "BrowserSecUID": "CBD3A11B-D3A7-4695-B1A3-891678B8307D"
          }
        })
        .then((response) => response.json())
        .then((data) => {
            setLocationLoading(false);
            if (data.isRequestSuccessful) {
                const responseLocationList = data.locationList
                let locationListExclude;
                if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') {
                    locationListExclude = responseLocationList.filter((item) => item.locationNumber !== 9988 && item.locationNumber !== 9977); // All locations excluding 9988 && 9977 (For Catering)
                } else {
                    locationListExclude = responseLocationList.filter((item) => item.locationNumber !== 9988 && item.locationNumber !== 9977 && item.tyroPaymentS8AppLocationID !== ""); // All locations excluding 9988, 9977 and blank Tyro IDs
                }
                setLocationList((prevState) => {
                    return ({
                        ...prevState,
                        original: locationListExclude,
                        filtered: locationListExclude,
                        listLoaded: true,
                        searchFilterText: "",
                        showOpenOnly: false,
                        showClearButton: false
                    })
                });
            } else {
                setLocationError({
                HasError: true,
                ErrorMessage: Messages.ErrorLoadingLocations
                });
            }
        })
        .catch((error) => {
            setLocationLoading(false);
            setLocationError({
                HasError: true,
                ErrorMessage: Messages.ErrorLoadingLocations
            });
        });
    }

    // ---------- Function - Location: Search Location ----------
    function handleSearchLocation(event) {
        const searchText = event.currentTarget.value;
        setLocationList((prevState) => {
            const showClear = searchText !== "";
            return({
                ...prevState,
                searchFilterText: searchText,
                showClearButton: showClear
            })
        });
        filterLocation();
    }

    // ---------- Function - Location: Clear Search Location ----------
    function handleClearSearchLocation() {
        setLocationList((prevState) => {
            return({
                ...prevState,
                searchFilterText: "",
                showClearButton: false
            })
        });
        filterLocation();
    }

    // ---------- Function - Location: Focus Search Location ----------
    function handleFocusSearchLocation () {
        locationSearchInputRef.current && locationSearchInputRef.current.focus();
    }

    // ---------- Function - Location: Toggle Open Location ----------
    function handleToggleOpenLocation() {
        setLocationList((prevState) => {
            return({
                original: prevState.original,
                filtered: prevState.filtered,
                listLoaded: prevState.listLoaded,
                searchFilterText: prevState.searchFilterText,
                showOpenOnly: !prevState.showOpenOnly,
                showClearButton: prevState.showClearButton
            })
        });
        filterLocation();
    }

    // ---------- Function - Location: Filter Location Logic ----------
    function filterLocation() {
        setLocationList((prevState) => {
            const filteredLocations = prevState.original.filter((location) => {
                if (prevState.searchFilterText === "") {
                    return location
                }
                else {
                    return location.locationFullAddress.toLowerCase().includes(prevState.searchFilterText.toLowerCase()) || location.locationName.toLowerCase().includes(prevState.searchFilterText.toLowerCase());
                }
            }).filter((location) => {
                if (prevState.showOpenOnly) {
                    if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup' || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === null || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'null') {
                        // Pick Up selected
                        if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') {
                        // Catering & Pickup Selected
                        return location.isAllowS8WebCateringOrder
                        } else {
                            return location.isAllowOnlineOrder
                        }
                    } else {
                        // Delivery selected
                        return location.isAllowOnlineOrder
                    }
                } else {
                    return location
                }
            })
            return({
                ...prevState,
                filtered: filteredLocations,
            })
        })
    }

    // ---------- Function - Location Warning: Confirm Change Location ----------
    function handleConfirmChangeLocation() {
        getCurrentLocationDetails(alertModal.LocationIdIfConfirm);
        setAlertModal((prevState) => {
            return({
                ...prevState,
                ShowAlertModal: false,
                ClearCart: true // Triggers useEffect that calls clearCart()
            })
        })
    }

    // ---------- Function - Location Warning: Cancel Change Location ----------
    function handleCancelChangeLocation() {
        setAlertModal((prevState) => {
            return({
                ...prevState,
                ShowAlertModal: false,
                LocationIdIfConfirm: ""
            });
        })
    }

    // ---------- Function - Menu: Load Product Menu / Handle Select Location - Functions ----------
    function handleSelectLocation(event) {
        const locationId = event.target.getAttribute('data-locNum');
        if (alertModal.IsInitialLocation) {
            getCurrentLocationDetails(locationId);
        } else {
            if (amountInCart === 0) {
                getCurrentLocationDetails(locationId);
            } else {
                setAlertModal((prevState) => {
                    return({
                        ...prevState,
                        ShowAlertModal: true,
                        LocationIdIfConfirm: locationId,
                        PreviousLocationClearCart: currentLocation.LocationID
                    })
                });
                setShowLocModal(false);
            }
        }
    }

    // ---------- Function - Location: Get Location Details ----------
    function getCurrentLocationDetails (locationId) {
        setProductsLoading(true);
        setMenuError({
            HasError: false,
            ErrorMessage: ""
        });
        const currentLocDetails = locationList.original.find((item) => item.locationNumber == locationId);
        if (typeof currentLocDetails === "undefined") {
            setShowLocModal(true);
            return
        }

        // Validation Table Number
        let isValidTableNum;
        if (urlConfig.Type == '' || urlConfig.Type == null || urlConfig.Type == 'null' || urlConfig.Type == 'web') { // Web Ordering
            isValidTableNum = true;
        } else if (urlConfig.Type == 'q8r6e') { // QR Code
            isValidTableNum = true;
        } else if (urlConfig.Type == 'k7s5k') { // Kiosk
            isValidTableNum = true;
        } else if (urlConfig.Type == 't9b6f') { // Table Number
            if (urlConfig.TableNumber < 1 || urlConfig.TableNumber > 999) {
                isValidTableNum = false;
            } else {
                isValidTableNum = true;
            }
        } else { // Default
            isValidTableNum = true;
        }

        // Check if Ward Modal needs to be shown
        let showWardModal;
        if (urlConfig.WardInfo == 1) {
            showWardModal = true;
        } else {
            showWardModal = false;
        }

        // Sale Order Type ID
        let saleOrderTypeId; // 1 = Dine In, 3 = Delivery, 4 = Pick Up (default)
        if (urlConfig.MenuType == 'delivery') {
            saleOrderTypeId = 3; // Delivery
        } else {
            if (urlConfig.Type == 't9b6f' && isValidTableNum) {
                saleOrderTypeId = 1; // Dine In (Valid Table)
            } else {
                saleOrderTypeId = 4; // Pick Up
            }
        }

        setCurrentLocation((prevState) => {
            let allowOnlineOrder;
            if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup' || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === null || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'null') {
                // Pickup
                if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') {
                allowOnlineOrder = currentLocDetails.isAllowS8WebCateringOrder;
                } else {
                    allowOnlineOrder = currentLocDetails.isAllowOnlineOrder;
                }
            } else {
                // Delivery
                allowOnlineOrder = currentLocDetails.isAllowOnlineOrder;
            }
            
            return ({
                ...prevState,
                LocationID: locationId,
                LocationName: currentLocDetails.locationName,
                LocationAddressLine1: currentLocDetails.locationAddress1,
                LocationAddressLine2: currentLocDetails.locationAddress2,
                LocationSuburb: currentLocDetails.locationSuburb,
                LocationState: currentLocDetails.locationState,
                LocationPostcode: currentLocDetails.locationPostCode,
                LocationPhoneNumber: currentLocDetails.locationPhoneNumber,
                LocationDistance: currentLocDetails.distance,
                ProductPriceTierID: prevState.ProductPriceTierID,
                ModPriceTierID: prevState.ModPriceTierID,
                AllowOnlineOrder: allowOnlineOrder,
                PickupTimeValue: 0,
                CalendarValue: "",
                CalendarValueServer: "",
                MondayOpenTime: moment(currentLocDetails.mondayOpenTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                MondayCloseTime: moment(currentLocDetails.mondayCloseTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                TuesdayOpenTime: moment(currentLocDetails.tuesdayOpenTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                TuesdayCloseTime: moment(currentLocDetails.tuesdayCloseTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                WednesdayOpenTime: moment(currentLocDetails.wednesdayOpenTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                WednesdayCloseTime: moment(currentLocDetails.wednesdayCloseTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                ThursdayOpenTime: moment(currentLocDetails.thursdayOpenTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                ThursdayCloseTime: moment(currentLocDetails.thursdayCloseTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                FridayOpenTime: moment(currentLocDetails.fridayOpenTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                FridayCloseTime: moment(currentLocDetails.fridayCloseTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                SaturdayOpenTime: moment(currentLocDetails.saturdayOpenTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                SaturdayCloseTime: moment(currentLocDetails.saturdayCloseTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                SundayOpenTime: moment(currentLocDetails.sundayOpenTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                SundayCloseTime: moment(currentLocDetails.sundayCloseTime, 'YYYY-MM-DDTHH:mm:ss').format('HH:mm'),
                Validation: {
                    ...prevState.Validation,
                    IsValidTableNumber: isValidTableNum,
                    CalendarHasError: false,
                    CalendarErrorMessage: Messages.ErrorFieldRequired
                },
                CalendarConfig: {
                    ...prevState.CalendarConfig,
                    CalendarMinDate: "",
                    CalendarMaxDate: "",
                    CalendarMinDays: "",
                    CalendarMaxDays: ""
                }
            })
        });
        setCartDetails((prevState) => {
            return ({
                ...prevState,
                cartIsLoading: false,
                cartDetails: {
                    locationName: "",
                    products: [],
                    totalInc: 0
                }
            })
        });
        setAlertModal((prevState) => {
            return({
                ...prevState,
                IsInitialLocation: false
            })
        });
        setUrlConfig((prevState) => {
            return({
                ...prevState,
                LocationQuery: locationId,
                SaleOrderTypeId: saleOrderTypeId
            })
        });
        setOfflineModal((prevState) => {
            let showModal;
            if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup' || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === null || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'null') {
                // Pickup
                if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') { // Catering
                    showModal = !currentLocDetails.isAllowS8WebCateringOrder
                } else {
                    showModal = !currentLocDetails.isAllowOnlineOrder;
                }
            } else {
                // Delivery
                showModal = !currentLocDetails.isAllowOnlineOrder;
            }
            return ({
                ...prevState,
                IsShown: showModal
            })
        })
        setErrorModal((prevState) => {
            return ({
                ...prevState,
                Message: Messages.ErrorTableNumberInvalid,
                IsShown: isValidTableNum ? false : true,
                IsError: true
            })
        });
        setPickupOrDelivery((prevState) => {
            return ({
                ...prevState,
                DeliveryStatus: {
                    ...prevState.DeliveryStatus,
                    GetProductMenu: false // Reset useEffect trigger that calls the delivery product menu
                }
            })
        });
        setWardDetails((prevState) => {
            return ({
                ...prevState,
                WardModal: {
                    ...prevState.WardModal,
                    ModalIsShown: showWardModal
                }
            })
        });
        if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') {
            setOrderStatusDetails((prevState) => {
                return ({
                    ...prevState,
                    OpenOrders: [
                        {
                            ...prevState.OpenOrders[0],
                            "locationName": currentLocDetails.locationName,
                            "businessAddressLine1": currentLocDetails.locationAddress1,
                            "businessAddressLine2": currentLocDetails.locationAddress2,
                            "businessStateName": currentLocDetails.locationState,
                            "businessPostCode": currentLocDetails.locationPostCode,
                            "businessPhone": currentLocDetails.locationPhoneNumber,
                            "gpsLatitude": currentLocDetails.coordinateX,
                            "gpsLongitude": currentLocDetails.coordinateY,
                            "locationNumber": currentLocDetails.locationNumber
                        }
                    ]
                })
            })
        }
    }

    // ---------- Function - Nav > Location Change: Clear Cart ----------
    function clearCart() {
        setAlertModal((prevState) => {
            return({
                ...prevState,
                ClearCart: false // Reset useEffect trigger
            })
        })
        let fetchUrl;
        if (CommonUtilities.StaticFields.PriceType == 'bp8826') {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartForBulkPricing?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${alertModal.PreviousLocationClearCart}&MemberId=${urlConfig.MemberId}&Type=ALL&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        } else {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${alertModal.PreviousLocationClearCart}&MemberId=${urlConfig.MemberId}&Type=ALL&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        }
        fetch(fetchUrl, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            }
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                getCartDetails();
                // If logout
                if (loyaltyMember.ServerCalls.GetLogout) {
                    getLoyaltyLogout();
                }
            } else {
                if (loyaltyMember.ServerCalls.GetLogout) {
                    setLoyaltyMember((prevState) => {
                        return ({
                            ...prevState,
                            VoucherModalPage: {
                                ...prevState.VoucherModalPage,
                                GeneralError: {
                                    ...prevState.VoucherModalPage.GeneralError,
                                    HasError: true,
                                    ErrorMessage: Messages.ErrorGeneric,
                                    ButtonsDisabled: false
                                }
                            },
                            ServerCalls: {
                                ...prevState.ServerCalls,
                                GetLogout: false // Reset useEffect
                            }
                        })
                    })
                } else {
                    setErrorModal((prevState) => {
                        return ({
                            ...prevState,
                            Message: data.responseMessage,
                            IsShown: true,
                            IsError: true
                        })
                    });
                }
            }
        })
        .catch((error) => {
            if (loyaltyMember.ServerCalls.GetLogout) {
                setLoyaltyMember((prevState) => {
                        return ({
                            ...prevState,
                            VoucherModalPage: {
                                ...prevState.VoucherModalPage,
                                GeneralError: {
                                    ...prevState.VoucherModalPage.GeneralError,
                                    HasError: true,
                                    ErrorMessage: Messages.ErrorGeneric,
                                    ButtonsDisabled: false
                                }
                            },
                            ServerCalls: {
                                ...prevState.ServerCalls,
                                GetLogout: false // Reset useEffect
                            }
                        })
                    })
            } else {
                setErrorModal((prevState) => {
                    return ({
                        ...prevState,
                        Message: Messages.ErrorGeneric,
                        IsShown: true,
                        IsError: true
                    })
                });
            }
        })
    }

    React.useEffect(() => {
        if (alertModal.ClearCart) { // When switching between pickup / Delivery toggle
            clearCart();
        } else { // Clear cart has completed
            if (alertModal.ClearMemberDetails) {
                setUrlConfig((prevState) => { // Set URL Confirm
                    return ({
                        ...prevState,
                        LocationQuery: null,
                        MemberId: null,
                        TableNumber: null,
                        DeviceUID: null,
                        Type: null,
                        MenuType: null,
                        Ref: null
                    })
                });
                setAlertModal((prevState) => { // Reset clear member details trigger
                    return ({
                        ...prevState,
                        ClearMemberDetails: false 
                    })
                })
            }
        }
    }, [alertModal.ClearCart])

    // ---------- Function - Nav: View Cart / Navigate from Menu to Cart ----------
    function handleViewCart() {
        getCartDetails();
        navigate(`?s8view=cart&token=${urlConfig.DeviceUID}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${urlConfig.Ref}`);
    }
    
    // ====================== end: Navigation Bar & Location Modal ====================== 

    // ====================== begin: Brand Details ====================== 
    
    // ---------- useEffect: Brand Details ----------
    React.useEffect(() => {
        if (currentLocation.LocationID !== "" && currentLocation.LocationID !== null) {
            getBrandDetails()
        }
    }, [currentLocation.LocationID])

    function getBrandDetails() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}BrandDetails?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            }
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setCurrentLocation((prevState) => {
                    let formattedNow = moment().format('YYYY-MM-DD');
                    let minDate, maxDate;
                    maxDate = formattedNow;
                    if (data.minFulfillmentDays !== null) {
                        minDate = moment().add(data.minFulfillmentDays, 'days').format('YYYY-MM-DD') + `T00:00:00`;
                    } else {
                        minDate = formattedNow + `T00:00:00`;;
                    }

                    if (data.maxFulfillmentDays !== null) {
                        maxDate = moment().add(data.maxFulfillmentDays, 'days').format('YYYY-MM-DD') + `T23:59:59`;
                    } else {
                        maxDate = '';
                    }
                    return({
                        ...prevState,
                        CalendarConfig: {
                            ...prevState.CalendarConfig,
                            CalendarMinDate: minDate,
                            CalendarMaxDate: maxDate,
                            CalendarMinDays: data.minFulfillmentDays,
                            CalendarMaxDays: data.maxFulfillmentDays
                        }
                    })
                })
            } else {
                //console.log('server error');
            }
        })
        .catch((error) => {
            //console.log('fetch error');
        })
    }

    // ---------- useEffect: Get & Set Branding Colours ----------
    React.useEffect(function() {
      let backgroundColour = brandColourRequest[0].backgroundColour;
      let textColour = brandColourRequest[0].textColour;

      let style = document.createElement('style');
      style.innerHTML =
          `.active-scroll-spy {
              background-color: ${backgroundColour};
              border-radius: 15px;
              padding: 5px 10px;
              transition: all 0.5s;
          }
          .active-scroll-spy>small {
              color: ${textColour} !important;
          }
          .btn-outline-primary {
              border-color: ${backgroundColour};
              color: #000000;
          }
          .btn.btn-outline-primary.btn-check:checked {
              background-color: ${backgroundColour} !important;
              color: ${textColour}
          }
          .btn-check:checked+.btn, .btn.active, .btn.show, .btn:first-child:active, :not(.btn-check)+.btn:active {
              color: ${textColour};
              background-color: ${backgroundColour};
              border-color: ${backgroundColour};
          }
          .btn-check+.btn:hover {
              color: ${textColour};
              background-color: ${backgroundColour};
              border-color: ${backgroundColour};
              filter: brightness(90%);
          }`;
      document.getElementsByTagName('head')[0].appendChild(style);
    }, []);

    // function getBrandColour() {
    //     fetch('http://localhost:3000/common/JSONs/BrandColour.js', {
    //         method: "GET",
    //         headers: {
    //             Accept: "application/json",
    //             "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
    //             "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
    //             "Content-Type": "application/json",
    //             BrandUID: CommonUtilities.StaticFields.BrandUID,
    //             DeviceUID: urlConfig.DeviceUID
    //         }
    //     })
    //     .then((response) => response.json())
    //     .then((data) => {
    //         if (data.IsRequestSuccessful) {
    //             console.log('success');
    //         } else {
    //             console.log('server error');
    //         }
    //     })
    //     .catch((error) => {
    //         console.log('fetch error');
    //     })
    // }

    // ====================== end: Brand Details ====================== 

    // ====================== begin: Loyalty Member - Navigation Bar & Authentication ====================== 
    const [loyaltyMember, setLoyaltyMember] = React.useState({
        MenuNav: {
            LoyaltyMemberButtonValue: 'Guest',
            IsLoggedIn: false,
            CardNumber: ""
        },
        LoyaltyModal: {
            ModalIsShown: false,
            ModalPageShow: 'Login' // Login, Mobile, OTP, Vouchers, ItemVoucherParent, ItemVoucherProductList, ItemVoucherChild, ConfirmationAddedToCart
        },
        MobileModalPage: {
            Fields: {
                authmobile: {
                    Value: '',
                    IsRequired: true,
                    HasError: false,
                    ErrorMessage: Messages.ErrorFieldRequired,
                    MaxLength: 10
                }
            },
            GeneralError: {
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            }
        },
        OtpModalPage: {
            Fields: {
                authotp: {
                    Value: '',
                    IsRequired: true,
                    HasError: false,
                    ErrorMessage: Messages.ErrorFieldRequired,
                    MaxLength: 6
                }
            },
            GeneralError: {
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            },
        },
        VoucherModalPage: {
            Vouchers: [],
            ShowVouchersLoading: false,
            GeneralError: {
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            }
        },
        ItemVoucherParentPage: {
            SelectedVoucherToGetDetails: {
                VoucherType: '',
                IssuedVoucherID: '',
                MemberID: ''
            },
            GeneralError: {
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            }
        },
        ItemVoucherProductListPage: {
            SelectedGroup: {
                VoucherMenuNumber: ''
            },
            GeneralError: {
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            }
        },
        ItemVoucherChildPage: {
            SelectedProduct: {
                ProductID: '',
                ProductPLU: '',
                ProductName: '',
                ChargePriceForMods: null
            },
            FormData: {
                ModifierCategories: []
            },
            GeneralError: {
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            }
        },
        VoucherDetails: {
            OriginalData: {},
            FormData: {},
            VoucherIssueId: null // Needed to determine Locked or In Another Cart
        },
        ServerCalls: {
            // Server Calls
            SendOtpSms: false,
            PostOtp: false,
            GetVouchers: false,
            GetItemVoucherDetails: false,
            GetSaleVoucherDetails: false,
            PostInitiateVoucherAddToCart: false, // Add to cart 1/2
            PostAddVoucherToCart: false,  // Add to cart 2/2
            // Triggers for functions
            GetLogout: false, 
            ResendOtpStartTimer: false,
            ResendOtpCountdownSeconds: 15,
            PopulateItemVoucherProductList: false
        }
    });

    function handleClickOpenLoyaltyModal() {
        setLoyaltyMember((prevState) => {
            let showModalPage = prevState.MenuNav.IsLoggedIn ? 'Vouchers' : 'Login';
            let getVouchers = prevState.MenuNav.IsLoggedIn ? true : false;

            return({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalIsShown: true,
                    ModalPageShow: showModalPage
                },
                VoucherModalPage: {
                    ...prevState.VoucherModalPage,
                    ShowVouchersLoading: getVouchers
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    GetVouchers: getVouchers
                }
            })
        })
    }

    function handleClickCloseLoyaltyModal() {
        setLoyaltyMember((prevState) => {
            return({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalIsShown: false
                },
                MobileModalPage: {
                    ...prevState.MobileModalPage,
                    Fields: {
                        ...prevState.MobileModalPage.Fields,
                        authmobile: {
                            ...prevState.MobileModalPage.Fields.authmobile,
                            Value: '',
                            HasError: false
                        }
                    },
                    GeneralError: {
                        ...prevState.MobileModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                OtpModalPage: {
                    ...prevState.OtpModalPage,
                    Fields: {
                        ...prevState.OtpModalPage.Fields,
                        authotp: {
                            ...prevState.OtpModalPage.Fields.authotp,
                            Value: '',
                            HasError: false
                        }
                    },
                    GeneralError: {
                        ...prevState.OtpModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                VoucherModalPage: {
                    ...prevState.VoucherModalPage,
                    GeneralError: {
                        ...prevState.VoucherModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                ItemVoucherParentPage: {
                    ...prevState.ItemVoucherParentPage,
                    GeneralError: {
                        ...prevState.ItemVoucherParentPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                ItemVoucherProductListPage: {
                    ...prevState.ItemVoucherProductListPage,
                    GeneralError: {
                        ...prevState.ItemVoucherProductListPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                ItemVoucherChildPage: {
                    ...prevState.ItemVoucherChildPage,
                    GeneralError: {
                        ...prevState.ItemVoucherChildPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    // Server Calls
                    SendOtpSms: false,
                    PostOtp: false,
                    GetVouchers: false,
                    GetItemVoucherDetails: false,
                    GetSaleVoucherDetails: false,
                    PostInitiateVoucherAddToCart: false, // Add to cart 1/2
                    PostAddVoucherToCart: false,  // Add to cart 2/2
                    // Triggers for functions
                    GetLogout: false,
                    ResendOtpStartTimer: false,
                    ResendOtpCountdownSeconds: 15,
                    PopulateItemVoucherProductList: false
                }
            })
        })
    }

    function handleClickLoginLoyaltyMobile() {
        setLoyaltyMember((prevState) => {
            return({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: 'Mobile'
                }
            })
        })
    }

    // --- begin: Mobile Modal Page  ---
    function handleChangeMobileFields(e) {
        const { name, value } = e.currentTarget;

        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                MobileModalPage: {
                    ...prevState.MobileModalPage,
                    Fields: {
                        ...prevState.MobileModalPage.Fields,
                        [name]: {
                            ...prevState.MobileModalPage.Fields[name],
                            Value: value
                        }
                    }
                }
            })
        })
    }

    function handleNextValidateMobile() {
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                MobileModalPage: {
                    ...prevState.MobileModalPage,
                    GeneralError: {
                        ...prevState.MobileModalPage.GeneralError,
                        ButtonsDisabled: true
                    }
                }
            })
        })
        let mobileError = false;
        let mobileErrorMsg = Messages.ErrorFieldRequired;
        let allFieldError = false;

        if (!loyaltyMember.MobileModalPage.Fields.authmobile.Value) {
            mobileError = true;
            allFieldError = true;
            mobileErrorMsg = Messages.ErrorFieldRequired;
        } else {
            const runner = CommonUtilities.RegexPatterns.regexMobile.test(loyaltyMember.MobileModalPage.Fields.authmobile.Value);
            if (!runner) {
                mobileError = true;
                allFieldError = true;
                mobileErrorMsg = Messages.ErrorMobileInvalid;
            }
        }
        
        let sendSms = !allFieldError;
        let disableButtons = !allFieldError; 

        setLoyaltyMember((prevState) => {
            return({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                },
                MobileModalPage: {
                    ...prevState.MobileModalPage,
                    Fields: {
                        ...prevState.MobileModalPage.Fields,
                        authmobile: {
                            ...prevState.MobileModalPage.Fields.authmobile,
                            HasError: mobileError,
                            ErrorMessage: mobileErrorMsg
                        }
                    },
                    GeneralError: {
                        ...prevState.MobileModalPage.GeneralError,
                        ButtonsDisabled: disableButtons
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    SendOtpSms: sendSms // useEffect to trigger sending OTP SMS - calls SendOtpSms()
                }
            })
        })
    }
    // --- end: Mobile Modal Page  ---
    
    // --- begin: OTP Modal Page  ---
    function handleBackOtpPage() {
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: 'Mobile'
                },
                OtpModalPage: {
                    ...prevState.OtpModalPage,
                    Fields: {
                        ...prevState.OtpModalPage.Fields,
                        authotp: {
                            ...prevState.OtpModalPage.Fields.authotp,
                            HasError: false
                        }
                    }
                }
            })
        })
    }

    function handleChangeOtpFields(e) {
        const { name, value } = e.currentTarget;
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                OtpModalPage: {
                    ...prevState.OtpModalPage,
                    Fields: {
                        ...prevState.OtpModalPage.Fields,
                        [name]: {
                            ...prevState.OtpModalPage.Fields[name],
                            Value: value
                        }
                    }
                }
            })
        })
    }

    function intervalShowResendOtp() {
        if (loyaltyMember.ServerCalls.ResendOtpCountdownSeconds > 0) {
            setTimeout(() => {
                setLoyaltyMember((prevState) => {
                    return({
                        ...prevState,
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            ResendOtpStartTimer: true,
                            ResendOtpCountdownSeconds: prevState.ServerCalls.ResendOtpCountdownSeconds - 1
                        }
                    })
                })
            }, 1000)
        } else {
            setLoyaltyMember((prevState) => {
                return({
                    ...prevState,
                    ServerCalls: {
                        ...prevState.ServerCalls,
                        ResendOtpStartTimer: false
                    }
                })
            })
        }
    }

    function handleResendOtpToMobile() {
        setLoyaltyMember((prevState) => {
            return({
                ...prevState,
                OtpModalPage: {
                    ...prevState.OtpModalPage,
                    GeneralError: {
                        ...prevState.GeneralError,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    SendOtpSms: true // Triggers useEffect that calls sendOtpToMobile()
                }
            })
        })
    }

    function handleNextValidateOtp() {
        let otpError = false;
        let allFieldError = false;
        
        if (!loyaltyMember.OtpModalPage.Fields.authotp.Value) {
            otpError = true;
            allFieldError = true;
        }

        let postOtp = !allFieldError;
        let disableButtons = !allFieldError;

        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                OtpModalPage: {
                    ...prevState.OtpModalPage,
                    Fields: {
                        ...prevState.OtpModalPage.Fields,
                        authotp: {
                            ...prevState.OtpModalPage.Fields.authotp,
                            HasError: otpError
                        }
                    },
                    GeneralError: {
                        ...prevState.OtpModalPage.GeneralError,
                        ButtonsDisabled: disableButtons
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    PostOtp: postOtp // Triggers useEffect that calls postOtpToServer()
                }
            })
        })    
    }
    // --- end: OTP Modal Page  ---
    
    // --- begin: Vouchers Modal Page  ---
    function handleLoyaltyLogout() {
        setCustomerDetailsObjToPost((prevState) => {
            return ({
                ...prevState,
                PutDetailsAfterOTP: true, // Triggers useEffect that calls putCustomerDetailsAfterOtp()
                PostObject: {
                    ...prevState.PostObject,
                    FirstName: "",
                    LastName: "",
                    Email: "",
                    Mobile: "",
                    MemberCardNumber: "",
                    BrandMemberID: -1
                }
            })
        })
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                VoucherModalPage: {
                    ...prevState.VoucherModalPage,
                    GeneralError: {
                        ...prevState.VoucherModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: true
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    GetLogout: true // Flag for inside clearCart() > After successful completion, call getLoyaltyLogout() 
                }
            })
        })
        setAlertModal((prevState) => {
            return({
                ...prevState,
                PreviousLocationClearCart: currentLocation.LocationID,
                ClearCart: true // Triggers useEffect that calls clearCart()
            })
        })
    }
    
    function handleSelectVoucher(e) {
        let productType = e.currentTarget.getAttribute('producttype');
        let discountType = e.currentTarget.getAttribute('discounttype');
        let issuedVoucherId = e.currentTarget.getAttribute('issuedvoucherid');
        let memberId = e.currentTarget.getAttribute('memberid');
        let lockedFlag = e.currentTarget.getAttribute('lockedflag');
        // Determine Locked and Message to display
        let showLockedMsg = e.currentTarget.getAttribute('showlockedmsg'); // Show locked message
        let lockedmsgflag = e.currentTarget.getAttribute('lockedmsgflag'); // Determines which message to display

        let isItemVoucher = productType === 'item' ? true : false;
        let isSaleVoucher = productType === 'sale' ? true : false; 
        let voucherLoadDetails = isItemVoucher || isSaleVoucher ? true : false;
        
        if (productType === 'item' && discountType === 'DollarFixed') {
            // === Valid Vouchers ===

            // --- Check List - Check if a voucher in the list is locked ---
            if (showLockedMsg === "true") {
                // 0 = Not Locked, 1 = In Cart, 2 = Used In Another Cart
                if (lockedmsgflag === "1") { 
                    // In Cart
                    setErrorModal((prevState) => {
                        return ({
                            ...prevState,
                            Message: Messages.ErrorVoucherIsInCart,
                            IsShown: true,
                            IsError: false
                        })
                    })
                } else if (lockedmsgflag === "2") {
                    // Used In Another Cart
                    setErrorModal((prevState) => {
                        return ({
                            ...prevState,
                            Message: Messages.ErrorVoucherInUse,
                            IsShown: true,
                            IsError: false
                        })
                    })
                }
            } else {
                setLoyaltyMember((prevState) => {
                    return({
                        ...prevState,
                        VoucherModalPage: {
                            ...prevState.VoucherModalPage,
                            GeneralError: {
                                ...prevState.VoucherModalPage.GeneralError,
                                HasError: false,
                                ButtonsDisabled: voucherLoadDetails
                            }
                        },
                        ItemVoucherParentPage: {
                            ...prevState.ItemVoucherParentPage,
                            SelectedVoucherToGetDetails: {
                                ...prevState.ItemVoucherParentPage.SelectedVoucherToGetDetails,
                                VoucherType: productType,
                                IssuedVoucherID: issuedVoucherId,
                                MemberID: memberId
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetItemVoucherDetails: isItemVoucher, // Triggers useEffect that calls getItemVoucherDetails()
                            GetSaleVoucherDetails: isSaleVoucher // Triggers useEffect - will add to cart (*** need to implement ****)
                        }
                    })
                })
            }
            
            // --- begin: Display only for voucher clicked  ----
            // if (lockedFlag === "0") { // 0 = Not Locked
            //     setLoyaltyMember((prevState) => {
            //         return({
            //             ...prevState,
            //             VoucherModalPage: {
            //                 ...prevState.VoucherModalPage,
            //                 GeneralError: {
            //                     ...prevState.VoucherModalPage.GeneralError,
            //                     HasError: false,
            //                     ButtonsDisabled: voucherLoadDetails
            //                 }
            //             },
            //             ItemVoucherParentPage: {
            //                 ...prevState.ItemVoucherParentPage,
            //                 SelectedVoucherToGetDetails: {
            //                     ...prevState.ItemVoucherParentPage.SelectedVoucherToGetDetails,
            //                     VoucherType: productType,
            //                     IssuedVoucherID: issuedVoucherId,
            //                     MemberID: memberId
            //                 }
            //             },
            //             ServerCalls: {
            //                 ...prevState.ServerCalls,
            //                 GetItemVoucherDetails: isItemVoucher, // Triggers useEffect that calls getItemVoucherDetails()
            //                 GetSaleVoucherDetails: isSaleVoucher // Triggers useEffect - will add to cart (*** need to implement ****)
            //             }
            //         })
            //     })
            // } else if (lockedFlag === "1") { // 1 = In Cart
            //     setErrorModal((prevState) => {
            //         return ({
            //             ...prevState,
            //             Message: Messages.ErrorVoucherIsInCart,
            //             IsShown: true,
            //             IsError: false
            //         })
            //     })
            // } else if (lockedFlag === "2") { // 2 = Used In Another Cart
            //     setErrorModal((prevState) => {
            //         return ({
            //             ...prevState,
            //             Message: Messages.ErrorVoucherInUse,
            //             IsShown: true,
            //             IsError: false
            //         })
            //     })
            // }
            // --- end: Display only for voucher clicked  ----

        } else {
            // === Not yet valid vouchers ===
            setErrorModal((prevState) => {
                return ({
                    ...prevState,
                    Message: Messages.ErrorRedeemVoucherInStore,
                    IsShown: true,
                    IsError: false
                })
            })
        }
    }
    // --- end: Vouchers Modal Page  ---

    // --- begin: Item Voucher Parent Page ---
    function handleBackItemVoucherParent(e) {
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: 'Vouchers'
                },
                ItemVoucherParentPage: {
                    ...prevState.ItemVoucherParentPage,
                    GeneralError: {
                        ...prevState.ItemVoucherParentPage.GeneralError,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    }
                }
            })
        })
    }

    function handleSelectGroupItemVoucherParent(e) {
        let selectedIndex = e.currentTarget.getAttribute('data-indexvouchergroup');
        let updatedVoucherGroupFormData = loyaltyMember.VoucherDetails.FormData.VoucherGroups.map((group) => {
            return({
                ...group,
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric
            })
        })
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: 'ItemVoucherProductList'
                },
                ItemVoucherParentPage: {
                    ...prevState.ItemVoucherParentPage,
                    GeneralError: {
                        ...prevState.ItemVoucherParentPage.GeneralError,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    }
                },
                ItemVoucherProductListPage: {
                    ...prevState.ItemVoucherProductListPage,
                    SelectedGroup: {
                        ...prevState.ItemVoucherProductListPage.SelectedGroup,
                        VoucherMenuNumber: selectedIndex
                    }
                },
                VoucherDetails: {
                    ...prevState.VoucherDetails,
                    FormData: {
                        ...prevState.VoucherDetails.FormData,
                        VoucherGroups: updatedVoucherGroupFormData
                    }
                }
            })
        })
    }
    
    function handleAddVoucherToCartItemVoucherParent() {
        let allFieldsError = false;
        let allFieldErrorMsg = Messages.ErrorGeneric;
        let postToServer = false;
        let updatedFormData = loyaltyMember.VoucherDetails.FormData.VoucherGroups.map((group) => {
            if (group.ProductID == '' || group.ProductID == null) {
                allFieldsError = true;
                allFieldErrorMsg = Messages.ErrorProductForm
                return ({
                    ...group,
                    HasError: true,
                    ErrorMessage: Messages.ErrorSelectAProduct
                })
            } else {
                return ({
                    ...group,
                    HasError: false,
                    ErrorMessage: Messages.ErrorGeneric
                })
            }
        }) 

        if (!allFieldsError) {
            postToServer = true;
        }
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                ItemVoucherParentPage: {
                    ...prevState.ItemVoucherParentPage,
                    GeneralError: {
                        ...prevState.ItemVoucherParentPage.GeneralError,
                        HasError: allFieldsError,
                        ErrorMessage: allFieldErrorMsg,
                        ButtonsDisabled: postToServer
                    }
                },
                VoucherDetails: {
                    ...prevState.VoucherDetails,
                    FormData: {
                        ...prevState.VoucherDetails.FormData,
                        VoucherGroups: updatedFormData
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    PostInitiateVoucherAddToCart: postToServer // Triggers useEffect that will call postInitiateVoucherAddToCart()
                }
            })
        })        
    }

    function postInitiateVoucherAddToCart() {
        let objectToPost = {
        	DeviceUID: urlConfig.DeviceUID,
        	MemberId: urlConfig.MemberId,
        	CardNumber: customerDetailsFormData.formData.membercardnumberforcart,
        	BrandCode: CommonUtilities.StaticFields.BrandCode,
		    LocationNumber: parseInt(currentLocation.LocationID),
        	DeviceType: "iOS",
        	ProductPriceTierID: currentLocation.ProductPriceTierID,
		    ModPriceTierID: currentLocation.ModPriceTierID,
        	Products: [],
        	SaleOrderTypeId: urlConfig.SaleOrderTypeId
	    }
        let fetchUrl;
        if (CommonUtilities.StaticFields.PriceType == 'bp8826') {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartForBulkPricing?menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        } else {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartV2?menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        }
        fetch(fetchUrl, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID
            },
            body: JSON.stringify(objectToPost)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setLoyaltyMember((prevState) => {
                    return({
                        ...prevState,
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PostInitiateVoucherAddToCart: false, // Reset
                            PostAddVoucherToCart: true // Triggers useEffect that will call postAddVoucherToCart()
                        }
                    })
                })
            } else {
                setLoyaltyMember((prevState) => {
                    return({
                        ...prevState,
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PostInitiateVoucherAddToCart: false // Reset
                        },
                        ItemVoucherParentPage: {
                            ...prevState.ItemVoucherParentPage,
                            GeneralError: {
                                ...prevState.ItemVoucherParentPage.GeneralError,
                                HasError: true,
                                ErrorMessage: data.responseMessage,
                                ButtonsDisabled: false
                            }
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setLoyaltyMember((prevState) => {
                return({
                    ...prevState,
                    ServerCalls: {
                        ...prevState.ServerCalls,
                        PostInitiateVoucherAddToCart: false // Reset
                    },
                    ItemVoucherParentPage: {
                        ...prevState.ItemVoucherParentPage,
                        GeneralError: {
                            ...prevState.ItemVoucherParentPage.GeneralError,
                            HasError: true,
                            ErrorMessage: Messages.ErrorGeneric,
                            ButtonsDisabled: false
                        }
                    }
                })
            })
        })
    }

    function postAddVoucherToCart() {
        let productsInVoucherArray = loyaltyMember.VoucherDetails.FormData.VoucherGroups.map((group) => {

            // Modifiers
            let modsInProductArray;
            if (group.Mods.length > 0) {
                modsInProductArray = group.Mods.map((mod) => {
                    return ({
                        cartUserTempAddedDiscountsModsID: 0,
                        cartUserTempAddedDiscountsProductsID: 0,
                        originalPrice: 0,
                        priceEx: 0,
                        priceInc: 0,
                        priceTax: 0,
                        productName: null,
                        quantity: 0,
                        taxRate: 0,
                        plunumber: mod.PLU
                    })
                });
            } else {
                modsInProductArray = []
            }

            // Product
            return ({
                cartUserTempAddedDiscountsID: 0,
                cartUserTempAddedDiscountsProductsID: 0,
                originalPrice: 0,
                priceEx: 0,
                priceInc: 0,
                priceTax: 0,
                productName: null,
                quantity: 0,
                taxRate: 0,
                cartUserTempAddedDiscountsModsList: modsInProductArray,
                plunumber: group.ProductPLU,
			    isApplyPriceForMod: group.ChargePriceForMods
            })
        })

        let postVoucherObjToServer = {
            CartUserTempAddedDiscountsProducts: productsInVoucherArray,
            DiscountType: loyaltyMember.VoucherDetails.FormData.DiscountType, // PercentageDiscount = 1, DollarDiscount = 2, DollarFixed = 3
            LoyaltyIssueID: loyaltyMember.VoucherDetails.FormData.IssuedVoucherID,
            ProductType: null,
            VoucherType: 2 // MemberPoint = 1, LoyaltyProduct = 2, NonLoyaltyVoucher = 3, GiftCards = 4
        }
        fetch(`${CommonUtilities.StaticFields.APIDomain}MemberRewardAppRedemableV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            },
            body: JSON.stringify(postVoucherObjToServer)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        LoyaltyModal: {
                            ...prevState.LoyaltyModal,
                            ModalPageShow: 'ConfirmationAddedToCart'
                        },
                        ItemVoucherParentPage: {
                            ...prevState.ItemVoucherParentPage,
                            GeneralError: {
                                ...prevState.ItemVoucherParentPage.GeneralError,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PostAddVoucherToCart: false // Reset
                        }
                    })
                })
                setUrlConfig((prevState) => {
                    return ({
                        ...prevState,
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetUserCart: true // Triggers useEffect that calls getCartDetails() 
                        }
                    })
                })
            } else {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        ItemVoucherParentPage: {
                            ...prevState.ItemVoucherParentPage,
                            GeneralError: {
                                ...prevState.ItemVoucherParentPage.GeneralError,
                                HasError: true,
                                ErrorMessage: data.responseMessage,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PostAddVoucherToCart: false // Reset
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setLoyaltyMember((prevState) => {
                return ({
                    ...prevState,
                    ItemVoucherParentPage: {
                        ...prevState.ItemVoucherParentPage,
                        GeneralError: {
                            ...prevState.ItemVoucherParentPage.GeneralError,
                            HasError: true,
                            ErrorMessage: Messages.ErrorGeneric,
                            ButtonsDisabled: false
                        }
                    },
                    ServerCalls: {
                        ...prevState.ServerCalls,
                        PostAddVoucherToCart: false // Reset
                    }
                })
            })
        })
    }
    // --- end: Item Voucher Parent Page ---

    // --- begin: Item Voucher Product List Page ---
    function handleBackItemVoucherProductList() {
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: 'ItemVoucherParent'
                }
            })
        })
    }

    function handleSelectProductItemVoucherProductList(e) {
        let selectedProductID = e.currentTarget.getAttribute('data-productid');
        let voucherMenuNumber = `voucherMenu${loyaltyMember.ItemVoucherProductListPage.SelectedGroup.VoucherMenuNumber}`;
        let groupDetails = loyaltyMember.VoucherDetails.OriginalData[voucherMenuNumber][0];
        let productsInGroup = groupDetails.products;
        let productDetails = productsInGroup.find(({productID}) => productID == selectedProductID);

        let modifierCategories = []
        let modifiersArray = [];
        modifierCategories = productDetails.modeCategories?.map((modCat) => {
            let modCatFieldName;
            if (modCat.type == "Check") {
                modCatFieldName = `CheckModCatId${modCat.modCategoryID}`;
            } else if (modCat.type == "Radio") {
                modCatFieldName = `RadioModCatId${modCat.modCategoryID}`;
            }
            // Modifiers
            if (modCat.type == "Check") {
                let checkModsArray = modCat.mods?.map((mod, index) => {
                    let isChecked = mod.isDefaultMod;
                    let modFieldName = `CheckModCatId${modCat.modCategoryID}ModId${mod.modID}`; 
                    return ({
                        [modFieldName]: isChecked,
                        ModFieldName: modFieldName, 
                        Value: isChecked,
                        PLU: mod.modPLUNumberInt,
                        ModName: mod.modName,
                        PriceInc: mod.priceInc
                    })
                });
                modifiersArray = checkModsArray;
            } else if (modCat.type == "Radio") {
                let radioModCatName = `RadioModCatId${modCat.modCategoryID}`;
                let radioIdSelected = "";
                let pluSelected = "";
                let modName = "";
                let priceInc;
                modCat.mods.forEach((mod) => {
                    if (mod.isDefaultMod) {
                        radioIdSelected = mod.modID;
                        pluSelected = mod.modPLUNumberInt;
                        modName = mod.modName;
                        priceInc = mod.priceInc
                    }
                });
                let radioModSelected = [{
                    [radioModCatName]: radioIdSelected,
                    ModFieldName: radioModCatName,
                    Value: radioIdSelected,
                    PLU: pluSelected,
                    ModName: modName,
                    PriceInc: priceInc
                }]
                modifiersArray = radioModSelected;
            }

            // Modifier Category
            return ({
                ModCatFieldName: modCatFieldName, 
                Mods: modifiersArray,
                HasError: false,
                Min: modCat.minValue,
                Max: modCat.maxValue
            })
        })
        
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: 'ItemVoucherChild'
                },
                ItemVoucherChildPage: {
                    ...prevState.ItemVoucherChildPage,
                    SelectedProduct: {
                        ...prevState.ItemVoucherChildPage.SelectedProduct,
                        ProductID: selectedProductID,
                        ProductPLU: productDetails.productPLUNumberInt,
                        ProductName: productDetails.productDisplayName,
                        ChargePriceForMods: groupDetails.applyPriceForMod
                    },
                    FormData: {
                        ...prevState.ItemVoucherChildPage.FormData,
                        ModifierCategories: modifierCategories
                    }
                }
            })
        })
    }
    // --- end: Item Voucher Product List Page ---
    
    // --- begin: Item Voucher Child Page ---
    function handleBackItemVoucherChild() {
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: 'ItemVoucherProductList'
                },
                ItemVoucherChildPage: {
                    ...prevState.ItemVoucherChildPage,
                    GeneralError: {
                        ...prevState.ItemVoucherChildPage.GeneralError,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    }
                }
            })
        })
    }

    function handleModChangeItemVoucherChild(e) {
        const { name, value, type, checked } = e.target;
        let modCatName = e.target.getAttribute('data-modcat');
        let modPLU = e.currentTarget.getAttribute('data-modplu');
        let modName = e.currentTarget.getAttribute('data-modname');
        let modPrice = e.currentTarget.getAttribute('data-priceinc');

        let newModCatFormData = loyaltyMember.ItemVoucherChildPage.FormData.ModifierCategories.map((modCat) => {
            if (modCat.ModCatFieldName == modCatName) {
                // Selected Modifier Category
                let modsArray = modCat.Mods.map((mod) => {
                    if (mod.ModFieldName == name) {
                        return {
                            ...mod,
                            [name]: type === 'checkbox' ? checked : value,
                            ModFieldName: name,
                            Value: type === 'checkbox' ? checked : value,
                            PLU: modPLU,
                            ModName: modName,
                            PriceInc: modPrice
                        }
                    } else {
                        return mod
                    }
                })
                return {
                    ...modCat,
                    Mods: modsArray
                }
            } else {
                return modCat
            }
        })

        setLoyaltyMember((prevState) => {
            return({
                ...prevState,
                ItemVoucherChildPage: {
                    ...prevState.ItemVoucherChildPage,
                    FormData: {
                        ...prevState.ItemVoucherChildPage.FormData,
                        ModifierCategories: newModCatFormData
                    }    
                }
            })
        })
    }

    function handleAddToVoucherItemVoucherChild() {
        // Reset Errors
        setLoyaltyMember((prevState) => {
            return({
                ...prevState,
                ItemVoucherChildPage: {
                    ...prevState.ItemVoucherChildPage,
                    GeneralError: {
                        ...prevState.ItemVoucherChildPage.GeneralError,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric,  
                        ButtonsDisabled: false                    
                    }
                }
            })
        })
        
        let errorsAllFields = false;
        let allModsArray = [];
        let newModCatFormData = loyaltyMember.ItemVoucherChildPage.FormData.ModifierCategories.map((modCat) => {
            let minValue = modCat.Min;
            let maxValue = modCat.Max;
            let modCatError = false;

            if (modCat.ModCatFieldName.startsWith('Check')) {
                // Checkbox
                let filteredCheckArray = modCat.Mods.filter((mod) => mod.Value === true);
                allModsArray.push(filteredCheckArray);

                if (filteredCheckArray.length < minValue || filteredCheckArray.length > maxValue)  {
                    modCatError = true;
                    errorsAllFields = true;
                }
            } else if (modCat.ModCatFieldName.startsWith('Radio')) {
                // Radio
                allModsArray.push(modCat.Mods[0]);

                if (modCat.Mods[0].Value == '' || modCat.Mods[0].Value == null) {
                    modCatError = true;
                    errorsAllFields = true;
                } 
            }

            // Return modifier category
            return {
                ...modCat,
                HasError: modCatError
            }
        })

        let allModsArrayFlat = allModsArray.flat(2) ;
        let selectedVoucherGroup = loyaltyMember.ItemVoucherProductListPage.SelectedGroup.VoucherMenuNumber;
        let modalPageToDisplay;

        let updatedVoucherGroups = loyaltyMember.VoucherDetails.FormData.VoucherGroups.map((group) => {
            if (group.KeyForElement == selectedVoucherGroup) {
                return {
                    ...group,
                    ProductID: loyaltyMember.ItemVoucherChildPage.SelectedProduct.ProductID,
                    ProductPLU: loyaltyMember.ItemVoucherChildPage.SelectedProduct.ProductPLU,
                    ProductName: loyaltyMember.ItemVoucherChildPage.SelectedProduct.ProductName,
                    Mods: allModsArrayFlat
                }
            } else {
                return group
            }
        });

        if (errorsAllFields) {
            modalPageToDisplay = 'ItemVoucherChild';
        } else {
            modalPageToDisplay = 'ItemVoucherParent';
        }

        setLoyaltyMember((prevState) => {
            return({
                ...prevState,
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalPageShow: modalPageToDisplay
                },
                ItemVoucherChildPage: {
                    ...prevState.ItemVoucherChildPage,
                    FormData: {
                        ...prevState.ItemVoucherChildPage.FormData,
                        ModifierCategories: newModCatFormData
                    },
                    GeneralError: {
                        ...prevState.ItemVoucherChildPage.GeneralError,
                        HasError: errorsAllFields,
                        ErrorMessage: Messages.ErrorProductForm,
                    }
                },
                VoucherDetails: {
                    ...prevState.VoucherDetails,
                    FormData: {
                        ...prevState.VoucherDetails.FormData,
                        VoucherGroups: updatedVoucherGroups
                    }
                }
            })
        })
    }
    // --- end: Item Voucher Child Page ---
    
    // --- begin: Loyalty Server calls ---
    React.useEffect(function() {
        if (loyaltyMember.ServerCalls.SendOtpSms) {
            sendOtpToMobile();
        } else if (loyaltyMember.ServerCalls.ResendOtpStartTimer) {
            intervalShowResendOtp();
        } else if (loyaltyMember.ServerCalls.PostOtp) {
            let otpBody = {
                "OTP": loyaltyMember.OtpModalPage.Fields.authotp.Value,
                "Mobile": loyaltyMember.MobileModalPage.Fields.authmobile.Value
            }
            postOtpToServer(otpBody)
        } else if (loyaltyMember.ServerCalls.GetVouchers) {
            getVoucherList();
        } else if (loyaltyMember.ServerCalls.GetItemVoucherDetails) {
            getItemVoucherDetails();
        } else if (loyaltyMember.ServerCalls.GetSaleVoucherDetails) {
            //console.log('get voucher details - sale > add directly to cart');
        } else if (loyaltyMember.ServerCalls.PostInitiateVoucherAddToCart) {
            postInitiateVoucherAddToCart();
        } else if (loyaltyMember.ServerCalls.PostAddVoucherToCart) {
            postAddVoucherToCart();
        }
    }, [loyaltyMember.ServerCalls]);

    function sendOtpToMobile() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}MemberOTPForWebOrder?BrandCode=${CommonUtilities.StaticFields.BrandCode}&Mobile=${loyaltyMember.MobileModalPage.Fields.authmobile.Value}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            }
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        LoyaltyModal: {
                            ...prevState.LoyaltyModal,
                            ModalPageShow: 'OTP'
                        },
                        MobileModalPage: {
                            ...prevState.MobileModalPage,
                            GeneralError: {
                                ...prevState.MobileModalPage.GeneralError,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            SendOtpSms: false,
                            ResendOtpStartTimer: true,
                            ResendOtpCountdownSeconds: 15
                        }
                    })
                })
            } else {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        MobileModalPage: {
                            ...prevState.MobileModalPage,
                            GeneralError: {
                                ...prevState.MobileModalPage.GeneralError,
                                HasError: true,
                                ErrorMessage: data.responseMessage,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            SendOtpSms: false
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        MobileModalPage: {
                            ...prevState.MobileModalPage,
                            GeneralError: {
                                ...prevState.MobileModalPage.GeneralError,
                                 HasError: true,
                                ErrorMessage: Messages.ErrorGeneric,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            SendOtpSms: false
                        }
                    })
                })
        })
    }

    // ---------- State: Customer Details Object to POST / PUT ----------
    const [customerDetailsObjToPost, setCustomerDetailsObjToPost] = React.useState({
        PutDetailsAfterOTP: false, // Triggers useEffect that calls putCustomerDetailsAfterOtp()
        PutDetailsAfterWard: false, // Triggers useEffect that calls putCustomerWardDetails()
        PostToServer: false,
        PostObject: {
            LocationNumber: "",
            TableNumber: "",
            BrandCode: "",
            DeviceUID: "",
            FirstName: "",
            LastName: "",
            Email: "",
            Mobile: "",
            MemberCardNumber: "",
            Type: "",
            IsWardInfo: "",
            WardInfoPayload: "",
            BrandMemberID: -1,
            DoorDashEstimatePayload: null
        }
    })

    React.useEffect(() => {
        if (customerDetailsObjToPost.PutDetailsAfterOTP) {
            putCustomerDetailsAfterOtp();
        } else if (customerDetailsObjToPost.PutDetailsAfterWard) {
            putCustomerWardDetails();
        }
    }, [customerDetailsObjToPost.PutDetailsAfterOTP, customerDetailsObjToPost.PutDetailsAfterWard])

    function postOtpToServer(jsonBody) {
        fetch(`${CommonUtilities.StaticFields.APIDomain}MemberOTPForWebOrder?BrandCode=${CommonUtilities.StaticFields.BrandCode}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            },
            body: JSON.stringify(jsonBody)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setCustomerDetailsObjToPost((prevState) => {
                    let isWard = urlConfig.WardInfo == 1;
                    return ({
                        ...prevState,
                        PutDetailsAfterOTP: true, // Triggers useEffect that calls putCustomerDetailsAfterOtp()
                        PostObject: {
                            ...prevState.PostObject,
                            LocationNumber: urlConfig.LocationQuery,
                            TableNumber: urlConfig.TableNumber, 
                            BrandCode: CommonUtilities.StaticFields.BrandCode,
                            DeviceUID: urlConfig.DeviceUID, 
                            FirstName: data.memberDetails.firstName,
                            LastName: data.memberDetails.lastName,
                            Email: data.memberDetails.email,
                            Mobile: data.memberDetails.mobile,
                            MemberCardNumber: data.memberDetails.cardNumber,
                            IsWardInfo: isWard,
                            WardInfoPayload: wardDetails.WardDetailsPayload,
                            BrandMemberID: data.memberDetails.memberID
                        }
                    })
                })
                setUrlConfig((prevState) => {
                    return ({
                        ...prevState,
                        LoyaltyMemberId: data.memberDetails.memberID,
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetUserCart: true // Triggers useEffect to call getCartDetails()
                        }
                    })
                })
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        MenuNav: {
                            ...prevState.MenuNav,
                            LoyaltyMemberButtonValue: data.memberDetails.firstName,
                            IsLoggedIn: true,
                            CardNumber: data.memberDetails.cardNumber
                        },
                        LoyaltyModal: {
                            ...prevState.LoyaltyModal,
                            ModalPageShow: 'Vouchers'
                        },
                        VoucherModalPage: {
                            ...prevState.VoucherModalPage,
                            ShowVouchersLoading: true
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PostOtp: false,
                            GetVouchers: true // Triggers useEffect that calls getVoucherList()
                        }
                    })
                })
                setCustomerDetailsFormData((prevState) => {
                    return ({
                        ...prevState,
                        formData: {
                            ...prevState.formData,
                            MemberID: data.memberDetails.memberID,
                            firstname: data.memberDetails.firstName,
                            lastname: data.memberDetails.lastName,
                            email: data.memberDetails.email,
                            mobile: data.memberDetails.mobile,
                            cardnumber: data.memberDetails.cardNumber
                        }
                    })
                })
            } else {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        OtpModalPage: {
                            ...prevState.OtpModalPage,
                            GeneralError: {
                                ...prevState.OtpModalPage.GeneralError,
                                HasError: true,
                                ErrorMessage: data.responseMessage,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            PostOtp: false,
                            ResendOtpStartTimer: true,
                            ResendOtpCountdownSeconds: 15
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setLoyaltyMember((prevState) => {
                return ({
                    ...prevState,
                    OtpModalPage: {
                        ...prevState.OtpModalPage,
                        GeneralError: {
                            ...prevState.OtpModalPage.GeneralError,
                            HasError: true,
                            ErrorMessage: Messages.ErrorGeneric,
                            ButtonsDisabled: false
                        }
                    },
                    ServerCalls: {
                        ...prevState.ServerCalls,
                        PostOtp: false,
                        ResendOtpStartTimer: true,
                        ResendOtpCountdownSeconds: 15
                    }
                })
            })
        })
    }

    function putCustomerDetailsAfterOtp() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}CustomerForWebOrder?DeviceUID=${urlConfig.DeviceUID}&BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "PUT",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID
            },
            body: JSON.stringify(customerDetailsObjToPost.PostObject)
        })
        .then((response) => response.json())
        .then((data) => {
            setCustomerDetailsObjToPost((prevState) => {
                return ({
                    ...prevState,
                    PutDetailsAfterOTP: false // Reset useEffect trigger
                })
            })
            if (data.isRequestSuccessful) {
                // Do nothing
            } else {
                // Do nothing
            }
        })
        .catch((error) => {
            setCustomerDetailsObjToPost((prevState) => {
                return ({
                    ...prevState,
                    PutDetailsAfterOTP: false // Reset useEffect trigger
                })
            })
            // Do nothing
        })
    }

    function getVoucherList() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}MemberRewardAppRedemableV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.LoyaltyMemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            }
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        VoucherModalPage: {
                            ...prevState.VoucherModalPage,
                            Vouchers: data.vouchers,
                            ShowVouchersLoading: false,
                            GeneralError: {
                                ...prevState.VoucherModalPage.GeneralError,
                                HasError: false,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetVouchers: false
                        }
                    })
                })
            } else {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        VoucherModalPage: {
                            ...prevState.VoucherModalPage,
                            Vouchers: [],
                            ShowVouchersLoading: false,
                            GeneralError: {
                                ...prevState.VoucherModalPage.GeneralError,
                                HasError: true,
                                ErrorMessage: data.responseMessage,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetVouchers: false
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setLoyaltyMember((prevState) => {
                return ({
                    ...prevState,
                    VoucherModalPage: {
                        ...prevState.VoucherModalPage,
                        Vouchers: [],
                        ShowVouchersLoading: false,
                        GeneralError: {
                            ...prevState.VoucherModalPage.GeneralError,
                            HasError: true,
                            ErrorMessage: Messages.ErrorGeneric,            
                            ButtonsDisabled: false
                        }
                    },
                    ServerCalls: {
                        ...prevState.ServerCalls,
                        GetVouchers: false
                    }
                })
            })
        })
    }

    function getLoyaltyLogout() {
        setUrlConfig((prevState) => {
            return ({
                ...prevState,
                LoyaltyMemberId: null
            })
        });
        setLoyaltyMember((prevState) => {
            return ({
                ...prevState,
                MenuNav: {
                    ...prevState.MenuNav,
                    LoyaltyMemberButtonValue: 'Guest',
                    IsLoggedIn: false,
                    CardNumber: ""
                },
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalIsShown: false,
                    ModalPageShow: 'Login'
                },
                MobileModalPage: {
                    ...prevState.MobileModalPage,
                    Fields: {
                        ...prevState.MobileModalPage.Fields,
                        authmobile: {
                            ...prevState.MobileModalPage.Fields.authmobile,
                            Value: '',
                            HasError: false,
                        }
                    },
                    GeneralError: {
                        ...prevState.MobileModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                OtpModalPage: {
                    ...prevState.OtpModalPage,
                    Fields: {
                        ...prevState.OtpModalPage.Fields,
                        authotp: {
                            ...prevState.OtpModalPage.Fields.authotp,
                            Value: '',
                            HasError: false
                        }

                    },
                    GeneralError: {
                        ...prevState.OtpModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                VoucherModalPage: {
                    ...prevState.VoucherModalPage,
                    Vouchers: [],
                    GeneralError: {
                        ...prevState.VoucherModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    SendOtpSms: false,
                    PostOtp: false,
                    GetVouchers: false,
                    GetLogout: false,
                    ResendOtpStartTimer: false,
                    ResendOtpCountdownSeconds: 15,
                    GetItemVoucherDetails: false,
                    GetSaleVoucherDetails: false
                }
            })
        });
        // Reset Customer Details
        setCustomerDetailsFormData((prevState) => {
            return ({
                ...prevState,
                formData: {
                    ...prevState.formData,
                    MemberID: "",
                    firstname: "",
                    lastname: "",
                    email: "",
                    mobile: "",
                    cardnumber: ""
                }
            })
        });
        getTokenDetails();
    }

    function getItemVoucherDetails() {
        let voucherDetails = loyaltyMember.ItemVoucherParentPage;
        fetch(`${CommonUtilities.StaticFields.APIDomain}MemberRewardLoyaltyVoucherDetailsV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&IssuedVoucherID=${voucherDetails.SelectedVoucherToGetDetails.IssuedVoucherID}&MemberId=${voucherDetails.SelectedVoucherToGetDetails.MemberID}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            }
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                // Map voucher product groups
                let voucherGroups = [];
                if (data.voucherDetails.voucherMenu1.length > 0) {
                    voucherGroups.push({
                        GroupName: data.voucherDetails.voucherMenu1[0].name,
                        ProductID: '',
                        ProductPLU: '',
                        ProductName: '',
                        Mods: [],
                        KeyForElement: 1, // Voucher Menu Number
                        ChargePriceForMods: data.voucherDetails.voucherMenu1[0].applyPriceForMod,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    })
                }
                if (data.voucherDetails.voucherMenu2.length > 0) {
                    voucherGroups.push({
                        GroupName: data.voucherDetails.voucherMenu2[0].name,
                        ProductID: '',
                        ProductPLU: '',
                        ProductName: '',
                        Mods: [],
                        KeyForElement: 2, // Voucher Menu Number
                        ChargePriceForMods: data.voucherDetails.voucherMenu2[0].applyPriceForMod,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    })
                }
                if (data.voucherDetails.voucherMenu3.length > 0) {
                    voucherGroups.push({
                        GroupName: data.voucherDetails.voucherMenu3[0].name,
                        ProductID: '',
                        ProductPLU: '',
                        ProductName: '',
                        Mods: [],
                        KeyForElement: 3, // Voucher Menu Number
                        ChargePriceForMods: data.voucherDetails.voucherMenu3[0].applyPriceForMod,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    })
                }
                
                // Voucher Form Data
                let formData = {
                    IssuedVoucherID: data.voucherDetails.issuedVoucherID,
                    FieldName: `radiovoucherissueid${data.voucherDetails.issuedVoucherID}`,
                    Quantity: 0,
                    VoucherGroups: voucherGroups,
                    DiscountType: data.voucherDetails.discountType
                }

                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        LoyaltyModal: {
                            ...prevState.LoyaltyModal,
                            ModalPageShow: 'ItemVoucherParent'
                        },
                        VoucherModalPage: {
                            ...prevState.VoucherModalPage,
                            GeneralError: {
                                ...prevState.VoucherModalPage.GeneralError,
                                ButtonsDisabled: false
                            }
                        },
                        VoucherDetails: {
                            ...prevState.VoucherDetails,
                            OriginalData: data.voucherDetails,
                            FormData: formData
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetItemVoucherDetails: false
                        }
                    })
                })
                // success
            } else {
                setLoyaltyMember((prevState) => {
                    return ({
                        ...prevState,
                        VoucherModalPage: {
                            ...prevState.VoucherModalPage,
                            GeneralError: {
                                ...prevState.VoucherModalPage.GeneralError,
                                HasError: true,
                                ErrorMessage: data.responseMessage,
                                ButtonsDisabled: false
                            }
                        },
                        ServerCalls: {
                            ...prevState.ServerCalls,
                            GetItemVoucherDetails: false
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setLoyaltyMember((prevState) => {
                return ({
                    ...prevState,
                    VoucherModalPage: {
                        ...prevState.VoucherModalPage,
                        GeneralError: {
                            ...prevState.VoucherModalPage.GeneralError,
                            HasError: true,
                            ErrorMessage: Messages.ErrorGeneric,
                            ButtonsDisabled: false
                        }
                    },
                    ServerCalls: {
                        ...prevState.ServerCalls,
                        GetItemVoucherDetails: false
                    }
                })
            })
        })
    }

    // --- begin: Loyalty Server calls ---

    // ====================== end: Loyalty Member - Navigation Bar & Authentication ====================== 
    
    // ====================== begin: Product Menu - Main & Scroll Spy ====================== 

    // ---------- State: Product Menu ----------
    const [productData, setProductData] = React.useState([]);
    const [fullMenuData, setFullMenuData] = React.useState([]);
    const [productsLoading, setProductsLoading] = React.useState(true);
    const [menuError, setMenuError] = React.useState({
      HasError: false,
      ErrorMessage: Messages.ErrorGeneric
    });
    
    // ---------- useEffect - Menu: Get Product Menu ----------
    React.useEffect(function() {
        if (currentLocation.LocationID !== "") {
            // GET Token / Customer Details
            getTokenDetails();
            fetchProductMenu(currentLocation.LocationID);
            if (urlConfig.DeviceUID !== "" && urlConfig.MemberId !== "" && urlConfig.MemberId != null && urlConfig.MemberId != 'null') {
                getCartDetails();   
            }
        }
        if (!alertModal.IsInitialLocation && amountInCart !== 0) {
            clearCart();
        }
    }, [currentLocation.LocationID]);

    // ---------- Function - Menu: Get Product Menu ----------
    function fetchProductMenu(location) {
        setMenuError({
            HasError: false,
            ErrorMessage: Messages.ErrorLoadingMenu
        });
        
        // === begin: Bulk Pricing ===
        let productMenuUrl;
        if (CommonUtilities.StaticFields.PriceType == 'bp8826') {
            productMenuUrl = `${CommonUtilities.StaticFields.APIDomain}ProductListForBulkPricing?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${location}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        } else {
            productMenuUrl = `${CommonUtilities.StaticFields.APIDomain}ProductListWithComboV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${location}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        }
        // === end: Bulk Pricing ===

        fetch(`${productMenuUrl}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
            "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
            BrandUID: CommonUtilities.StaticFields.BrandUID,
            DeviceUID: urlConfig.DeviceUID
          }
        })
        .then((response) => response.json())
        .then((data) => {
          setProductsLoading(false);
          if (data.isRequestSuccessful) {

            setFullMenuData(data);
            // Filter Products
            // --> Modifier Combo excluded from menu for products inside Product Combos
            const filteredProducts = data.productDetails.map((prodCat) => {
                
                return {
                    ...prodCat,
                    // Product Categories > Product
                    products: prodCat.products.map((product) => {
                        // Product Categories > Product > Product Combo
                        if (product.comboGroups !== null) {
                            return {
                                ...product,
                                // Product Categories > Product > Product Combo > Groups
                                comboGroups: product.comboGroups.map((comboGroup) => {
                                    if (comboGroup.products.length > 0) {
                                        return {
                                            ...comboGroup,
                                            // Product Categories > Product > Product Combo > Groups > Products
                                            products: comboGroup.products.map((product) => {
                                                if (product.modCategories.length > 0) {
                                                    return {
                                                        ...product,
                                                        // Product Categories > Product > Product Combo > Groups > Products > Modifier Categories > Block Modifier Combos 
                                                        // (Cannot have Modifier Combos in Products attached to a Product Combo)
                                                        modCategories: product.modCategories.filter((mod) => {
                                                            if (mod.itemUsageTypeName !== 'Combo') {
                                                                return mod
                                                            }
                                                        })
                                                    }
                                                } else {
                                                    return product
                                                }
                                            })
                                        }
                                    } else {
                                        return comboGroup
                                    }
                                }) 
                            }
                        } else {
                            return product
                        }
                    })
                }
            });

            // Filter Product Categories (only show if Products > 0 as some now may be empty categories)
            const filteredProdCats = filteredProducts.filter((prodCat) => {
                return prodCat.products.length > 0
            })
            setProductData(filteredProdCats);

            // Empty Menu
            if (data.productDetails.length <= 0) {
                setOfflineModal({
                    IsShown: true
                });
                if (urlConfig.Type === '' || urlConfig.Type == null || urlConfig.Type == 'null' || urlConfig.Type == 'web') {
                    setShowLocModal(true);
                } else {
                    setShowLocModal(false);
                }
            } else {
                setShowLocModal(false);
            }

            // Validate Table Number
            setCurrentLocation((prevState) => {
                return (
                    {
                        ...prevState,
                        ProductPriceTierID: data.productPriceTierID,
                        ModPriceTierID: data.modPriceTierID,
                        Validation: {
                            ...prevState.Validation,
                            IsValidTableNumber: prevState.Validation.IsValidTableNumber,
                        }
                    }
                )
            });

            setCustomerDetailsFormData((prevState) => {
                return ({
                    ...prevState,
                    formData: {
                        ...prevState.formData,
                        TandCLink: data.termsAndConditionsURL
                    }
                })
            })
          } else {
            setMenuError({
              HasError: true,
              ErrorMessage: Messages.ErrorLoadingMenu
            });
          }
        })
        .catch((error) => {
          setProductsLoading(false);
          setMenuError({
            HasError: true,
            ErrorMessage: Messages.ErrorLoadingMenu
          });
        })
    }

    // ---------- Function - Menu > Scrollspy: Change selected product category scroll spy ----------
    function handleScrollspySelectorClick(event) {
        let activeScrollspyId = event.currentTarget.id;
        // Remove highlight from all
        document.querySelectorAll('.scrollspySelector').forEach(item => {
           item.classList.remove('active-scroll-spy');
       });
       // Highlight clicked
       document.getElementById(activeScrollspyId).classList.add('active-scroll-spy');

        document.addEventListener('scrollend', function() { // Scroll end as scroll will override the above action
            // Remove highlight from all
            document.querySelectorAll('.scrollspySelector').forEach(item => {
                item.classList.remove('active-scroll-spy');
            });
            // Highlight clicked
            document.getElementById(activeScrollspyId).classList.add('active-scroll-spy');
        });
    }

    // ---------- Function - Menu > Product Menu Click: Populate Product Details Modal ----------
    function handleProductMenuClick(event) {
        
        // Regular Product and Product Combo
        const productCategoryList = productData.find((item) => item.productCategoryId == event.currentTarget.getAttribute('data-productCategoryId'));
        const productDetailsOriginal = productCategoryList.products.find((item) => {
            return item.productId == event.currentTarget.getAttribute('data-productId')
        });
        const prodCatId = event.currentTarget.getAttribute('data-productCategoryId'); 
        // --- Product - Filter out Modifier Combos that have no modLinkedToCombo price ---
        let filteredMods = productDetailsOriginal.modCategories.filter((modCat) => {
            if (modCat.itemUsageTypeName === "Regular") {
                return modCat
            } else if (modCat.itemUsageTypeName === "Combo" && modCat.modLinkedToCombo !== null && modCat.modLinkedToCombo !== "") {
                return modCat
            }
        });
        const productDetails = { // Filter out Modifier Combos that have no modLinkedToCombo price (causes error) 
            ...productDetailsOriginal,
            modCategories: filteredMods
        }
        const productType = productDetailsOriginal.itemUsageTypeID; // Item Usage Type ID: 1 = Regular, 2 = Combo
        let productComboArray, modsArray, modCategoryArray, productComboGroups;
        // Special Instructions / Product Notes
        let showSpecialInstructionsProduct = false;
        let showSpecialInstructionsMod = false;
        let titleSpecialInstr = "";
        let charSpecialInstr = 30; // Default
        setProductDetails(productDetails);
        if (productType == 1) { // Product Type: Regular
            setAddToCartError({
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
            setProductFormDataToPost("");
            
            // Special Instructions
            if (!productDetails.hideSpecialInstruction) { // false = Show
                showSpecialInstructionsProduct = true;
                titleSpecialInstr = productDetails.specialInstructionTitle;
                charSpecialInstr = productDetails.specialInstructionMaxLength;
            }
            
            // Modifier Category Validation
            modCategoryArray = productDetails.modCategories.map((modCat) => {
                return { 'ModCatId': modCat.modCategoryId, 'Type': modCat.type, 'Min': modCat.minValue, 'Max': modCat.maxValue, 'HasError': false, 'ErrorMessage': Messages.ErrorGeneric }
            });
            // --- Product (Regular) Form Data - Remapping ---
            let mods = productDetails.modCategories.map((item) => {
            const modCatId = item.modCategoryId;
                if (item.itemUsageTypeName === "Regular") {
                    if (item.type === "Check") { 
                        // Modifier Category = Check
                        const checkModsArray = item.mods.map((item) => {
                            if (item.isDefaultMod && !item.hideSpecialInstruction) {
                                showSpecialInstructionsMod = true;
                                titleSpecialInstr = item.specialInstructionTitle;
                                charSpecialInstr = item.specialInstructionMaxLength;
                            }
                            const checkName = `CheckModCatId${modCatId}ModId${item.modId}`
                            return { 'name': checkName, 'value': item.isDefaultMod, 'plu': item.modPLUNumber, 'modCatId': modCatId, 'modComboGroups': null, 'modComboPriceInc': null, 'modComboLinkedModId': null, 'showSpecialInstruction': !item.hideSpecialInstruction, 'isChecked': item.isDefaultMod, 'titleSpecialInstr': item.specialInstructionTitle, 'charSpecialInstr': item.specialInstructionMaxLength }
                        })
                        return (checkModsArray);
                    } else if (item.type === "Radio") { 
                        // Modifier Category = Radio
                        let radioIdSelected = "";
                        let pluSelected = "";
                        let radioShowSpecialInstr = false;
                        item.mods.forEach((innerItem) => {
                            if (innerItem.isDefaultMod) {
                                if (!innerItem.hideSpecialInstruction) {
                                    showSpecialInstructionsMod = true;
                                    radioShowSpecialInstr = true;
                                    titleSpecialInstr = innerItem.specialInstructionTitle;
                                    charSpecialInstr = innerItem.specialInstructionMaxLength;
                                }
                                radioIdSelected = innerItem.modId;
                                pluSelected = innerItem.modPLUNumber;
                            }
                        });
    
                        const radioName = `RadioModCatId${item.modCategoryId}`;
                        return { 'name': radioName, 'value' : radioIdSelected, 'plu': pluSelected, 'modCatId': item.modCategoryId, 'modComboGroups': null, 'modComboPriceInc': null, 'modComboLinkedModId': null, 'showSpecialInstruction': radioShowSpecialInstr, 'isChecked': null, 'titleSpecialInstr': titleSpecialInstr, 'charSpecialInstr': charSpecialInstr }
                    } else if (item.type === "Substitution") { 
                        // Modifier Category = Substitution Category (Note - this is always a radio and max 1 in a product)
                        let subRadioIsSelected = "";
                        let subPluSelected = "";
                        let radioShowSpecialInstr = false;
                        item.mods.forEach((innerItem) => {
                            if (innerItem.isDefaultMod) {
                                if (!innerItem.hideSpecialInstruction) {
                                    showSpecialInstructionsMod = true;
                                    radioShowSpecialInstr = true;
                                    titleSpecialInstr = innerItem.specialInstructionTitle;
                                    charSpecialInstr = innerItem.specialInstructionMaxLength;
                                }
                                subRadioIsSelected = innerItem.modId;
                                subPluSelected = innerItem.modPLUNumber;
                            }
                        });
    
                        const subRadioName = `SubRadioModCatId${item.modCategoryId}`;
                        return { 'name': subRadioName, 'value' : subRadioIsSelected, 'plu': subPluSelected, 'modCatId': item.modCategoryId, 'modComboGroups': null, 'modComboPriceInc': null, 'modComboLinkedModId': null, 'showSpecialInstruction': radioShowSpecialInstr, 'isChecked': null, 'titleSpecialInstr': titleSpecialInstr, 'charSpecialInstr': charSpecialInstr }
                    }
                } else if (item.itemUsageTypeName === "Combo") {
                    // Modifier Combo (Note - this can only contain Modifier Categories that are radios but can contain more than one of the same group)
                    let modComboGroupIndex = -1;
                    const modComboGroups = item.modComboGroups.map(function(groupItem) {
                        modComboGroupIndex += 1; // Starts at 0 index
                        let defaultModId, defaultModPlu, defaultModName, defaultModPriceInc;
                        // Modifiers inside selected Group inside selected Modifier Combo - mapped out for determining form data control
                        let modsInGroup = groupItem.mods.map((modInGroup) => {
                            if (modInGroup.isDefaultMod) {
                                defaultModId = modInGroup.modId;
                                defaultModPlu = modInGroup.modPLUNumberInt;
                                defaultModName = modInGroup.modName;
                                defaultModPriceInc = modInGroup.priceInc;
                            }
                            // Modifier in Modifier Combo Group
                            return (
                                {
                                    'IsChecked': modInGroup.isDefaultMod,
                                    'IsDefaultMod': modInGroup.isDefaultMod,
                                    'FieldName': `RadioModComboModCatId${item.modCategoryId}ModGroupIndex${modComboGroupIndex}ModId${modInGroup.modId}`,
                                    'ModifierName': modInGroup.modName,
                                    'ModifierComboGroupIndex': modComboGroupIndex,
                                    'ModifierCategoryID': item.modCategoryId,
                                    'PriceInc': modInGroup.priceInc,
                                    'ModId': modInGroup.modId,
                                    'ModPLU': modInGroup.modPLUNumberInt
                                }
                            )
                        }) 
                        // Modifier Combo Group
                        return {
                            'FieldName': `ModCombo${item.modCategoryId}ModGroup${modComboGroupIndex}`,
                            'ModComboGroupIndex': modComboGroupIndex,
                            'ModComboGroupCategoryID': groupItem.modCategoryId,
                            'ModifierID': defaultModId, // Modifier is either the default modifier or has been selected by the user in the group
                            'ModifierPLU': defaultModPlu, // Modifier is either the default modifier or has been selected by the user in the group
                            'ModifierName': defaultModName,
                            'PriceInc': defaultModPriceInc,
                            'Quantity': 0, // Determines if the user has selected an option (including the default mod - will be 1 if selected and 0 if empty)
                            'ModsInGroup': modsInGroup,
                            'ValidationModComboGroup': {
                                    'HasError': false,
                                    'ErrorMessage': Messages.ErrorGeneric,
                                    'Min': groupItem.minValue,
                                    'Max': groupItem.maxValue,
                                    'Type': groupItem.type,
                                }
                            }
                    })
                    // Modifier Combo
                    return { 'name': `ModComboModCatId${item.modCategoryId}`, 'value': false, 'plu': `${item.modLinkedToCombo.modPLUNumber}`, 'modCatId': `${item.modCategoryId}`, 'modComboGroups': modComboGroups, 'modComboPriceInc': `${item.modLinkedToCombo.priceInc}`, 'modComboLinkedModId': `${item.modLinkedToCombo.modId}`, 'showSpecialInstruction': false, 'isChecked': null, 'titleSpecialInstr': '', 'charSpecialInstr': 0 } // Is Checked is null for Combo
                }
            });
            const modsFlat = mods.flat(2);
            modsArray = modsFlat.map((item) => {
                return ({ [item.name] : item.value, 'plu': item.plu, 'fieldName': item.name, 'modCatId': item.modCatId, 'modComboGroups': item.modComboGroups, 'modComboPriceInc': item.modComboPriceInc, 'modComboLinkedModId': item.modComboLinkedModId, 'showSpecialInstruction': item.showSpecialInstruction, 'isChecked': item.isChecked, 'titleSpecialInstr': item.titleSpecialInstr, 'charSpecialInstr': item.charSpecialInstr })
            });

        } else if (productType == 2) { // Product Type: Product Combo

            setAddToCartError({
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
            setProductFormDataToPost("");

            // Special Instructions Product
            showSpecialInstructionsProduct = false; // Not visible for Combo

            // Product Combo Group Validation
            productComboGroups = productDetails.comboGroups.map((item) => { // Validation for Product Combo Groups - always a Radio
                return { 'ProductComboGroupID': item.productComboGroupID, 'Min': 1, 'Max': 1, 'HasError': false, 'ErrorMessage': 'An error occurred. Please try again.' }
            });
            // --- Product (Combo) Form Data - Remapping ---
            productComboArray = productDetails.comboGroups.map((item) => { // This will be what is displayed on the form in the Product Combo Parent Modal
                return {
                    'ProductCategoryId': (event.currentTarget.getAttribute('data-productCategoryId')),
                    'productComboGroupID': item.productComboGroupID,
                    'productComboGroupName': item.productComboGroupName,
                    'ProductID': null,
                    'ProductPLU': null,
                    'ProductName': '',
                    'Quantity': 0,
                    'PriceIncForComboSubstitution': '', // Used to calculate the price difference for a selected product with a substitution category for a product group in a product combo
                    'OriginalProductID': null, // Server to calculate price difference for substitution category
                    'OriginalProductPLUNumber': null, // Server to calculate price difference for substitution category
                    'CalculatePriceDifference': false, // Check if Product Combo needs to calculate price difference
                    'SpecialInstructions': '',
                    'ItemUsageTypeID': null,
                    'ProductsInCombo': null,
                    'Mods': []
                 }
            });
        }

        // Shared
        setProductFormData((prevState) => {
            return({
                ...prevState,
                data: {
                    ...prevState.data,
                    ProductCategoryId: prodCatId,
                    ProductID: productDetails.productId,
                    ProductPLU: productDetails.productPLUNumberInt,
                    ProductsInCombo: productDetails.itemUsageTypeID === 1 ? null : productComboArray, // ItemUsageTypeID = 1 (Regular), 2 (Combo)
                    Quantity: 1,
                    SpecialInstructions: "",
                    ItemUsageTypeID: productDetails.itemUsageTypeID,
                    MemberId: customerDetailsFormData.formData.MemberID,
                    CardNumber: customerDetailsFormData.CardNumber,
                    BulkPricingProductsID: productDetails.bulkPricingProductsID,
                    Mods: productDetails.itemUsageTypeID === 1 ? modsArray : null // ItemUsageTypeID = 1 (Regular), 2 (Combo)
                },
                config: {
                    ...prevState.config,
                    SpecialInstructions: {
                        ...prevState.config.SpecialInstructions,
                        IsShownProduct: showSpecialInstructionsProduct,
                        IsShownMod: showSpecialInstructionsMod,
                        Title: titleSpecialInstr,
                        CharacterLength: charSpecialInstr
                    },
                    ProductSpecialInstructions: {
                        ProductTitle: productDetails.specialInstructionTitle,
                        ProductCharacterLength: productDetails.specialInstructionMaxLength
                    }
                },
                validation: {
                    ...prevState.validation,
                    PostToServer: false,
                    HasErrorAllFields: false,
                    ModCategories: modCategoryArray,
                    ProductComboGroups: productComboGroups,
                    SpecialInstructions: {
                        ...prevState.validation.SpecialInstructions,
                        Min: 0,
                        Max: charSpecialInstr,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    },
                    Quantity: {
                        Min: 1,
                        Max: 30,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    }
                }
            })
        });
        setProductModalDisplay((prevState) => {
            return ({
                ...prevState,
                showProductModal: true,
                showModalPage: "Parent Details",
                productType: productDetails.itemUsageTypeName,
            })
        });
    }

    

    // ====================== end: Product Menu - Main & Scroll Spy  ====================== 
    
    // ====================== begin: Product Details Modal & Add to Cart ====================== 

    // ---------- State: Product Details Modal ----------
    const [productDetails, setProductDetails] = React.useState({});
    const [productComboChildDetails, setProductComboChildDetails] = React.useState({
        productList: [],
        selectedProductDetails: {}
    });
    const [modifierComboDetails, setModifierComboDetails] = React.useState({
        modifierComboDetails: {},
        selectedGroupDetails: {},
        selectedGroupIndex: null
    })
    const [productModalDisplay, setProductModalDisplay] = React.useState({
        showProductModal: false,
        showModalPage: "Parent Details", // 'Parent Details', 'Modifier Combo Groups List', 'Modifier Combo Group Details', 'Product Combo Child Details' or 'Product Combo List'
        productType: "", // Combo or Regular
    });
    const [productFormData, setProductFormData] = React.useState({
        // Changes the selection of form elements (checks, radio) for the Product Modal in the UI
        data: {
            ProductCategoryId: "",
            ProductID: "",
            ProductPLU: "",
            ProductsInCombo: "",
            Quantity: 1,
            SpecialInstructions: "",
            ItemUsageTypeID: "",
            MemberId: "",
            CardNumber: "",
            BulkPricingProductsID: "",
            Mods: []
        },
        config: {
            SpecialInstructions: {
                IsShownProduct: false,
                IsShownMod: false,
                Title: "",
                CharacterLength: 30 // Default
            },
            ProductSpecialInstructions: { // Needed if Modifiers are unticked, can revert back to Product
                ProductTitle: "",
                ProductCharacterLength: 30 // Default
            }
        },
        validation: {
            PostToServer: false,
            HasErrorAllFields: false,
            ModCategories: [],
            ProductComboGroups: [],
            SpecialInstructions: {},
            Quantity: {}
        }
    });

    // ---------- State: Product Combo Child - Form Data and Validation ----------
    const [productComboChildFormData, setProductComboChildFormData] = React.useState({
        data: {
            ProductCategoryId: "",
            ProductComboGroupID: "",
            ProductComboGroupName: "",
            ProductID: "",
            ProductPLU: "",
            ProductName: "",
            ProductsInCombo: "",
            Quantity: 1,
            PriceIncForComboSubstitution: "", // Used to calculate the price difference for a selected product with a substitution category for a product group in a product combo
            SpecialInstructions: "",
            ItemUsageTypeID: "",
            MemberId: "",
            CardNumber: "",
            Mods: []
        },
        validation: {
            PostToServer: false,
            HasErrorAllFields: false,
            ModCategories: [],
            SpecialInstructions: {},
            Quantity: {}
        }
    });

    // ---------- State: Product Details - POST - Add to Cart ----------
    const [productFormDataToPost, setProductFormDataToPost] = React.useState(""); // Gets the Form Data and formats it to post to the server
    const [addToCartError, setAddToCartError] = React.useState({
        HasError: false,
        ErrorMessage: Messages.ErrorGeneric,
        ButtonsDisabled: false
    });

    // ---------- State: Modifier Combo Errors ----------
    const [errorModifierCombo, setErrorModifierCombo] = React.useState({
        GroupsListPage: {
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: false
        },
        GroupDetailsPage: {
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: false
        }
    });

    // ---------- State: Product Combo Child Details - Add to Combo Error ----------
    const [addToProductComboError, setAddToProductComboError] = React.useState({
        HasError: false,
        ErrorMessage: Messages.ErrorGeneric,
        ButtonsDisabled: false
    });

    // ---------- useEffect - Menu: Add Product to Cart ----------
    React.useEffect(()=> {
        if (productFormData.validation.PostToServer) {
            formatProductDataToPost();
        }
    }, [productFormData.validation.PostToServer]);

    React.useEffect(function() {
        if (productFormDataToPost !== "") {
            postProductToServerCart();
        }
    }, [productFormDataToPost]);

    // ---------- Function - Menu > Product Modal Click: Close Product (Regular) Modal ----------
    function handleProductModalClose() {
        setProductModalDisplay((prevState) => {
            return {
                showProductModal: false,
                productType: "",
            }
        });
    }

    // ---------- Function - Menu: Handle Product (Regular) Form Mod Changes ----------
    function handleModFormChange(event) {
        setProductFormData((prevState) => {
        let showModSpecialInstr = false;
        let titleSpecialInstr, maxLengthSpecialInstr;
        let maxLengthProdSpecialInstr = prevState.config.ProductSpecialInstructions.ProductCharacterLength;
        const { name, value, type, checked } = event.target;
        const modsArray = prevState.data.Mods.map((item) => {
            // Checkbox
            let showString = item.showSpecialInstruction.toString();
            let showForRadio = event.target.getAttribute("data-showspecialinstruction");
            if (item.fieldName === name) {
                if (type === "checkbox") {
                    if (checked === true && showString === "true") {
                        showModSpecialInstr = true;
                        titleSpecialInstr = event.target.getAttribute("data-titlespecialinstruction");
                        maxLengthSpecialInstr = event.target.getAttribute("data-maxlengthspecialinstructions");
                    }
                } else {
                    // Radio
                    if (showForRadio === "true") {
                        showModSpecialInstr = true;
                        titleSpecialInstr = event.target.getAttribute("data-titlespecialinstruction");
                        maxLengthSpecialInstr = event.target.getAttribute("data-maxlengthspecialinstructions");
                    }
                }
                //let isCheckedThis = type === "checkbox" ? checked : null;
                let isCheckedThis = checked;
                return {[name]: type === "checkbox" ? checked : value, plu: event.target.getAttribute("data-modplu"), fieldName: name, modCatId: item.modCatId, priceInc: event.target.getAttribute("data-priceinc"), showSpecialInstruction: event.target.getAttribute("data-showspecialinstruction"), isChecked: isCheckedThis, 'titleSpecialInstr': event.target.getAttribute("data-titlespecialinstruction"), 'charSpecialInstr': event.target.getAttribute("data-maxlengthspecialinstructions") }
            } else {
                if (item.isChecked === true && showString === "true") {
                    showModSpecialInstr = true;
                    titleSpecialInstr = item.titleSpecialInstr;
                    maxLengthSpecialInstr = item.charSpecialInstr;
                }
                return item
            }
        })

        let specialInstructionsValue = !prevState.config.IsShownProduct && !showModSpecialInstr ? "" : prevState.data.SpecialInstructions;
        let validationSpecialCharLength = showModSpecialInstr ? maxLengthSpecialInstr : maxLengthProdSpecialInstr;

        return ({
                ...prevState,
                config: {
                    ...prevState.config,
                    SpecialInstructions: {
                        ...prevState.config.SpecialInstructions,
                        IsShownMod: showModSpecialInstr,
                        Title: titleSpecialInstr,
                        CharacterLength: maxLengthSpecialInstr
                    }
                },
                data: {
                    ...prevState.data,
                    SpecialInstructions: specialInstructionsValue,
                    Mods: modsArray
                },
                validation: {
                    ...prevState.validation,
                    SpecialInstructions: {
                        ...prevState.validation.SpecialInstructions,
                        Max: validationSpecialCharLength
                    }
                }
            })
        });
    }

    // ---------- Function - Menu > Product Modal: Handle Product Regular Form Change (Special Instructions) ----------
    function handleProductFormChange(event) {
        setProductFormData((prevState) => {
            const { name, value } = event.target;
            return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    [name]: value
                }
            })
        });
    }
    
    // ---------- Function - Menu > Product Modal: Handle Regular Product Form Add Quantity Change ----------
    function handleProductFormAddQuantity() {
        setProductFormData((prevState) => {
            if (prevState.data.Quantity < 30) {
                const prodQuantity = prevState.data.Quantity + 1;
                return ({
                    ...prevState,
                    data: {
                        ...prevState.data,
                        Quantity: prodQuantity
                    }
                })
            } else {
                return prevState
            }
        })
    }

    // ---------- Function - Menu > Product Modal: Handle Product Form Subtract Quantity Change ----------
    function handleProductFormSubtractQuantity() {
        setProductFormData((prevState) => {
          if (prevState.data.Quantity > 1) {
            const prodQuantity = prevState.data.Quantity - 1;
          return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    Quantity: prodQuantity
                }
            })
          } else {
            return prevState
          }
        })
    }

    // ---------- Function - Menu > Product (Regular) Modal: Modifier Combo Click ----------
    function handleModifierComboSelect(event) {
        const modifierComboDetails = productDetails.modCategories.find((item) => item.modCategoryId == event.currentTarget.getAttribute('data-modcombo-modcatid'));
        setModifierComboDetails({
            modifierComboDetails: modifierComboDetails,
            selectedGroupDetails: {},
            selectedGroupIndex: null
        })
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Modifier Combo Groups List",
            }
        })
    }

    // ---------- Function - Menu > Product (Regular) Modal: Modifier Combo Back to Product (Regular) Parent Details ----------
    function handleBackModifierComboSelect() {
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Parent Details"
            }
        });
        resetErrorsModComboGroupListModalPage();
    }

    // ---------- Function - Menu > Product (Regular) Modal: Modifier Combo Groups List > Reset Errors ----------
    function resetErrorsModComboGroupListModalPage() {
        setErrorModifierCombo((prevState) => {
            return (
                {
                    GroupsListPage: {
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric,
                        ButtonsDisabled: false
                    },
                    GroupDetailsPage: prevState.GroupDetailsPage
                }
            )
        });
        let modsParentArray = productFormData.data.Mods.map((modCombo) => { // Stores Modifier Combo, Radios and Checks
            if (modCombo.fieldName === `ModComboModCatId${modifierComboDetails.modifierComboDetails.modCategoryId}`) { // Modifier Combo selected
                let modComboGroupsArr = modCombo.modComboGroups.map((group) => { // Groups inside selected Modifier Combo
                    return {
                        ...group,
                        ValidationModComboGroup: {
                            ...group.ValidationModComboGroup,
                            HasError: false, // Reset error for each modifier combo group
                            ErrorMessage: Messages.ErrorGeneric
                        }
                    }
                });
                return {
                    ...modCombo,
                    modComboGroups: modComboGroupsArr
                }
            } else {
                return modCombo
            }
        });

        setProductFormData((prevState) => {
            return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    Mods: modsParentArray
                }
            })
        });
    }

    // ---------- Function - Menu > Product (Regular) Modal > Modifier Combo: Modifier Group Click ----------
    function handleModifierComboGroupSelect(event) {
        let groupIndex = event.currentTarget.getAttribute('data-modcombogroupindex');
        const selectedGroupDetails = modifierComboDetails.modifierComboDetails.modComboGroups[groupIndex];
        setModifierComboDetails((prevState) => {
            return (
                {
                    ...prevState,
                    selectedGroupDetails: selectedGroupDetails,
                    selectedGroupIndex: groupIndex
                }
            )
        })
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Modifier Combo Group Details",
            }
        })
        resetErrorsModComboGroupListModalPage();
    }

    // ---------- Function - Menu > Product (Regular) Modal > Modifier Combo > Group Details: Back to Group List Click ----------
    function handleBackModifierComboGroupSelect() {
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Modifier Combo Groups List",
            }
        })
    }

    // ---------- Function - Menu > Product (Regular) Modal > Modifier Combo > Group Details: Handle Modifier change ----------
    function handleModifierChangeModComboGroup(event) {
        const { value } = event.target;
    
        let modsParentArray = productFormData.data.Mods.map((modCombo) => { // Stores Modifier Combo, Radios and Checks
            if (modCombo.fieldName == `ModComboModCatId${modifierComboDetails.modifierComboDetails.modCategoryId}`) {
                let modComboGroupsArr = modCombo.modComboGroups.map((group) => { // Stores Modifier Groups in Modifier Combo
                    if (group.ModComboGroupIndex == modifierComboDetails.selectedGroupIndex) {
                        let modifierNameText, modPriceInc = "";
                        let modsArr = group.ModsInGroup.map((mod) => { // Stores Modifiers in Groups
                            if (mod.ModId == value) {
                                modifierNameText = mod.ModifierName;
                                modPriceInc = mod.PriceInc
                                return {
                                    ...mod,
                                    IsChecked: true, 
                                    // /* --- Sets modifier radio to true and others to false for group inside modifier combo --- 
                                    // Note - Group will not be set to selected until Quantity is set from 0 to 1 by hitting 'Add to Combo'
                                    // This can be cleared by the user in the parent Modifier Combo button */
                                }
                            } else {
                                return {
                                    ...mod,
                                    IsChecked: false
                                }
                            }
                        })

                        // Populate Selected Modifier for selected Group in Modifier Combo
                        return {
                            ...group,
                            ModifierID: value,
                            ModifierPLU: event.target.getAttribute("data-modplu"),
                            ModifierName: modifierNameText,
                            PriceInc: modPriceInc,
                            ModsInGroup: modsArr
                        }
                    } else {
                        return group
                    }
                })
                // Populate Groups inside Modifier Combo
                return {
                    ...modCombo,
                    modComboGroups: modComboGroupsArr
                }
            } else {
                return modCombo
            }
        })
        
        setProductFormData((prevState) => {
            return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    Mods: modsParentArray
                }
            })
        })
    }

    // ---------- Function - Menu > Product (Regular) Modal: Modifier Combo > Add Selection to Group (Group Details Page) ----------
    function handleModifierComboAddSelectionToGroup() {
        setErrorModifierCombo((prevState) => {
            return ({
                GroupsListPage: prevState.GroupsListPage,
                GroupDetailsPage: {
                    HasError: false,
                    ErrorMessage: Messages.ErrorGeneric,
                    ButtonsDisabled: true
                }
            })
        });
        let modComboData = productFormData.data.Mods.find((modCombo) => {
            return modCombo.fieldName === `ModComboModCatId${modifierComboDetails.modifierComboDetails.modCategoryId}`
        });
        let groupSelectionData = modComboData.modComboGroups[modifierComboDetails.selectedGroupIndex];
        if (groupSelectionData.ModifierID === '' || groupSelectionData.ModifierID === null || groupSelectionData.ModifierID === '' || groupSelectionData.ModifierID === undefined) {
            setErrorModifierCombo((prevState) => {
                return (
                    {
                        GroupsListPage: prevState.GroupsListPage,
                        GroupDetailsPage: {
                            HasError: true,
                            ErrorMessage: Messages.ErrorProductForm,
                            ButtonsDisabled: false
                        }
                    }
                )
            });
        } else {
            setErrorModifierCombo((prevState) => {
                return (
                    {
                        GroupsListPage: prevState.GroupsListPage,
                        GroupDetailsPage: {
                            ...prevState.GroupDetailsPage,
                            ButtonsDisabled: false
                        }
                    }
                )
            });

            // Update product form data Quantity to 1 for the group inside the modifier combo
            let modsParentArray = productFormData.data.Mods.map((modCombo) => { // Stores Modifier Combo, Radios and Checks
                if (modCombo.fieldName === `ModComboModCatId${modifierComboDetails.modifierComboDetails.modCategoryId}`) { // Selected Modifier Combo
                    let modComboGroupsArr = modCombo.modComboGroups.map((group) => { // Stores Modifier Groups in Modifier Combo
                        if (group.ModComboGroupIndex == modifierComboDetails.selectedGroupIndex) {
                            return {
                                ...group,
                                Quantity: 1 // Selected
                            }
                        } else {
                            return group
                        }
                    });
                    // Into selected mod combo
                    return {
                        ...modCombo,
                        modComboGroups: modComboGroupsArr
                    }
                } else {
                    return modCombo
                }
            });
            setProductFormData((prevState) => {
                return ({
                    ...prevState,
                    data: {
                        ...prevState.data,
                        Mods: modsParentArray
                    }
                })
            });
            setProductModalDisplay((prevState) => {
                return (
                    {
                        ...prevState,
                        showModalPage: "Modifier Combo Groups List"
                    }
                )
            })
            
        }
    }

    // ---------- Function - Menu > Product (Regular) Modal: Modifier Combo > Add Combo to Product (Groups List Page) ----------
    function handleModComboAddComboToProduct() {
        setErrorModifierCombo((prevState) => {
            return (
                {
                    GroupsListPage: {
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric,
                        ButtonsDisabled: true
                    },
                    GroupDetailsPage: prevState.GroupDetailsPage,
                }
            )
        });

        // Validate Modifier Combo Groups
        let hasErrorAllFields = false;
        let allFieldsErrorMessage = Messages.ErrorGeneric;
        let modsParentArray = productFormData.data.Mods.map((modCombo) => { // Stores Modifier Combo, Radios and Checks
            if (modCombo.fieldName === `ModComboModCatId${modifierComboDetails.modifierComboDetails.modCategoryId}`) { // Modifier Combo selected
                let modComboGroupsArr = modCombo.modComboGroups.map((group) => { // Groups inside selected Modifier Combo
                    let validationGroupObj ={};
                    let quantityNum = parseInt(group.Quantity);
                    if (quantityNum < group.ValidationModComboGroup.Min || quantityNum > group.ValidationModComboGroup.Max) { // Modifier Combo Groups can only contain Radios
                        hasErrorAllFields = true;
                        allFieldsErrorMessage = Messages.ErrorProductForm;
                        validationGroupObj = {
                            ...group.ValidationModComboGroup,
                            ErrorMessage: Messages.ErrorSelectAProduct,
                            HasError: true
                        }
                    } else {
                        validationGroupObj = {
                            ...group.ValidationModComboGroup,
                            ErrorMessage: Messages.ErrorGeneric,
                            HasError: false
                        }
                    }
                    return {
                        ...group,
                        ValidationModComboGroup: validationGroupObj
                    }
                })
                return {
                    ...modCombo,
                    modComboGroups: modComboGroupsArr
                }
            } else {
                return modCombo
            }
        });
        
        setProductFormData((prevState) => {
            return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    Mods: modsParentArray
                }
            })
        });

        setErrorModifierCombo((prevState) => {
            return (
                {
                    GroupsListPage: {
                        HasError: hasErrorAllFields,
                        ErrorMessage: allFieldsErrorMessage,
                        ButtonsDisabled: false
                    },
                    GroupDetailsPage: prevState.GroupDetailsPage,
                }
            )
        });

        if (hasErrorAllFields) {
            return
        } else {
            // Update product form data for Modifier Combo Selection with group details
            let modsParentArray = productFormData.data.Mods.map((modCombo) => {  // Stores Modifier Combo, Radios and Checks
                if (modCombo.fieldName === `ModComboModCatId${modifierComboDetails.modifierComboDetails.modCategoryId}`) { // Selected Modifier Combo
                    return {
                        ...modCombo,
                        [`ModComboModCatId${modifierComboDetails.modifierComboDetails.modCategoryId}`]: true
                    }
                } else {
                    return modCombo
                }
            })
            setProductFormData((prevState) => {
                return ({
                    ...prevState,
                    data: {
                        ...prevState.data,
                        Mods: modsParentArray
                    }
                })
            })

            // Update product display modal
            setProductModalDisplay((prevState) => {
                return (
                    {
                        ...prevState,
                        showModalPage: "Parent Details"
                    }
                )
            })
        }
    }

    // ---------- Function - Menu > Product (Regular) Modal: Modifier Combo > Remove Modifier Combo selection from Product (Product Details Page) ----------
    function handleRemoveModifierComboFromProduct(event) {

        // Clear Modifier Combo Selection and revert modifiers groups back to their default selections
        let modsParentArray = productFormData.data.Mods.map((modCombo) => { // Stores Modifier Combo, Radios and Checks
            if (modCombo.fieldName == `ModComboModCatId${event.currentTarget.getAttribute('data-modcombo-modcatid')}`) { // Modifier Combo chosen to be removed from product
                let modComboGroupsArr = modCombo.modComboGroups.map((group) => { // Stores Modifier Groups in Modifier Combo
                    let defaultModId, defaultModPlu, defaultModName, defaultModPrice;
                    let modsArr = group.ModsInGroup.map((mod) => { // Stores Modifiers in Groups
                        if (mod.IsDefaultMod) { 
                            // Find default modifier
                            defaultModId = mod.ModId;
                            defaultModName = mod.ModifierName;
                            defaultModPlu = mod.ModPLU;
                            defaultModPrice = mod.PriceInc;
                            return {
                                ...mod,
                                IsChecked: true
                            }
                        } else {
                            return {
                                ...mod,
                                IsChecked: false
                            }
                        }
                    })
                    return {
                        ...group,
                        ModifierID: defaultModId,
                        ModifierName: defaultModName,
                        ModifierPLU: defaultModPlu,
                        PriceInc: defaultModPrice,
                        Quantity: 0,
                        ModsInGroup: modsArr
                    }
                })
                return {
                    ...modCombo,
                    [`ModComboModCatId${event.currentTarget.getAttribute('data-modcombo-modcatid')}`]: false,
                    modComboGroups: modComboGroupsArr
                }
            } else {
                return modCombo
            }
        });

        setProductFormData((prevState) => {
            return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    Mods: modsParentArray
                }
            })
        })
    }

    // ---------- Function - Menu > Product Combo Modal: Select Group to show Product List ----------
    function handleProductComboGroupSelect(event) {
        const productComboGroupId = productDetails.comboGroups.find((item) => item.productComboGroupID == event.currentTarget.getAttribute('data-productcombogroupid'));
        let productComboProductList = productComboGroupId.products;

        setProductComboChildDetails((prevState) => {
            return {
                ...prevState,
                productList: {productComboProductList}
            }
        });
        setProductComboChildFormData((prevState) => {
            return {
                data: {
                    ...prevState.data,
                    ProductComboGroupID: productComboGroupId.productComboGroupID,
                    ProductComboGroupName: productComboGroupId.productComboGroupName,
                },
                validation: {
                    ...prevState.validation
                }
            }
        });
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Product Combo List"
            }
        });
        setAddToProductComboError({
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: false
        });
        setProductFormData((prevState) => {
            // Reset product combo group errors
            let resetProductGroupsErrors = productFormData.validation.ProductComboGroups.map((item) => {
                return ({
                    ProductComboGroupID: item.ProductComboGroupID,
                    Min: item.Min,
                    Max: item.Max,
                    HasError: false,
                    ErrorMessage: item.ErrorMessage
                })
            })

            return ({
                ...prevState,
                validation: {
                    ...prevState.validation,
                    ProductComboGroups: resetProductGroupsErrors,
                    HasAllErrorFields: false
                }
            })
        });
        setAddToCartError({
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: false
        });
    }

    // ---------- Function - Menu > Product Combo Modal: Back from Select Product Group List to Product Parent ----------
    function handleBackProductComboGroupSelect() {
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Parent Details",
            }
        })
    }

    // ---------- Function - Menu > Product Combo Modal: Select Product in Product List to show Product Child Details (Combo) ----------
    function handleSelectProductInComboList(event) {
        let selectedProductInGroup = productComboChildDetails.productList.productComboProductList.find((item) => item.productId == event.currentTarget.getAttribute('data-productid'));
        setProductComboChildDetails((prevState) => {
            return {
                ...prevState,
                selectedProductDetails: {selectedProductInGroup}
            }
        });
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Product Combo Child Details",
                productType: "Combo",
            }
        })
        // --- Product Combo Form Data - Remapping ---
        let modCategoryArray = selectedProductInGroup.modCategories.map((item) => {
            return { 'ModCatId': item.modCategoryId, 'Type': item.type, 'Min': item.minValue, 'Max': item.maxValue, 'HasError': false, 'ErrorMessage': 'An error occurred. Please try again.' }
        });
        let mods = selectedProductInGroup.modCategories.map((item) => {
            const modCatId = item.modCategoryId;
            if (item.type === "Check") {
                    const checkModsArray = item.mods.map((item) => {
                        const checkName = `CheckModCatId${modCatId}ModId${item.modId}`
                        return { 'name': checkName, 'value': item.isDefaultMod, 'plu': item.modPLUNumber, 'modCatId': modCatId, 'modName': item.modName, 'priceInc': item.priceInc }
                    });
                return (checkModsArray);
            } else if (item.type === "Radio") {
                let radioIdSelected, pluSelected, modNameSelected, priceIncSelected = "";
                item.mods.forEach((innerItem) => {
                    if (innerItem.isDefaultMod) {
                            radioIdSelected = innerItem.modId;
                            pluSelected = innerItem.modPLUNumber;
                            modNameSelected = innerItem.modName;
                            priceIncSelected = innerItem.priceInc;
                    }
                });
                const radioName = `RadioModCatId${item.modCategoryId}`;
                return { 'name': radioName, 'value' : radioIdSelected, 'plu': pluSelected, 'modCatId': item.modCategoryId, 'modName': modNameSelected, 'priceInc': priceIncSelected }
            } else if (item.type === "Substitution") {
                // Substitution
                let subRadioIdSelected, pluSelected, modNameSelected, priceIncSelected = "";
                item.mods.forEach((innerItem) => {
                    if (innerItem.isDefaultMod) {
                            subRadioIdSelected = innerItem.modId;
                            pluSelected = innerItem.modPLUNumber;
                            modNameSelected = innerItem.modName;
                            priceIncSelected = innerItem.priceInc;
                    }
                });
                const radioName = `SubRadioModCatId${item.modCategoryId}`;
                return { 'name': radioName, 'value' : subRadioIdSelected, 'plu': pluSelected, 'modCatId': item.modCategoryId, 'modName': modNameSelected, 'priceInc': priceIncSelected }
            }
        });

        const modsFlat = mods.flat();
        const modsArray = modsFlat.map((item) => {
        return ({ [item.name] : item.value, 'plu': item.plu, 'fieldName': item.name, 'modCatId': item.modCatId, 'modName': item.modName, 'priceInc': item.priceInc })
        });
        setProductComboChildFormData((prevState) => {
            return {
                data: {
                    ProductCategoryId: prevState.data.ProductCategoryId,
                    ProductComboGroupID: prevState.data.ProductComboGroupID,
                    ProductComboGroupName: prevState.data.ProductComboGroupName,
                    ProductID: selectedProductInGroup.productId,
                    ProductPLU: selectedProductInGroup.productPLUNumberInt,
                    ProductName: selectedProductInGroup.productName,
                    ProductsInCombo: null,
                    Quantity: 1,
                    PriceIncForComboSubstitution: selectedProductInGroup.priceInc, // Used to calculate the price difference for a selected product with a substitution category for a product group in a product combo
                    SpecialInstructions: "",
                    ItemUsageTypeID: 1, // ItemUsageTypeID = 1 (Regular), 2 = (Combo)
                    MemberId: customerDetailsFormData.formData.MemberID,
                    CardNumber: customerDetailsFormData.CardNumber,
                    Mods: modsArray
                },
                validation: {
                    PostToServer: false,
                    HasErrorAllFields: false,
                    ModCategories: modCategoryArray,
                    SpecialInstructions: {
                        Min: 0,
                        Max: 32,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    },
                    Quantity: {
                        Min: 1,
                        Max: 30,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    }
                }
            }
        })
    }

    // ---------- Function - Menu > Product Combo (Child) Modal: Back from Child to List ----------
    function handleBackProductComboChild() {
        setProductModalDisplay((prevState) => {
            return {
                ...prevState,
                showModalPage: "Product Combo List",
            }
        })
    }

    // ---------- Function - Menu > Product Combo (Child) Modal: Handle Product Combo (Child) Form Change (Special Instructions) ----------
    function handleProductComboChildFormChange(event) {
        setProductComboChildFormData((prevState) => {
            const { name, value } = event.target;
            return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    [name]: value
                },
                validation: {
                    ...prevState.validation
                }
            })
        });
    }

    // ---------- Function - Menu > Product Combo (Child) Modal > Modifier: Modifier Form Change (Radio & Checkboxes) ----------
    function handleProductComboModFormChange(event) {
        setProductComboChildFormData((prevState) => {
            const { name, value, type, checked } = event.target;
            const modsArray = prevState.data.Mods.map((item) => {
                if (item.fieldName === name) {
                    return {[name]: type === "checkbox" ? checked : value, plu: event.target.getAttribute("data-modplu"), fieldName: name, modCatId: item.modCatId, modName: type === "checkbox" ? item.modName : event.target.labels[0].innerHTML, priceInc: event.target.getAttribute("data-priceinc")}
                } else {
                    return item
                }
            })
            return ({
                ...prevState,
                data: {
                    ...prevState.data,
                    Mods: modsArray
                },
                validation: {
                    ...prevState.validation
                }
            })
        });
    }

    // ---------- Function - Menu > Product Combo (Child) Modal: Add to Combo ----------
    function handleAddToProductCombo() {
        setAddToProductComboError({
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: true
        });
        // Remap Modifiers / Modifier Categories for Validation (Product Combo) - Filter selected
        const modsArrayValidate = Object.entries(productComboChildFormData.data.Mods).map(([key, value]) => ({"Field": value.fieldName, "PLU": value.plu, "Value" : value[value.fieldName], "ModCatId": value.modCatId, "ModName": value.modName, "PriceInc": value.priceInc }));
        const filteredArrayValidate = modsArrayValidate.filter((item) => {
            return (item.Value !== false)
        });
        
        // Validation Setter & Logic for Product Modifiers & Fields (Product Combo)
        let hasErrorAllFields = false;
        let postToServer = false;
        let qtyError = false;
        let instrError = false;
        let qtyMsg = "";
        let instrMsg = "";
        
        // Mapping modifier categories
        const validatedModCat = productComboChildFormData.validation.ModCategories.map((modCat) => {
            let modCatHasError = false;
            let modCatErrorMessage = "";
            // Selected Modifiers in this Modifier Category
            const modsSelectedModCat = filteredArrayValidate.filter((selectedMod) => {
                return selectedMod.ModCatId == modCat.ModCatId
            });

            if (modsSelectedModCat.length < modCat.Min || modsSelectedModCat.length > modCat.Max) {
                modCatHasError = true;
                hasErrorAllFields = true;
                modCatErrorMessage = `Please select between ${modCat.Min} and ${modCat.Max}.`;
            }
            if (modCat.Type === 'Radio') {
                modsSelectedModCat.forEach((mod) => {
                    if (mod.Value === "" || mod.Value === null || mod.Value === undefined) {
                        modCatHasError = true;
                        hasErrorAllFields = true;
                        modCatErrorMessage = `Please select a modifier to continue.`;
                    }
                })
            }
            return({ ModCatId: modCat.ModCatId, Type: modCat.Type, Min: modCat.Min, Max: modCat.Max, HasError: modCatHasError, ErrorMessage: modCatErrorMessage });
        });

        // Special Instructions Validation
        if (productComboChildFormData.data.SpecialInstructions.length < productComboChildFormData.validation.SpecialInstructions.Min || productComboChildFormData.data.SpecialInstructions.length > productComboChildFormData.validation.SpecialInstructions.Max) {
            instrError = true;
            hasErrorAllFields = true;
            instrMsg = `Special Instructions must be a maximum of ${productComboChildFormData.validation.SpecialInstructions.Max} characters.`;
        } else {
            const runner = CommonUtilities.RegexPatterns.regexValidNameSpecialChar.test(productComboChildFormData.data.SpecialInstructions);
            if (runner) {
                instrError = true;
                hasErrorAllFields = true;
                instrMsg = Messages.ErrorSpecialCharName;
            }
        }

        //Quantity Validation
        if (productComboChildFormData.data.Quantity < productComboChildFormData.validation.Quantity.Min || productComboChildFormData.data.Quantity > productComboChildFormData.validation.Quantity.Max) {
            qtyError = true;
            hasErrorAllFields = true;
            qtyMsg = `Quantity must be between ${productComboChildFormData.validation.Quantity.Min} and ${productComboChildFormData.validation.Quantity.Max} (inclusive).`
        }

        // Check if any errors exist
        if (hasErrorAllFields) {
            postToServer = false;
            setAddToProductComboError({
                HasError: true,
                ErrorMessage: Messages.ErrorProductForm,
                ButtonsDisabled: false
            });
        } else {
                postToServer = true; // Add to Combo = true
                let previousProductsInCombo = productFormData.data.ProductsInCombo;
                let productsInComboUpdated = previousProductsInCombo.map((item) => {
                    if (productComboChildFormData.data.ProductComboGroupID === item.productComboGroupID) {
                        return {
                            ...item,
                            ProductID: productComboChildFormData.data.ProductID,
                            ProductPLU: productComboChildFormData.data.ProductPLU,
                            ProductName: productComboChildFormData.data.ProductName,
                            ProductsInCombo: null,
                            Mods: filteredArrayValidate,
                            ItemUsageTypeID: 1, // Item Usage ID: 1 = Regular, 2 = Combo
                            Quantity: 1,
                            PriceIncForComboSubstitution: productComboChildFormData.data.PriceIncForComboSubstitution, // Used to calculate the price difference for a selected product with a substitution category for a product group in a product combo
                            OriginalProductID: productComboChildFormData.data.ProductID, // Server to calculate price difference for substitution category
                            OriginalProductPLUNumber: productComboChildFormData.data.ProductPLU, // Server to calculate price difference for substitution category
                            CalculatePriceDifference: productComboChildFormData.data.CalculatePriceDifference,
                            SpecialInstructions: productComboChildFormData.data.SpecialInstructions
                        }
                    } else {
                        return item
                    }
                });

                setProductFormData((prevState) => {
                    return ({
                        ...prevState,
                        data: {
                            ...prevState.data,
                            ProductsInCombo: productsInComboUpdated
                        }
                    })
                });

                setProductModalDisplay((prevModal) => {
                    return {
                        ...prevModal,
                        showModalPage: "Parent Details"
                    }
                });
            //}
        }
        setProductComboChildFormData((prevState) => {
            return({
                ...prevState,
                data: {
                    ...prevState.data
                },
                validation: {
                    PostToServer: postToServer,
                    ModCategories: validatedModCat,
                    HasErrorAllFields: hasErrorAllFields,
                    Quantity: {
                        Max: prevState.validation.Quantity.Max,
                        Min: prevState.validation.Quantity.Min,
                        HasError: qtyError,
                        ErrorMessage: qtyMsg
                    },
                    SpecialInstructions: {
                        Max: prevState.validation.SpecialInstructions.Max,
                        Min: prevState.validation.SpecialInstructions.Min,
                        HasError: instrError,
                        ErrorMessage: instrMsg
                    }
                }
            })
        })
    }

    // ---------- Function - Menu > Product (Regular) Modal : Add to Cart ----------
    function handleAddProductToCart() {
        // Shared
        setAddToCartError({
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: true
        });
        if (productDetails.itemUsageTypeID === 1) { // === Product Type: Regular ===

            let modsArrayValidate, filteredArrayValidate; // Regular Product Variables

            // Remap Modifiers / Modifier Categories for Validation (Regular) - Filter selected
            modsArrayValidate = Object.entries(productFormData.data.Mods).map(([key, value]) => ({
                "Field" : value.fieldName,
                "PLU": value.plu,
                "Value" : value[value.fieldName],
                "ModCatId": value.modCatId }));
            filteredArrayValidate = modsArrayValidate.filter((item) => {
                return (item.Value !== false)
            });

            // Validation Setter & Logic for Product Modifiers & Fields - Regular
            setProductFormData((prevState) => {
                let hasErrorAllFields = false;
                let postToServer = false;
                let qtyError = false;
                let instrError = false;
                let qtyMsg = "";
                let instrMsg = "";

                const validatedModCat = productFormData.validation.ModCategories.map((modCat) => {
                    let modCatHasError = false;
                    let modCatErrorMessage = "";
                    // Selected Modifiers in this Modifier Category
                    const modsSelectedModCat = filteredArrayValidate.filter((selectedMod) => {
                        return selectedMod.ModCatId == modCat.ModCatId
                    });

                    if (modsSelectedModCat.length < modCat.Min || modsSelectedModCat.length > modCat.Max) {
                        modCatHasError = true;
                        hasErrorAllFields = true;
                        modCatErrorMessage = `Please select between ${modCat.Min} and ${modCat.Max}.`;
                    }
                    if (modCat.Type === 'Radio' || modCat.Type === 'Substitution') {
                        modsSelectedModCat.forEach((mod) => {
                            if (mod.Value === "" || mod.Value === null) {
                                modCatHasError = true;
                                hasErrorAllFields = true;
                                modCatErrorMessage = `Please select a modifier to continue.`;
                            }
                        })
                    }

                    return (
                        {
                            ...modCat,
                            HasError: modCatHasError,
                            ErrorMessage: modCatErrorMessage
                        }
                    )
                });

                // Special Instructions Validation
                if (productFormData.data.SpecialInstructions.length < productFormData.validation.SpecialInstructions.Min || productFormData.data.SpecialInstructions.length > productFormData.validation.SpecialInstructions.Max) {
                    instrError = true;
                    hasErrorAllFields = true;
                    instrMsg = `Must be a maximum of ${productFormData.validation.SpecialInstructions.Max} characters.`;
                } else {
                    const runner = CommonUtilities.RegexPatterns.regexValidNameSpecialChar.test(productFormData.data.SpecialInstructions);
                    if (runner) {
                        instrError = true;
                        hasErrorAllFields = true;
                        instrMsg = Messages.ErrorSpecialCharName;
                    }
                }

                // Quantity Validation
                if (productFormData.data.Quantity < productFormData.validation.Quantity.Min || productFormData.data.Quantity > productFormData.validation.Quantity.Max) {
                    qtyError = true;
                    hasErrorAllFields = true;
                    qtyMsg = `Quantity must be between ${productFormData.validation.Quantity.Min} and ${productFormData.validation.Quantity.Max} (inclusive).`
                }

                // Check if any errors exist
                if (hasErrorAllFields) {
                    postToServer = false;
                    setAddToCartError({
                        HasError: true,
                        ErrorMessage: Messages.ErrorProductForm,
                        ButtonsDisabled: false
                    });
                } else {
                    postToServer = true; // Triggers useEffect to call formatProductDataToPost()
                }
                return ({
                    ...prevState,
                    validation: {
                        PostToServer: postToServer,
                        ModCategories: validatedModCat,
                        ProductComboGroups: [],
                        HasErrorAllFields: hasErrorAllFields,
                        Quantity: {
                            Max: prevState.validation.Quantity.Max,
                            Min: prevState.validation.Quantity.Min,
                            HasError: qtyError,
                            ErrorMessage: qtyMsg
                        },
                        SpecialInstructions: {
                            Max: prevState.validation.SpecialInstructions.Max,
                            Min: prevState.validation.SpecialInstructions.Min,
                            HasError: instrError,
                            ErrorMessage: instrMsg
                        }
                    }
                });
            });

        } else if (productDetails.itemUsageTypeID === 2) { // === Product Type: Product Combo ===

            setProductFormData((prevState) => {
                let hasErrorAllFields = false;
                let postToServer = false;
                let qtyError = false;
                let qtyMsg = "";

                // Product Combo Group validation - Map through existing display validation data and return error if needed
                const validatedProductGroups = productFormData.validation.ProductComboGroups.map((group) => {
                    let productGroupHasError = false;
                    let productGroupErrorMessage = "";

                    // Selected Group in data Combo to validate against rules / selection
                    const groupSelectedProductCombo = productFormData.data.ProductsInCombo.filter((item) => {
                        return item.productComboGroupID == group.ProductComboGroupID
                    });

                    if (groupSelectedProductCombo[0].ProductID == '' || groupSelectedProductCombo[0].ProductID == null) {
                        productGroupHasError = true;
                        productGroupErrorMessage = Messages.ErrorSelectAProduct;
                        hasErrorAllFields = true;
                    }
                    return (
                        {
                            ...group,
                            HasError: productGroupHasError,
                            ErrorMessage: productGroupErrorMessage
                        }
                    )
                });

                // Quantity Validation
                if (productFormData.data.Quantity < productFormData.validation.Quantity.Min || productFormData.data.Quantity > productFormData.validation.Quantity.Max) {
                    qtyError = true;
                    hasErrorAllFields = true;
                    qtyMsg = `Quantity must be between ${productFormData.validation.Quantity.Min} and ${productFormData.validation.Quantity.Max} (inclusive).`
                }

                // Check if any errors exist
                if (hasErrorAllFields) {
                    postToServer = false;
                    setAddToCartError({
                        HasError: true,
                        ErrorMessage: Messages.ErrorProductForm,
                        ButtonsDisabled: false
                    });
                } else {
                    postToServer = true; // Triggers useEffect to run function formatProductDataToPost()
                }
                return ({
                    ...prevState,
                    validation: {
                        PostToServer: postToServer,
                        ModCategories: [],
                        ProductComboGroups: validatedProductGroups,
                        HasErrorAllFields: hasErrorAllFields,
                        Quantity: {
                            Max: prevState.validation.Quantity.Max,
                            Min: prevState.validation.Quantity.Min,
                            HasError: qtyError,
                            ErrorMessage: qtyMsg
                        },
                        SpecialInstructions: {}
                    }
                });
            });
        }
    }

    // ---------- Function - Menu > Product (Regular) Modal: Format Data to POST to Cart ----------
    function formatProductDataToPost () {
        // Triggered by useEffect when productFormData.validation.PostToServer == true
        if (productFormData.data.ItemUsageTypeID == 1) { // Item Usage Type ID: 1 = Regular, 2 = Combo
            // ===== Regular Product Remap Data / Format data to post to server =====

            // Format data to post to server
            const mods = productFormData.data.Mods;
            const modsArray = Object.entries(mods).map(([key, value]) => 
                (
                    {
                        "Field" : value.fieldName,
                        "PLU": value.plu, // If Mod Combo > Linked Mod PLU is stored here
                        "Value": value[value.fieldName],
                        "ModsInCombo": value.modComboGroups,
                        "ModComboLinkedModId": value.modComboLinkedModId,
                    }
                )
            );
            const filteredArray = modsArray.filter((item) => {
                return (item.Value !== false)
            });

            // Substitute Category Check & filter if required
            let subRadioId;
            let subRadioPlu;
            let isSubCatRadio = false;

            const excludeSubCatArray = filteredArray.filter((item) => {
                if (item.Field.startsWith("SubRadio")) {
                    subRadioId = parseInt(item.Value);
                    subRadioPlu = parseInt(item.PLU);
                    isSubCatRadio = true;
                }
                return (!(item.Field.startsWith("SubRadio")));
            });

            // Format filtered array
            const formattedModsArray = excludeSubCatArray.map((item) => {
                let modCatId, modId = "";
                let formatter, modsInComboArray, itemUsageTypeID;
                if (item.Field.startsWith("Check")) {
                    formatter = item.Field.split("CheckModCatId");
                    formatter = formatter[1];
                    formatter = formatter.split("ModId");
                    modCatId = formatter[0];
                    modId = formatter[1];
                    modsInComboArray = null;
                    itemUsageTypeID = 1;
                } else if (item.Field.startsWith("Radio")) {
                    formatter = item.Field.split("RadioModCatId");
                    modCatId = formatter[1];
                    modId = parseInt(item.Value);
                    modsInComboArray = null;
                    itemUsageTypeID = 1;
                } 
                else if (item.Field.startsWith("ModCombo")) {
                    formatter = item.Field.split("ModComboModCatId");
                    modCatId = formatter[1];
                    modId = parseInt(item.ModComboLinkedModId)
                    itemUsageTypeID = 2;
                    modsInComboArray = item.ModsInCombo.map((group) => {
                        // Format Modifiers in Combo
                        return ( 
                            {
                                Quantity: 1,
                                ModID: group.ModifierID,
                                ModPLU: group.ModifierPLU,
                                ItemUsageTypeID: 1,
                                ModsInCombo: [],
                                ModCategoryId: group.ModComboGroupCategoryID
                            }
                        )
                    })
                }   
                return ({
                    ModCategoryId: parseInt(modCatId),
                    ModID: parseInt(modId),
                    ModPLU: parseInt(item.PLU),
                    Quantity: 1,
                    ItemUsageTypeID: itemUsageTypeID,
                    ModsInCombo: modsInComboArray
                })
            });
            setProductFormDataToPost({ // Changes to this triggers useEffect that calls postProductToServerCart()
                DeviceUID: urlConfig.DeviceUID,
                MemberId: urlConfig.MemberId,
                CardNumber: customerDetailsFormData.formData.membercardnumberforcart,
                BrandCode: CommonUtilities.StaticFields.BrandCode,
                LocationNumber: parseInt(currentLocation.LocationID),
                DeviceType: "iOS",
                ProductPriceTierID: currentLocation.ProductPriceTierID,
                ModPriceTierID: currentLocation.ModPriceTierID,
                Products: [
                    {
                        ProductCategoryId: parseInt(productFormData.data.ProductCategoryId),
                        ProductID: isSubCatRadio ? parseInt(subRadioId) : parseInt(productFormData.data.ProductID),
                        ProductPLU: isSubCatRadio ? parseInt(subRadioPlu) : parseInt(productFormData.data.ProductPLU),
                        Quantity: parseInt(productFormData.data.Quantity),
                        SpecialInstructions: productFormData.data.SpecialInstructions,
                        ItemUsageTypeID: parseInt(productFormData.data.ItemUsageTypeID),
                        ProductsInCombo: productFormData.data.ProductsInCombo,
                        BulkPricingProductsID: parseInt(productFormData.data.BulkPricingProductsID),
                        Mods: formattedModsArray
                    }
                ]
            });

        } else if (productFormData.data.ItemUsageTypeID == 2) {
            // ===== Combo Product Remap Data for POST =====

            let productsInComboExcludeSubCat = productFormData.data.ProductsInCombo.map((productInCombo) => {

                // Substitute Category Check / Filter & Switch Main PLU (if required)
                let subRadioId;
                let subRadioPlu;
                let isSubCatRadio = false;

                let excludeSubCatArrayForProdInCombo = productInCombo.Mods.filter((item) => {
                    if (item.Field.startsWith("SubRadio")) {
                        subRadioId = parseInt(item.Value);
                        subRadioPlu = parseInt(item.PLU);
                        isSubCatRadio = true;
                    }
                    return (!(item.Field.startsWith("SubRadio")));
                });
                return  {
                            ProductCategoryId: productInCombo.ProductCategoryId,
                            productComboGroupID: productInCombo.productComboGroupID,
                            productComboGroupName: productInCombo.productComboGroupName,
                            ProductID: isSubCatRadio ? subRadioId : productInCombo.ProductID, // Switch Substitute Category Product ID (if required)
                            ProductPLU: isSubCatRadio ? subRadioPlu : productInCombo.ProductPLU, // Switch Substitute Category Product PLU (if required)
                            ProductName: productInCombo.ProductName,
                            Quantity: productInCombo.Quantity,
                            PriceIncForComboSubstitution: productInCombo.PriceIncForComboSubstitution,
                            OriginalProductID: productInCombo.ProductID, // Server to calculate price difference for substitution category
                            OriginalProductPLUNumber: productInCombo.ProductPLU, // Server to calculate price difference for substitution category
                            CalculatePriceDifference: isSubCatRadio,
                            SpecialInstructions: productInCombo.SpecialInstructions,
                            ItemUsageTypeID: productInCombo.ItemUsageTypeID,
                            ProductsInCombo: productInCombo.ProductsInCombo,
                            Mods: excludeSubCatArrayForProdInCombo
                        }
            });

            // Map Products In Combo (Groups and selected products for these groups - filtered array for substitution category)
            let productsInComboArray = Object.entries(productsInComboExcludeSubCat).map(([key, value]) => {
                let modsFormattedArray = [];
                if (value.Mods.length > 0) {
                    modsFormattedArray = Object.entries(value.Mods).map(([key, modValue]) => {

                        // Mapping Modifiers for Product selected in Product Group
                        let modCatId, modId = "";
                        let formatter;
                        if (modValue.Field.startsWith("Check")) {
                            formatter = modValue.Field.split("CheckModCatId");
                            formatter = formatter[1];
                            formatter = formatter.split("ModId");
                            modCatId = formatter[0];
                            modId = formatter[1];
                        } else if (modValue.Field.startsWith("Radio")) {
                            formatter = modValue.Field.split("RadioModCatId");
                            modCatId = formatter[1];
                            modId = parseInt(modValue.Value);
                        }
                        return ({
                                ModCategoryId: parseInt(modCatId),
                                ModID: parseInt(modId),
                                ModPLU: parseInt(modValue.PLU),
                                Quantity: 1,
                                ItemUsageTypeID: 1,
                                ModsInCombo: null
                            });
                    });
                }
                // Mapping Product Group
                return ({
                        ProductCategoryId: parseInt(value.ProductCategoryId),
                        productComboGroupID: parseInt(value.productComboGroupID),
                        ProductID: parseInt(value.ProductID),
                        ProductPLU: parseInt(value.ProductPLU),
                        Quantity: 1,
                        OriginalProductID: value.OriginalProductID, // Server to calculate price difference for substitution category
                        OriginalProductPLUNumber: value.OriginalProductPLUNumber, // Server to calculate price difference for substitution category
                        CalculatePriceDifference: value.CalculatePriceDifference,
                        SpecialInstructions: value.SpecialInstructions,
                        ItemUsageTypeID: 1,
                        ProductsInCombo: null,
                        Mods: modsFormattedArray
                    })
                }
            );

            setProductFormDataToPost({ // Changes to this triggers useEffect that calls postProductToServerCart()
                DeviceUID: urlConfig.DeviceUID,
                MemberId: urlConfig.MemberId,
                CardNumber: customerDetailsFormData.formData.membercardnumberforcart,
                BrandCode: CommonUtilities.StaticFields.BrandCode,
                LocationNumber: parseInt(currentLocation.LocationID),
                DeviceType: "iOS",
                ProductPriceTierID: currentLocation.ProductPriceTierID,
                ModPriceTierID: currentLocation.ModPriceTierID,
                Products: [
                    {
                        ProductCategoryId: parseInt(productFormData.data.ProductCategoryId),
                        ProductID: parseInt(productFormData.data.ProductID),
                        ProductPLU: parseInt(productFormData.data.ProductPLU),
                        Quantity: parseInt(productFormData.data.Quantity),
                        SpecialInstructions: "",
                        BulkPricingProductsID: parseInt(productFormData.data.BulkPricingProductsID),
                        Mods: null,
                        ItemUsageTypeID: 2,
                        ProductsInCombo: productsInComboArray
                    }
                ]
            });
        }
    }

    // ---------- Function - Menu > Product Modal: Post Product (Regular) to Cart ----------
    function postProductToServerCart () {
        setAddToCartError({
            HasError: false,
            ErrorMessage: Messages.ErrorGeneric,
            ButtonsDisabled: true
        });
        let fetchUrl;
        if (CommonUtilities.StaticFields.PriceType == 'bp8826') {
            // Bulk Pricing
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartForBulkPricing?menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        } else {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartV2?menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        }
        fetch(fetchUrl, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID
            },
            body: JSON.stringify(productFormDataToPost)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                let amountInCart = data.noOfProducts + data.noOfVoucherUsed;
                setAmountInCart(amountInCart);
                setProductModalDisplay((prevState) => {
                    return {
                        ...prevState,
                        showProductModal: false
                    }
                });
            } else {
                setAddToCartError({
                    HasError: true,
                    ErrorMessage: data.responseMessage,
                    ButtonsDisabled: false
                })
            }
        })
        .catch((error) => {
            setAddToCartError({
                HasError: true,
                ErrorMessage: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
        })
    }

    // ====================== end: Product Details Modal & Add to Cart ====================== 
    
    // ====================== begin: Cart - Main ====================== 
    
    // ---------- State: Cart ----------
    const [cartDetails, setCartDetails] = React.useState({
        cartIsLoading: false,
        cartDetails: {
            locationName: "",
            products: [],
            totalInc: 0,
            saleDiscountDetails: {}
        }
    });

    const [cartError, setCartError] = React.useState({
        HasError: false,
        Message: Messages.ErrorGeneric,
        ButtonsDisabled: false
    });
    const [updateCartBody, setUpdateCartBody] = React.useState([]);
    const [removeProdCartBody, setRemoveProdCartBody] = React.useState([]);
    
    // ---------- State: Order & Pay ----------
    const [orderPayBody, setOrderPayBody] = React.useState({
        PostCustomerDetails: false,
        data: {
            SaleOrderTypeId: urlConfig.SaleOrderTypeId, // 1 = Dine In, 3 = Delivery, 4 = Pickup (default)
            OrderNote: "",
            ChannelID: 8,
            FulfillmentDate: "",
            TableName: "",
            TableNumber: "",
            IsDoorDashDelivery: false,
            DoorDashDeliveryRequestDetail: {},
            IdempotencyKey: "", // Device UID
            CustomerDetails: {
                FirstName: "",
                LastName: "",
                AddressLine1: "",
                AddressLine2: "",
                Suburb: "",
                PostCode: "",
                Phone: "",
                Email: "",
                State: "",
                CardNumber: ""
            }
        },
        validation: {
            PostToServer: false,
            HasErrorAllFields: false,
            RestaurantInstructions: {
                Min: 0,
                Max: 300,
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric
            },
            Products: {
                Min: 1,
                HasError: false,
                ErrorMessage: Messages.ErrorGeneric
            }
        }
    });

    React.useEffect(() => {
        if (orderPayBody.PostCustomerDetails) {
            postCustomerDetails();
        }
    }, [orderPayBody.PostCustomerDetails])

    // ---------- useEffect - Cart: Update Product in Cart ----------
    React.useEffect(function(){
        if (updateCartBody != "") {
            putUpdateCart();
        }
    }, [updateCartBody]);

    // ----------useEffect -  Cart: Remove Product from Cart ----------
    React.useEffect(function() {
        if (removeProdCartBody != "") {
            deleteProdFromCart();
        }
    }, [removeProdCartBody]);

    // ---------- Function - Cart: Handle Back Click to Menu ----------
    function handleBackClickCart() {
        navigate(`${basePath.current}?s8view=menu&token=${urlConfig.DeviceUID}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${urlConfig.Ref}`);
        if (locationList.original.length < 1) {
            setShowLocModal(true);
        }
    }

    // ---------- Function - Cart: Get Cart Details ----------
    function getCartDetails() {
        setUrlConfig((prevState) => {
            return({
                ...prevState,
                ServerCalls: {
                    ...prevState.ServerCalls,
                    GetUserCart: false // Reset useEffect
                }
            })
        })
        setCartDetails((prevState) => { // Clear cart to show loading before products shown on cart page
            return ({
                ...prevState,
                cartIsLoading: true
            })
        });
        setCartError({
            HasError: false,
            Message: Messages.ErrorGeneric,
            ButtonsDisabled: false
        });
        setCurrentLocation((prevState) => {
            return({
                ...prevState,
                Validation: {
                    ...prevState.Validation,
                    CalendarHasError: false, //  Reset Calendar Error
                    CalendarErrorMessage: Messages.ErrorFieldRequired
                }
            })
        })
        setOrderPayBody((prevState) => {
            return({
                ...prevState,
                PostCustomerDetails: false,
                validation: {
                    ...prevState.validation,
                    PostToServer: false,
                    HasErrorAllFields: false,
                    RestaurantInstructions: {
                        ...prevState.validation.RestaurantInstructions,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    },
                    Products: {
                        ...prevState.validation.Products,
                        HasError: false,
                        ErrorMessage: Messages.ErrorGeneric
                    }
                }
            })
        });

        if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup' || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === null || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'null') {
            let locationId;
            if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup') {
                locationId = currentLocation.LocationID;
            } else if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === null || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'null') {
                if (urlConfig.Type === '' || urlConfig.Type === null || urlConfig.Type === 'null' || urlConfig.Type == 'web') {
                    locationId = alertModal.PreviousLocationClearCart;
                } else {
                    locationId = currentLocation.LocationID;
                }
            }
            let fetchUrl;
            if (CommonUtilities.StaticFields.PriceType == 'bp8826') {
                //fetchUrl = `https://6551e9f4-3718-46b6-a339-28d07f3898ec.mock.pstmn.io/bulkpricingcart`; // Dummy Data for bulk cart pricing
                fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartForBulkPricing?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${locationId}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
            } else {
                fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${locationId}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
            }
            // Call GET for pickup / null
            fetch(`${fetchUrl}`, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                    "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                    "Content-Type": "application/json",
                    BrandUID: CommonUtilities.StaticFields.BrandUID,
                    DeviceUID: urlConfig.DeviceUID
                }
            })
            .then((response) => response.json())
            .then((data) => {
                if (data.isRequestSuccessful) {
                    if (data.cartDetails == "") {
                        setAmountInCart(0);
                        setLoyaltyMember((prevState) => {
                            return ({
                                ...prevState,
                                VoucherDetails: {
                                    ...prevState.VoucherDetails,
                                    VoucherIssueId: null
                                }
                            })
                        })
                    } else {
                        let amountOfProducts;
                        let loyaltyIssueId;
                        if (data.cartDetails.saleDiscountDetails != undefined && data.cartDetails.saleDiscountDetails != null) {
                            amountOfProducts = data.noOfProducts + 1;
                            loyaltyIssueId = data.cartDetails.saleDiscountDetails.loyaltyIssueID; 
                        } else {
                            loyaltyIssueId = null;
                            amountOfProducts = data.noOfProducts;
                        }
                        setAmountInCart(amountOfProducts);
                        setLoyaltyMember((prevState) => {
                            return ({
                                ...prevState,
                                VoucherDetails: {
                                    ...prevState.VoucherDetails,
                                    VoucherIssueId: loyaltyIssueId
                                }
                            })
                        })
                    }
                    setCartDetails((prevState) => {
                        return ({
                            ...prevState,
                            cartIsLoading: false,
                            cartDetails: data.cartDetails
                        })
                    });
                } else {
                    setCartError({
                        HasError: true,
                        Message: data.responseMessage,
                        ButtonsDisabled: false
                    });
                }
            })
            .catch((error) => {
                setCartError({
                    HasError: true,
                    Message: Messages.ErrorGeneric,
                    ButtonsDisabled: false
                });
            });
        } else if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'delivery') {
            if (currentLocation.LocationID !== '') {
                // Call PATCH for to GET User Cart details for delivery
                let fetchUrl;
                if (CommonUtilities.StaticFields.PriceType == 'bp8826')  {
                    fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartForBulkPricing?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
                } else {
                    fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
                }
                fetch(fetchUrl, {
                    method: "PATCH",
                    headers: {
                        Accept: "application/json",
                        "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                        "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                        "Content-Type": "application/json",
                        BrandUID: CommonUtilities.StaticFields.BrandUID,
                        DeviceUID: urlConfig.DeviceUID
                    },
                    body: JSON.stringify(pickupOrDelivery.DeliveryStatus.DoordashObject) 
                })
                .then((response) => response.json())
                .then((data) => {
                    if (data.isRequestSuccessful) {
                        if (data.cartDetails == "") {
                            setAmountInCart(0);
                        } else {
                            let amountOfProducts;
                            if (data.cartDetails.saleDiscountDetails != undefined) {
                                amountOfProducts = data.noOfProducts + 1;
                            } else {
                                amountOfProducts = data.noOfProducts;
                            }
                            setAmountInCart(amountOfProducts);
                        }
                        setCartDetails((prevState) => {
                            return ({
                                ...prevState,
                                cartIsLoading: false,
                                cartDetails: data.cartDetails
                            })
                        });
                    } else {
                        setCartError({
                            HasError: true,
                            Message: data.responseMessage,
                            ButtonsDisabled: false
                        });
                    }
                })
                .catch((error) => {
                    setCartError({
                        HasError: true,
                        Message: Messages.ErrorGeneric,
                        ButtonsDisabled: false
                    });
                });
            }
        } 
    }

    // ---------- Function - Cart: Update Pickup Time ----------
    function handlePickupTimeChange(event) {
        var pickupTime = event.currentTarget.value;
        setCurrentLocation((prevState) => {
            return {
                ...prevState,
                PickupTimeValue: pickupTime,
                Validation: {
                    ...prevState.Validation,
                    IsValidTableNumber: prevState.Validation.IsValidTableNumber
                }
            }
        })
    }

    // ---------- Function - Cart: Update Calendar ----------
    function handleCalendarChange(event) {
        let calendarValue = event == '' || event == null ? '' : event;
        let calendarValueForServer = event == '' || event == null ? '' : moment(event).format('YYYY-MM-DDTHH:mm:ss');
        let error = false;
        let errorMessage = Messages.ErrorFieldRequired;

        if (event !== '' && event !== null) {
            let selectedDay = event;
            let dayOfWeek = selectedDay.getDay();
            let minutes = selectedDay.getHours() * 60 + selectedDay.getMinutes(); // Selected Date/Time in Minutes
            if (dayOfWeek === 0) { // Sunday
                const hourMin = moment(currentLocation.SundayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.SundayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.SundayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.SundayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 1) { // Monday
                const hourMin = moment(currentLocation.MondayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.MondayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.MondayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.MondayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 2) { // Tuesday
                const hourMin = moment(currentLocation.TuesdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.TuesdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.TuesdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.TuesdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 3) { // Wednesday
                const hourMin = moment(currentLocation.WednesdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.WednesdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.WednesdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.WednesdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 4) { // Thursday
                const hourMin = moment(currentLocation.ThursdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.ThursdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.ThursdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.ThursdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 5) { // Friday
                const hourMin = moment(currentLocation.FridayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.FridayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.FridayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.FridayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 6) { // Saturday
                const hourMin = moment(currentLocation.SaturdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.SaturdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.SaturdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.SaturdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            }
        }

        if (error) {
            errorMessage = Messages.ErrorSelectTimeInStoreOpeningHours;
        }

        setCurrentLocation((prevState) => {
            return({
                ...prevState,
                CalendarValue: calendarValue,
                CalendarValueServer: calendarValueForServer,
                Validation: {
                    ...prevState.Validation,
                    CalendarHasError: error,
                    CalendarErrorMessage: errorMessage
                }
            })
        })
    }

    // ---------- Function - Cart: Update Product Quantity ----------
    function handleQuantityChange(event) {
        const updateBody = [{
            "ProductRecId": event.currentTarget.getAttribute('data-productrecid'),
            "Quantity": event.currentTarget.value
        }]
        setUpdateCartBody(updateBody);
    }

    function putUpdateCart() {
        setCartError({
            HasError: false,
            Message: Messages.ErrorGeneric,
            ButtonsDisabled: false
        });
        let fetchUrl;
        if (CommonUtilities.StaticFields.PriceType == 'bp8826') {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartForBulkPricing?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        } else {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        }
        fetch(fetchUrl, {
            method: "PUT",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            },
            body: JSON.stringify(updateCartBody)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setCartDetails((prevState) => {
                    return ({
                        ...prevState,
                        cartIsLoading: false,
                        cartDetails: data.cartDetails
                    })
                });
                let amountOfProducts;
                if (data.cartDetails.saleDiscountDetails != undefined) {
                    amountOfProducts = data.noOfProducts + 1;
                } else {
                    amountOfProducts = data.noOfProducts;
                }
                setAmountInCart(amountOfProducts);
            } else {
                setCartError({
                    HasError: true,
                    Message: data.responseMessage,
                    ButtonsDisabled: false
                });
            }
        })
        .catch((error) => {
            setCartError({
                HasError: true,
                Message: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
        })
    }

    // ---------- Function - Cart: Delete Product from Cart ----------
    function handleRemoveProdFromCart(event) {
        const deleteBody = [{
            "ProductRecId": event.currentTarget.getAttribute('data-productrecid')
        }]
        setRemoveProdCartBody(deleteBody);
    }

    function deleteProdFromCart () {
        setCartDetails((prevState) => { // Clear cart to show loading before products shown on cart page
            return ({
                ...prevState,
                cartIsLoading: true                
            })
        });
        setCartError({
            HasError: false,
            Message: Messages.ErrorGeneric,
            ButtonsDisabled: false
        });
        let fetchUrl;
        if (CommonUtilities.StaticFields.PriceType == 'bp8826') {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartForBulkPricing?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&Type=IDS&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        } else {
            fetchUrl = `${CommonUtilities.StaticFields.APIDomain}UserCartV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&Type=IDS&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`;
        }
        fetch(fetchUrl, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            },
            body: JSON.stringify(removeProdCartBody)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                getCartDetails();
            } else {
                setCartError({
                    HasError: true,
                    Message: data.responseMessage,
                    ButtonsDisabled: false
                });
            }
        })
        .catch((error) => {
            setCartError({
                HasError: true,
                Message: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
        })
    }

    // ---------- Function - Cart: Remove Voucher from Cart ----------
    function handleRemoveVoucherFromCart(e) {
        let addedDiscountId = e.currentTarget.getAttribute('data-addeddiscountid');
        let objToPost = {
            CartUserTempAddedDiscountsID: addedDiscountId
        }
        fetch(`${CommonUtilities.StaticFields.APIDomain}MemberRewardAppRedemableV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.LoyaltyMemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            },
            body: JSON.stringify(objToPost)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                getCartDetails();
            } else {
                setCartError({
                    HasError: true,
                    Message: data.responseMessage,
                    ButtonsDisabled: false
                });
            }
        })
        .catch((error) => {
            setCartError({
                HasError: true,
                Message: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
        })
    }

    // ---------- Function - Cart: Order & Pay - Notes On Change ----------
    function handleCartNotes(event) {
        const { value } = event.target;
        setOrderPayBody((prevState) => {
            return({
                ...prevState,
                PostCustomerDetails: false,
                data: {
                    ...prevState.data,
                    OrderNote: value
                }
            })
        })
    }

    // ---------- Function - Skip Fulfillment Time (Catering) ----------
    function skipFulfillmentTimeCheck() {
        setCustomerDetailsObjToPost((prevState) => {
            return ({
                ...prevState,
                PostToServer: false // Reset useEffect trigger
            })
        });
        // Catering - Using Calendar
        let dateTimeToPost = currentLocation.CalendarValueServer + ':00';
        setOrderPayBody((prevState) => {
            return({
                ...prevState,
                PostCustomerDetails: true, // UseEffect > When orderPayBody.PostCustomerDetails = true, call postCustomerDetails()
                data: {
                    ...prevState.data,
                    FulfillmentDate: dateTimeToPost
                }
            })
        });
    }

    // ---------- Function - Cart: Validate Pick Up Time (Fulfillment Time from Server) ----------
    function handleValidateFulfillmentTime () {
        setCustomerDetailsFormData((prevState) => {
            return ({
                ...prevState,
                validation: {
                    ...prevState.validation,
                    HasErrorAllFields: false,
                    AlertMessage: Messages.ErrorGeneric,
                    ButtonsDisabled: true
                }
            })
        });
        fetch(`${CommonUtilities.StaticFields.APIDomain}FulfillmentTimeV2?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&TimeInMin=${currentLocation.PickupTimeValue}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            }
        })
        .then((response) => response.json())
        .then((dataResponse) => {
            if (dataResponse.isRequestSuccessful) {
                // Check if Calendar or Pickup time should be sent
                let dateTimeToPost;
                if (pickupOrDelivery.Config.EnableDoorDashWhiteLabelDelivery) {
                    if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup') {
                        if (CommonUtilities.StaticFields.ShowCalendar) {
                            if (urlConfig.Type === '' || urlConfig.Type === null || urlConfig.Type === 'null' || urlConfig.Type == 'web') {
                                dateTimeToPost = currentLocation.CalendarValueServer + ':00';
                            } else {
                                dateTimeToPost = dataResponse.fulfillmentTime;
                            }
                        } else {
                            dateTimeToPost = dataResponse.fulfillmentTime;
                        }
                    }
                } else {
                    if (CommonUtilities.StaticFields.ShowCalendar) {
                        dateTimeToPost = currentLocation.CalendarValueServer + ':00';
                    } else {
                        dateTimeToPost = dataResponse.fulfillmentTime;
                    }
                }
                setOrderPayBody((prevState) => {
                    return({
                        ...prevState,
                        PostCustomerDetails: true, // UseEffect > When orderPayBody.PostCustomerDetails = true, call postCustomerDetails()
                        data: {
                            ...prevState.data,
                            FulfillmentDate: dateTimeToPost
                        }
                    })
                });
            } else {
                setCustomerDetailsFormData((prevState) => {
                    return ({
                        ...prevState,
                        validation: {
                            ...prevState.validation,
                            HasErrorAllFields: true,
                            AlertMessage: dataResponse.responseMessage,
                            ButtonsDisabled: false
                        }
                    })
                });
            }
        })
        .catch((error) => {
            setCustomerDetailsFormData((prevState) => {
                return ({
                    ...prevState,
                    validation: {
                        ...prevState.validation,
                        HasErrorAllFields: true,
                        AlertMessage: Messages.ErrorGeneric,
                        ButtonsDisabled: false
                    }
                })
            });
        });
    }

    

    // ---------- Function - Cart: Validate Cart > Show Customer Modal ----------
    function handleValidateCart() {
        setOrderPayBody((prevState) => {
            let hasErrorAllFields = false;
            let postToServer = false; // UseEffect
            let restaurantInfoError = false;
            let productError = false;
            let calendarError = false;
            let calErrorMessage = Messages.ErrorFieldRequired;
            let restaurantInfoMsg = "";
            let productMsg = "";
            let isDoordashDelivery, doordashFee, doordashCartObj;

            // Check pickup or delivery - Update Flag
            if (pickupOrDelivery.Config.EnableDoorDashWhiteLabelDelivery) {
                if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'delivery') {
                    isDoordashDelivery = true;
                    doordashCartObj = pickupOrDelivery.DeliveryStatus.DoordashObject;
                    doordashFee = pickupOrDelivery.DeliveryStatus.DoordashDeliveryFee;
                } else {
                    isDoordashDelivery = false;
                    doordashCartObj = {};
                    doordashFee = null;
                }
            } else {
                isDoordashDelivery = false;
                doordashCartObj = {};
                doordashFee = null;
            }

            // Validation Checks
            if (orderPayBody.data.OrderNote.length > orderPayBody.validation.RestaurantInstructions.Max) {
                hasErrorAllFields = true;
                restaurantInfoError = true;
                restaurantInfoMsg = `Kitchen Instructions must be a maximum of ${orderPayBody.validation.RestaurantInstructions.Max} characters.`;
            }
            if (amountInCart < orderPayBody.validation.Products.Min) {
                hasErrorAllFields = true;
                productError = true;
                productMsg = `Please select at least ${orderPayBody.validation.Products.Min} products.`;
            }

            // Calendar
            if (urlConfig.Type == '' || urlConfig.Type == null || urlConfig.Type == 'null' || urlConfig.Type == 'web') { // Web Order only
                if (pickupOrDelivery.Config.EnableDoorDashWhiteLabelDelivery) {
                    if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup') {
                        if (CommonUtilities.StaticFields.ShowCalendar) {
                            if (currentLocation.CalendarValue === "") {
                                hasErrorAllFields = true;
                                calendarError = true;
                            } else {
                                if (timeErrorNotStoreHoursCalendar()) {
                                    hasErrorAllFields = true;
                                    calendarError = true;
                                    calErrorMessage = Messages.ErrorSelectTimeInStoreOpeningHours;
                                }
                            }
                        }
                    }
                } else {
                    if (CommonUtilities.StaticFields.ShowCalendar) {
                        if (currentLocation.CalendarValue === "") {
                            hasErrorAllFields = true;
                            calendarError = true;
                        } else {
                            if (timeErrorNotStoreHoursCalendar()) {
                                hasErrorAllFields = true;
                                calendarError = true;
                                calErrorMessage = Messages.ErrorSelectTimeInStoreOpeningHours;
                            }
                        }
                    }
                }
            }

            // Calendar Error
            setCurrentLocation((prevState) => {
                return ({
                    ...prevState,
                    Validation: {
                        ...prevState.Validation,
                        CalendarHasError: calendarError,
                        CalendarErrorMessage: calErrorMessage
                    }
                })
            })

            if (hasErrorAllFields) {
                postToServer = false;
                setCartError({
                    HasError: true,
                    Message: Messages.ErrorProductForm,
                    ButtonsDisabled: false
                })
            } else {
                postToServer = true;
                setCartError({
                    HasError: false,
                    Message: Messages.ErrorGeneric,
                    ButtonsDisabled: false
                });
                setCustomerDetailsFormData((prevState) => {
                    return({
                        ...prevState,
                        formData: {
                            ...prevState.formData,
                            tandcs: false
                        },
                        validation: {
                            ...prevState.validation,
                            HasErrorAllFields: false,
                            ButtonsDisabled: false,
                            firstname: {
                                ...prevState.validation.firstname,
                                HasError: false
                            },
                            lastname: {
                                ...prevState.validation.lastname,
                                HasError: false
                            },
                            email: {
                                ...prevState.validation.email,
                                HasError: false
                            },
                            mobile: {
                                ...prevState.validation.mobile,
                                HasError: false
                            },
                            cardnumber: {
                                ...prevState.validation.cardnumber,
                                HasError: false
                            },
                            tandcs: {
                                ...prevState.validation.tandcs,
                                HasError: false
                            }
                        }
                    })
                });
                setShowCustomerDetailsModal({
                    IsShown: true
                })
            }

            // Return Order and Pay Body
            return ({
                PostCustomerDetails: false,
                data: {
                    ...prevState.data,
                    SaleOrderTypeId: urlConfig.SaleOrderTypeId,
                    TableName: urlConfig.TableNumber == "null" ? null : urlConfig.TableNumber,
                    TableNumber: urlConfig.TableNumber == "null" ? null : urlConfig.TableNumber,
                    IsDoorDashDelivery: isDoordashDelivery,
                    DoorDashDeliveryFeeInCents: doordashFee,
                    DoorDashDeliveryRequestDetail: doordashCartObj,
                    IdempotencyKey: urlConfig.DeviceUID,
                    CustomerDetails: {
                        ...prevState.data.CustomerDetails,
                    }
                },
                validation: {
                    ...prevState.validation,
                    PostToServer: postToServer,
                    HasErrorAllFields: hasErrorAllFields,
                    RestaurantInstructions: {
                        ...prevState.validation.RestaurantInstructions,
                        HasError: restaurantInfoError,
                        ErrorMessage: restaurantInfoMsg
                    },
                    Products: {
                        ...prevState.validation.Products,
                        HasError: productError,
                        ErrorMessage: productMsg
                    }
                }
            })
        })
    }

    function timeErrorNotStoreHoursCalendar () {
        let selectedDay = currentLocation.CalendarValue;
        let error = false;
        if (selectedDay !== '' && selectedDay !== null) {
            let dayOfWeek = selectedDay.getDay();
            let minutes = selectedDay.getHours() * 60 + selectedDay.getMinutes(); // Selected Date/Time in Minutes
            if (dayOfWeek === 0) { // Sunday
                const hourMin = moment(currentLocation.SundayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.SundayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.SundayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.SundayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 1) { // Monday
                const hourMin = moment(currentLocation.MondayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.MondayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.MondayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.MondayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 2) { // Tuesday
                const hourMin = moment(currentLocation.TuesdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.TuesdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.TuesdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.TuesdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 3) { // Wednesday
                const hourMin = moment(currentLocation.WednesdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.WednesdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.WednesdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.WednesdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 4) { // Thursday
                const hourMin = moment(currentLocation.ThursdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.ThursdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.ThursdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.ThursdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 5) { // Friday
                const hourMin = moment(currentLocation.FridayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.FridayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.FridayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.FridayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            } else if (dayOfWeek === 6) { // Saturday
                const hourMin = moment(currentLocation.SaturdayOpenTime, 'HH:mm').format('H');
                const minuteMin = moment(currentLocation.SaturdayOpenTime, 'HH:mm').format('m');
                const hourMax = moment(currentLocation.SaturdayCloseTime, 'HH:mm').format('H');
                const minuteMax = moment(currentLocation.SaturdayCloseTime, 'HH:mm').format('m');
                const start = (hourMin * 60) + Number(minuteMin);
                const end = hourMax * 60 + Number(minuteMax);
                if (minutes < start || minutes > end) {
                    error = true;
                }
            }
        }

        return error;
    }

    // ---------- Function - Cart: Order & Pay - Check if store is online (will work for catering) ----------
    function checkStoreIsOnlineForOrder() {
        setCartError({
            HasError: false,
            Message: Messages.ErrorGeneric,
            ButtonsDisabled: true
        });
        fetch(`${CommonUtilities.StaticFields.APIDomain}LocationList?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "GET",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                "BrowserSecUID": "CBD3A11B-D3A7-4695-B1A3-891678B8307D"
            }
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                if (pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'pickup' || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === null || pickupOrDelivery.SwitchMenuNav.SwitchPickupDeliveryValue === 'null') {
                    // Pickup
                    if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') {
                        // Catering
                        if (data.locationList.isAllowS8WebCateringOrder) {
                            putOrderAndPay();        
                        } else {
                            // Error - Catering
                            setCartError({
                                HasError: true,
                                Message: Messages.LocationClosed,
                                ButtonsDisabled: false
                            });
                            setCustomerDetailsFormData((prevState) => {
                                return({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        ButtonsDisabled: false
                                    }
                                })
                            })
                            return
                        }
                    } else {
                        // Not Catering - still pickup
                        if (data.locationList.isAllowOnlineOrder) {
                            putOrderAndPay();
                        } else {
                            // Error - Catering
                            setCartError({
                                HasError: true,
                                Message: Messages.LocationClosed,
                                ButtonsDisabled: false
                            });
                            setCustomerDetailsFormData((prevState) => {
                                return({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        ButtonsDisabled: false
                                    }
                                })
                            })
                            return
                        }
                    }
                } else {
                    // Delivery
                    checkDoordashIsOnlineForOrder();
                }
            } else {
                setCartError({
                    HasError: true,
                    Message: Messages.ErrorGeneric,
                    ButtonsDisabled: false
                });
                return
            }
        })
        .catch((error) => {
            setCartError({
                HasError: true,
                Message: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
        })
    }

    // ---------- Function - Cart: Check if Doordash is still online ----------
    function checkDoordashIsOnlineForOrder() {
        setCartError({
            HasError: false,
            Message: Messages.ErrorGeneric,
            ButtonsDisabled: true
        });
        let doordashEstimateObj = pickupOrDelivery.DeliveryStatus.DoordashObject;
        
        fetch(`${CommonUtilities.StaticFields.APIDomain}DoorDashEstimates?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${pickupOrDelivery.DeliveryStatus.LocationID}&MemberId=${urlConfig.MemberId !== null ? urlConfig.MemberId : 0}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID
            },
            body: JSON.stringify(doordashEstimateObj)
        })
        .then((response) => {
            return response.json()
        })
        .then((data) => {
            if (data.isRequestSuccessful) {
                putOrderAndPay();
            } else {
                setCartError({
                    HasError: true,
                    Message: Messages.ErrorGeneric,
                    ButtonsDisabled: false
                });
            }
        })
        .catch((error) => {
            setCartError({
                HasError: true,
                Message: Messages.ErrorGeneric,
                ButtonsDisabled: false
            });
        })
    }

    // ---------- Function - Cart: Order & Pay Function ----------
    function putOrderAndPay() {
        setCartError({
            HasError: false,
            Message: Messages.ErrorGeneric,
            ButtonsDisabled: true
        });
        // let customerMobile = `+61${(orderPayBody.data.CustomerDetails.Phone).slice(1)}`;
        // let businessPhone = `+61${(currentLocation.LocationPhoneNumber).slice(1)}`;
        
        // Create Doordash Cart Object
        let doordashCartObj = {
            ...pickupOrDelivery.DeliveryStatus.DoordashObject,
            customer: {
                first_name: orderPayBody.data.CustomerDetails.FirstName,
                last_name: orderPayBody.data.CustomerDetails.LastName,
                business_name: "",
                phone_number: orderPayBody.data.CustomerDetails.Phone,
                email: orderPayBody.data.CustomerDetails.Email,
                should_send_notifications: true,
                locale: "en-US"
            },
            pickup_phone_number: currentLocation.LocationPhoneNumber,
            dropoff_instructions: pickupOrDelivery.DeliveryAddressFields.deliveryinstructions.Value,
            allow_unattended_delivery: false
        }

        let orderAndPayBody = {
            ...orderPayBody.data,
            DoorDashDeliveryRequestDetail: doordashCartObj
        }
        fetch(`${CommonUtilities.StaticFields.APIDomain}UserCartLockV4?BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&MemberId=${urlConfig.MemberId}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}&priceType=${CommonUtilities.StaticFields.PriceType}&currentpage=${basePath.current.replace(/^\//, '')}`, {
            method: "PUT",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID,
                DeviceUID: urlConfig.DeviceUID,
                BrowserSecUID: 'CBD3A11B-D3A7-4695-B1A3-891678B8307D'
            },
            body: JSON.stringify(orderAndPayBody)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                setUrlConfig((prevState) => {
                    return ({
                        ...prevState,
                        Ref: data.cartSummaryID
                    })
                });
                // Prompt Post Terms through UseEffect
                setTermsObjToPost((prevState) => {
                    return({
                        PostToServer: true, // Triggers UseEffect that calls postTermsAndConditions() --> Handle Fulfillment Time
                        PostObject: {
                            ...prevState.PostObject,
                            CartSummaryID: data.cartSummaryID
                        }
                    })
                })
                if (data.skipPayment === true) {
                    // Skip Payment
                    getOrderStatusDetails();
                    getCartDetails();
                    setOrderStatusDetails((prevState) => {
                        return({
                            ...prevState,
                            IsFirstRun: true
                        })
                    });
                    navigate(`${basePath.current}?s8view=orderstatus&token=${urlConfig.DeviceUID}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${data.cartSummaryID}`);
                } else {
                    switch(data.cartLockMessage) {
                        case 1:
                            // Total Amount of cart is 0
                            setCustomerDetailsFormData((prevState) => {
                                return ({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        HasErrorAllFields: true,
                                        AlertMessage: data.responseMessage,
                                        ButtonsDisabled: false
                                    }
                                })
                            });
                            getOrderStatusDetails();
                            break
                        case 2:
                            // Amount is greater than 0 but less than MaxCartSkipPaymentS8App
                            setCustomerDetailsFormData((prevState) => {
                                return ({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        HasErrorAllFields: true,
                                        AlertMessage: data.responseMessage,
                                        ButtonsDisabled: false
                                    }
                                })
                            });
                            getOrderStatusDetails();
                            getCartDetails();
                            break
                        case 3:
                            // Cart Less than Stripe Mimimum
                            setCustomerDetailsFormData((prevState) => {
                                return ({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        HasErrorAllFields: true,
                                        AlertMessage: data.responseMessage,
                                        ButtonsDisabled: false
                                    }
                                })
                            });
                            break
                        case 4:
                            // Cart Value is less than Brand minimum
                            setCustomerDetailsFormData((prevState) => {
                                return ({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        HasErrorAllFields: true,
                                        AlertMessage: data.responseMessage,
                                        ButtonsDisabled: false
                                    }
                                })
                            });
                            break
                        case 5:
                            // Redirect to Payment Page
                            setOrderStatusDetails((prevState) => {
                                return({
                                    ...prevState,
                                    IsFirstRun: true
                                })
                            });
                            setCustomerDetailsFormData((prevState) => {
                                return ({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        HasErrorAllFields: false,
                                        AlertMessage: data.responseMessage,
                                        ButtonsDisabled: true
                                    }
                                })
                            });
                            if (data.appPaymentProvType === "TYRO") {

                                // *** Uncomment window.location.host when committing ***
                                //window.location.replace(`${CommonUtilities.StaticFields.Domain}PaymentPages/TyroPay.html?ps=${data.paySecret}&protocol=${window.location.protocol}&domain=${window.location.host}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&token=${urlConfig.DeviceUID}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${data.cartSummaryID}`);
                                //window.location.replace(`https://s8oostagingdeployment000fnuv.s8app.com/PaymentPages/TyroPay.html?ps=${data.paySecret}&protocol=${window.location.protocol}&domain=${window.location.host}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&token=${urlConfig.DeviceUID}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${data.cartSummaryID}`);

                                //window.location.replace(`https://${window.location.host}/PaymentPages/TyroPay.html?ps=${data.paySecret}&protocol=${window.location.protocol}&domain=${window.location.host}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&token=${urlConfig.DeviceUID}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref={data.cartSummaryID}`);

                                // === Injecting & Local ===
                                window.location.replace(`${CommonUtilities.StaticFields.AppDomain}/PaymentPages/TyroPay.html?ps=${data.paySecret}&protocol=${window.location.protocol}&domain=${window.location.host}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&token=${urlConfig.DeviceUID}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${data.cartSummaryID}&currentpage=${basePath.current.replace(/^\//, '')}`);
                            } else if (data.appPaymentProvType === "STRIPE") {
                                window.location.replace(data.stripeClientSecret);
                            } else if (data.appPaymentProvType === "FATZEBRA") {
                                window.location.replace(data.paymentURL);
                            }
                            else{
                                window.location.replace(data.paymentURL);
                            }
                            getCartDetails();
                            break
                        default:
                            setCustomerDetailsFormData((prevState) => {
                                return ({
                                    ...prevState,
                                    validation: {
                                        ...prevState.validation,
                                        HasErrorAllFields: true,
                                        AlertMessage: Messages.ErrorGeneric,
                                        ButtonsDisabled: false
                                    }
                                })
                            });
                            break
                    }
                }
            } else {
                setCustomerDetailsFormData((prevState) => {
                    return ({
                        formData: {
                            ...prevState.formData
                        },
                        validation: {
                            ...prevState.validation,
                            HasErrorAllFields: true,
                            AlertMessage: Messages.ErrorGeneric,
                            ButtonsDisabled: false
                        }
                    })
                });
            }
        })
        .catch((error) => {
            setCustomerDetailsFormData((prevState) => {
                return ({
                    ...prevState,
                    validation: {
                        ...prevState.validation,
                        HasErrorAllFields: true,
                        AlertMessage: Messages.ErrorGeneric,
                        ButtonsDisabled: false
                    }
                })
            });
        })
    }

    // ====================== end: Cart - Main ====================== 
    
    // ====================== begin: Customer Details Modal ====================== 
    
    // ---------- State: Customer Details ----------
    const [showCustomerDetailsModal, setShowCustomerDetailsModal] = React.useState({
        IsShown: false
    });

    const [customerDetailsFormData, setCustomerDetailsFormData] = React.useState({
        formData: {
            MemberID: "",
            TandCLink: "",
            firstname: "",
            lastname: "",
            email: "",
            mobile: "",
            cardnumber: "",
            membercardnumberforcart: "",
            tandcs: false,
        },
        validation: {
            PostToServer: false,
            HasErrorAllFields: false,
            ButtonsDisabled: false,
            AlertMessage: Messages.ErrorProductForm,
            firstname: {
                HasError: false,
                IsRequired: true,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            lastname: {
                HasError: false,
                IsRequired: false,
                ErrorMessage: Messages.ErrorGeneric
            },
            email: {
                HasError: false,
                IsRequired: false,
                ErrorMessage: Messages.ErrorGeneric
            },
            mobile: {
                HasError: false,
                IsRequired: true,
                ErrorMessage: Messages.ErrorFieldRequired
            },
            cardnumber: {
                HasError: false,
                IsRequired: false,
                ErrorMessage: Messages.ErrorGeneric
            },
            tandcs: {
                HasError: false,
                IsRequired: true,
                ErrorMessage: Messages.ErrorFieldRequired
            }
        }
    })

    // ---------- Function - Show Customer Details Modal ----------
    const handleShowCustomerDetailsModal = () => {
        setShowCustomerDetailsModal({
            IsShown: true
        });
    }
    // ---------- Function - Close Customer Details Modal ----------
    const handleCloseCustomerDetailsModal = () => {
        setShowCustomerDetailsModal({
            IsShown: false
        });
        setCartError((prevState) => {
            return({
                ...prevState,
                ButtonsDisabled: false
            })
        })
    }

    // ---------- State: Terms and Conditions Object to POST ----------
    const [termsObjToPost, setTermsObjToPost] = React.useState({
        PostToServer: false, // Triggers UseEffect that calls postTermsAndConditions()
        PostObject: {
            CartSummaryID: "",
            MemberID: "",
            AcceptedDate: "",
            GPSLatitude: "",
            GPSLongitude: ""
        }
    });
    
    // ---------- useEffect - Customer Details: Post Customer Details ----------
    // (Handle Fulfillment Time > Then Post Customer Details)
    React.useEffect(function(){
        if (customerDetailsObjToPost.PostToServer) {
            if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') {
                skipFulfillmentTimeCheck();
            } else {
                handleValidateFulfillmentTime();
            }
        }
    }, [customerDetailsObjToPost]);

    // ---------- useEffect - Customer Details: Post Terms and Conditions ----------
    React.useEffect(function() {
        if (termsObjToPost.PostToServer) {
            postTermsAndConditions();
        }
    }, [termsObjToPost.PostToServer])

    // ---------- Function - Customer Details Modal: On Change ----------
    function handleChangeCustDetails(e) {
        setCustomerDetailsFormData((prevState) => {
            const { name, value, type, checked } = e.target;
            const inputValue = type === 'checkbox' ? !prevState.formData[name] : value;
            return ({
                ...prevState,
                formData: {
                    ...prevState.formData,
                    [name]: inputValue
                }
            })
        })
    }

    // ---------- Function - Customer Details Modal: On Submit ----------
    function handleSubmitCustDetails() {
        let buttonDisabled;
        setCustomerDetailsFormData((prevState) => {
            return ({
                ...prevState,
                validation: {
                    ...prevState.validation,
                    ButtonsDisabled: true
                }
            })
        })
        let hasErrorAllFields = false;
        let postToServer = false;
        let firstNameError = false;
        let firstNameErrorMsg = "";
        let lastNameError = false;
        let lastNameErrorMsg = "";
        let emailError = false;
        let emailErrorMsg = "";
        let mobileError = false;
        let mobileErrorMsg = "";
        let tAndCsError = false;
        let tAndCsErrorMsg = "";

        // First Name required field as of 2025-02-05
        if (!customerDetailsFormData.formData.firstname) {
            firstNameError = true;
            firstNameErrorMsg = Messages.ErrorFieldRequired;
            hasErrorAllFields = true;
        }

        // Last Name required field as of 2025-12-12
        if (!customerDetailsFormData.formData.lastname) {
            lastNameError = true;
            lastNameErrorMsg = Messages.ErrorFieldRequired;
            hasErrorAllFields = true;
        }

        // Email required as of 2025-12-10
        if (customerDetailsFormData.formData.email === '' || customerDetailsFormData.formData.email === null || customerDetailsFormData.formData.email === ' ') {
            emailError = true;
            emailErrorMsg = Messages.ErrorFieldRequired;
            hasErrorAllFields = true;            
        } else {
            let trimmedEmail = (customerDetailsFormData.formData.email).trim();
            const runner = CommonUtilities.RegexPatterns.regexEmail.test(trimmedEmail);
            if (!runner) {
                // Invalid email
                emailError = true;
                emailErrorMsg = Messages.ErrorEmailInvalid;
                hasErrorAllFields = true;
            } 
        }
        
        // Mobile required if visible, otherwise is hidden - as of 2025-12-10
        if (urlConfig.Type === 'web' || urlConfig.Type === 'null' || urlConfig.Type === null || urlConfig.Type === '') { // If Web Ordering (is visible)
            if (!customerDetailsFormData.formData.mobile || customerDetailsFormData.formData.mobile === ' ') {
                 mobileError = true;
                 mobileErrorMsg = Messages.ErrorFieldRequired;
                 hasErrorAllFields = true;
            }
        }
        if (!customerDetailsFormData.formData.tandcs) {
            tAndCsError = true;
            tAndCsErrorMsg = Messages.ErrorTandCsRequired;
            hasErrorAllFields = true;
        }
        if (hasErrorAllFields) {
            postToServer = false;
            buttonDisabled = false;
        } else {
            postToServer = true;
            buttonDisabled = true;
        }
        setCustomerDetailsFormData((prevState) => {
            return ({
                ...prevState,
                validation: {
                    ...prevState.validation,
                    PostToServer: postToServer,
                    HasErrorAllFields: hasErrorAllFields,
                    AlertMessage: Messages.ErrorProductForm,
                    ButtonsDisabled: buttonDisabled,
                    firstname: {
                        ...prevState.validation.firstname,
                        HasError: firstNameError,
                        ErrorMessage: firstNameErrorMsg
                    },
                    lastname: {
                        ...prevState.validation.lastname,
                        HasError: lastNameError,
                        ErrorMessage: lastNameErrorMsg
                    },
                    email: {
                        ...prevState.validation.email,
                        HasError: emailError,
                        ErrorMessage: emailErrorMsg
                    },
                    mobile: {
                        ...prevState.validation.mobile,
                        HasError: mobileError,
                        ErrorMessage: mobileErrorMsg
                    },
                    tandcs: {
                        ...prevState.validation.tandcs,
                        HasError: tAndCsError,
                        ErrorMessage: tAndCsErrorMsg
                    }
                }
            })
        })

        if (postToServer) {
            setCustomerDetailsObjToPost((prevState) => {
                let isWard = urlConfig.WardInfo == 1;
                return ({
                    ...prevState,
                    PostToServer: true, // Use Effect - If true > trigger function handleValidateFulfillmentTime() 
                    PostObject: {
                        ...prevState.PostObject,
                        LocationNumber: currentLocation.LocationID,
                        TableNumber: urlConfig.TableNumber == "null" ? null : urlConfig.TableNumber,
                        BrandCode: CommonUtilities.StaticFields.BrandCode,
                        DeviceUID: urlConfig.DeviceUID ? urlConfig.DeviceUID : "",
                        FirstName: customerDetailsFormData.formData.firstname ? customerDetailsFormData.formData.firstname : " ",
                        LastName: customerDetailsFormData.formData.lastname ? customerDetailsFormData.formData.lastname : " ",
                        Email: customerDetailsFormData.formData.email ? customerDetailsFormData.formData.email : " ",
                        Mobile: customerDetailsFormData.formData.mobile ? customerDetailsFormData.formData.mobile : " ",
                        MemberCardNumber: customerDetailsFormData.formData.cardnumber ? customerDetailsFormData.formData.cardnumber : "",
                        Type: urlConfig.Type !== "" && urlConfig.Type !== null && urlConfig.Type !== "null" ? urlConfig.Type : "web",
                        IsWardInfo: isWard,
                        WardInfoPayload: wardDetails.WardDetailsPayload,
                        BrandMemberID: urlConfig.LoyaltyMemberId == null ? -1 : urlConfig.LoyaltyMemberId,
                        DoorDashEstimatePayload: JSON.stringify(pickupOrDelivery.DeliveryStatus.DoordashObject)
                    }
                })
            })
            setTermsObjToPost((prevState) => {
                let acceptedDate = moment(new Date()).format('YYYY-MM-DDTHH:mm:ss')
                return({
                    PostToServer: false, // Reset useEffect
                    PostObject: {
                        CartSummaryID: prevState.PostObject.CartSummaryID,
                        MemberID: urlConfig.MemberId,
                        AcceptedDate: acceptedDate,
                        GPSLatitude: geoLocation.GPSLatitude === '' ? 0 : geoLocation.GPSLatitude,
                        GPSLongitude: geoLocation.GPSLongitude === '' ? 0 : geoLocation.GPSLongitude
                    }
                })
            });
            setOrderPayBody((prevState) => {
                let updatedOrderNote;
                if (urlConfig.WardInfo == 1) {
                    updatedOrderNote = `${prevState.data.OrderNote} ${wardDetails.WardDetailsPayload}`; 
                } else {
                    updatedOrderNote = prevState.data.OrderNote;
                }
                return({
                    ...prevState,
                    PostCustomerDetails: false,
                    data: {
                        ...prevState.data,
                        OrderNote: updatedOrderNote,
                        CustomerDetails: {
                            ...prevState.data.CustomerDetails,
                            FirstName: customerDetailsFormData.formData.firstname ? customerDetailsFormData.formData.firstname : " ",
                            LastName: customerDetailsFormData.formData.lastname ? customerDetailsFormData.formData.lastname : " ",
                            Email: customerDetailsFormData.formData.email ? customerDetailsFormData.formData.email : " ",
                            Phone: customerDetailsFormData.formData.mobile ? customerDetailsFormData.formData.mobile : " ",
                            CardNumber: customerDetailsFormData.formData.cardnumber ? customerDetailsFormData.formData.cardnumber : "",
                        }
                    }
                })
            })
        }
    }

    // ---------- Function - Customer Details Modal: PUT ----------
    function postCustomerDetails() {
        setOrderPayBody((prevState) => {
            return({
                ...prevState,
                PostCustomerDetails: false // Reset useEffect trigger
            })
        });
        fetch(`${CommonUtilities.StaticFields.APIDomain}CustomerForWebOrder?DeviceUID=${urlConfig.DeviceUID}&BrandCode=${CommonUtilities.StaticFields.BrandCode}&LocationNumber=${currentLocation.LocationID}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "PUT",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID
            },
            body: JSON.stringify(customerDetailsObjToPost.PostObject)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                // Check if store is online - will work for catering
                checkStoreIsOnlineForOrder();
            } else {
                setCustomerDetailsFormData((prevState) => {
                    return ({
                        ...prevState,
                        validation: {
                            ...prevState.validation,
                            HasErrorAllFields: true,
                            AlertMessage: data.responseMessage,
                            ButtonsDisabled: false
                        }
                    })
                })
            }
        })
        .catch((error) => {
            setCustomerDetailsFormData((prevState) => {
                return ({
                    ...prevState,
                    validation: {
                        ...prevState.validation,
                        HasErrorAllFields: true,
                        AlertMessage: Messages.ErrorGeneric,
                        ButtonsDisabled: false
                    }
                })
            })
        })
    }
    
    // ---------- Function - Customer Details Modal > Terms and Conditions: POST ----------
    function postTermsAndConditions() {
        fetch(`${CommonUtilities.StaticFields.APIDomain}TermsAndCondition?BrandCode=${CommonUtilities.StaticFields.BrandCode}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                "Content-Type": "application/json",
                BrandUID: CommonUtilities.StaticFields.BrandUID
            },
            body: JSON.stringify(termsObjToPost.PostObject)
        })
        .then((response) => response.json())
        .then((data) => {
            if (data.isRequestSuccessful) {
                // Success - no display for this
            } else {
                // Fail - no display for this
            }
        })
        .catch((error) => {
            // Error - no display for this
        })
    }
    
    // ====================== end: Customer Details Modal ====================== 
    
    // ====================== begin: Order Status ====================== 
    
    // ---------- State: Order Status ----------
    const [orderStatusDetails, setOrderStatusDetails] = React.useState({
        OpenOrders: [],
        IsFirstRun: true
    });

    // ---------- Order Status - Get Status Interval (Every 15 seconds) ----------
    function startInterval(intervalFunction, intervalTime, pageCondition, timeoutDuration) {
        let intervalId;
        const stopInterval = () => {
            clearInterval(intervalId);
        }
        if (pageCondition) {
            intervalId = setInterval(intervalFunction, intervalTime);
            setTimeout(stopInterval, timeoutDuration);
            return intervalId;
        } else {
            return null;
        }
    }

    React.useEffect(function() {
        if (orderStatusDetails.IsFirstRun) {
            getOrderStatusDetails();
        }
        if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') {
            // Do not run interval for catering
            return
        }
        const intervalFunction = () => {
            getOrderStatusDetails();
        }
        const currentPath = locationPath.pathname.toLowerCase();
        const runTimer = currentPath == "/orderstatus" || searchParams.get('s8view') === 'orderstatus';
        const intervalTimer = startInterval(intervalFunction, 15000, runTimer, 45000); // Run every 15 seconds 3 times (45 seconds)
        return () => clearInterval(intervalTimer);
    }, [locationPath.pathname, locationPath.search])

    // ---------- Function - Order Status: Get Order Status Details ----------
    function getOrderStatusDetails() {
        if (CommonUtilities.StaticFields.PickupMenuType === 'app') { // Call Server - Pos needs to be online
            const memberParam = searchParams.get('memberid');
            if (memberParam !== '' && memberParam !== 'null' && memberParam !== null) {
                fetch(`${CommonUtilities.StaticFields.APIDomain}OpenOrders?BrandCode=${CommonUtilities.StaticFields.BrandCode}&MemberId=${memberParam}&menuType=${pickupOrDelivery.SwitchMenuNav.MenuTypeEndpointParam}`, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "S8Subscription-Key": CommonUtilities.StaticFields.OAuthToken,
                        "Access-Control-Allow-Origin": CommonUtilities.StaticFields.Domain,
                        "Content-Type": "application/json",
                        BrandUID: CommonUtilities.StaticFields.BrandUID,
                        DeviceUID: urlConfig.DeviceUID
                    }
                })
                .then((response) => response.json())
                .then((data) => {
                    if (data.isRequestSuccessful) {
                        setOrderStatusDetails({
                            OpenOrders: data.openOrders,
                            IsFirstRun: false
                        });
                    } else {
                        setErrorModal((prevState) => {
                            return ({
                                ...prevState,
                                Message: data.responseMessage,
                                IsShown: true,
                                IsError: true
                            })
                        });
                    }
                })
                .catch((error) => {
                    setErrorModal((prevState) => {
                        return ({
                            ...prevState,
                            Message: Messages.ErrorGeneric,
                            IsShown: true,
                            IsError: true
                        })
                    });
                })
            }
        } else if (CommonUtilities.StaticFields.PickupMenuType === 'webordering') { // Don't call server - (Catering) Pos does not need to be online
            let dateNow = moment().format('YYYY-MM-DDTHH:mm:ss');
            setOrderStatusDetails((prevState) => {
                return({
                    ...prevState,
                    OpenOrders: [
                        {
                            ...prevState.OpenOrders[0],
                            "cartSummaryRecId": urlConfig.Ref,
                            "memberId": urlConfig.MemberId,
                            "totalInc": null,
                            "totalEx": null,
                            "totalTax": null,
                            "paymentDate": dateNow,
                            "paymentStatus": "",
                            "orderStatus": "",
                            "orderStatusID": "",
                            "saleOrderTypeID": "",
                            "saleOrderTypeName": "",
                            "paymentReferenceNumber": ""
                        }
                    ],
                    IsFirstRun: false
                });
            })
        }
    }

    // ---------- Function - Order Status: Handle Back Click to Menu ----------
    function handleStartNewOrderClickOrderStatus() {
        let isWebOrder = urlConfig.Type === '' || urlConfig.Type === null || urlConfig.Type === 'null' || urlConfig.Type == 'web';
        setAmountInCart(0);
        setPickupOrDelivery((prevState) => {
            let switchValue;
            let menuTypeParam;
            if (isWebOrder) {
                switchValue = CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery ? null : 'pickup';
                menuTypeParam = CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery ? null : CommonUtilities.StaticFields.PickupMenuType;
            } else {
                switchValue = 'pickup';
                menuTypeParam = CommonUtilities.StaticFields.PickupMenuType;
            }
            let pickupDeliveryModalShown;
            if (isWebOrder) {
                // Web Ordering - Dynamic Location
                if (CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery) {
                    pickupDeliveryModalShown = true;
                } else {
                    pickupDeliveryModalShown = false;
                }
            } else {
                // Other Ordering (Kiosk, QR, Table, Static Web) - Static Location
                pickupDeliveryModalShown = false;
            }
            return({
                ...prevState,
                SwitchMenuNav: {
                    ...prevState.SwitchMenuNav,
                    SwitchPickupDeliveryValue: switchValue, // Reset Switch
                    MenuTypeEndpointParam: menuTypeParam // Reset
                },
                PickupDeliveryModal: {
                    ...prevState.PickupDeliveryModal,
                    ModalIsShown: pickupDeliveryModalShown,
                    ModalPageShown: 'Method' // Start order flow again
                },
                DeliveryAddressError: {
                    ...prevState.DeliveryAddressError,
                    HasError: false, // Reset error but display address if previously entered
                    ButtonsDisabled: false
                },
                DeliveryStatus: { // Reset delivery
                    ...prevState.DeliveryStatus,
                    EstimateResponseCode: 0,
                    LocationIndex: 0,
                    LocationID: 0,
                    EstimtePickupTime: null,
                    EstimateDeliveryTime: null,
                    //EstimateDeliveryMinutes: null,
                    DoordashDeliveryFee: null,
                    GetProductMenu: false,
                    DoordashObject: null
                }
            })
        });
        // Reset Loyalty Details
        setLoyaltyMember((prevState) => {
           return ({
                ...prevState,
                MenuNav: {
                    ...prevState.MenuNav,
                    LoyaltyMemberButtonValue: 'Guest',
                    IsLoggedIn: false,
                    CardNumber: ""
                },
                LoyaltyModal: {
                    ...prevState.LoyaltyModal,
                    ModalIsShown: false,
                    ModalPageShow: 'Login'
                },
                MobileModalPage: {
                    ...prevState.MobileModalPage,
                    Fields: {
                        ...prevState.MobileModalPage.Fields,
                        authmobile: {
                            ...prevState.MobileModalPage.Fields.authmobile,
                            Value: '',
                            HasError: false,
                        }
                    },
                    GeneralError: {
                        ...prevState.MobileModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                OtpModalPage: {
                    ...prevState.OtpModalPage,
                    Fields: {
                        ...prevState.OtpModalPage.Fields,
                        authotp: {
                            ...prevState.OtpModalPage.Fields.authotp,
                            Value: '',
                            HasError: false
                        }
                    },
                    GeneralError: {
                        ...prevState.OtpModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                VoucherModalPage: {
                    ...prevState.VoucherModalPage,
                    Vouchers: [],
                    GeneralError: {
                        ...prevState.VoucherModalPage.GeneralError,
                        HasError: false,
                        ButtonsDisabled: false
                    }
                },
                ServerCalls: {
                    ...prevState.ServerCalls,
                    SendOtpSms: false,
                    PostOtp: false,
                    GetVouchers: false,
                    GetLogout: false,
                    ResendOtpStartTimer: false,
                    ResendOtpCountdownSeconds: 15,
                    GetItemVoucherDetails: false,
                    GetSaleVoucherDetails: false
                }
            })
        })
        // Reset Customer Details
        setCustomerDetailsFormData((prevState) => {
            return ({
                ...prevState,
                formData: {
                    ...prevState.formData,
                    MemberID: "",
                    firstname: "",
                    lastname: "",
                    email: "",
                    mobile: "",
                    cardnumber: ""
                }
            })
        });
        if (isWebOrder) {
            // Web Ordering - Dynamic Location
            
            setProductsLoading(true); // Show loading skeleton for product
            setProductData([]); // Reset product menu
            setCurrentLocation((prevState) => ({ // Reset location so it is not pre-selected on new order
                ...prevState,
                LocationID: "",
                LocationName: "",
                LocationAddressLine1: "",
                LocationAddressLine2: "",
                LocationSuburb: "",
                LocationState: "",
                LocationPostcode: "",
                LocationPhoneNumber: "",
                LocationDistance: "",
                ProductPriceTierID: -999,
                ModPriceTierID: -999,
                AllowOnlineOrder: false,
                PickupTimeValue: 0,
                CalendarValue: "",
                CalendarValueServer: "",
                MondayOpenTime: "",
                MondayCloseTime: "",
                TuesdayOpenTime: "",
                TuesdayCloseTime: "",
                WednesdayOpenTime: "",
                WednesdayCloseTime: "",
                ThursdayOpenTime: "",
                ThursdayCloseTime: "",
                FridayOpenTime: "",
                FridayCloseTime: "",
                SaturdayOpenTime: "",
                SaturdayCloseTime: "",
                SundayOpenTime: "",
                SundayCloseTime: "",
                Validation: {
                    ...prevState.Validation,
                    IsValidTableNumber: true,
                    CalendarHasError: false,
                    CalendarErrorMessage: Messages.ErrorFieldRequired
                },
                CalendarConfig: {
                    ...prevState.CalendarConfig,
                    CalendarMinDate: "",
                    CalendarMaxDate: "",
                    CalendarMinDays: "",
                    CalendarMaxDays: ""
                }
            }));
            setUrlConfig((prevState) => { // Triggers useEffect [urlConfig]
                return({
                    ...prevState,
                    LocationQuery: '',
                    MemberId: null,
                    LoyaltyMemberId: null,
                    TableNumber: '',
                    DeviceUID: null,
                    Type: null,
                    MenuType: CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery ? null : 'pickup',
                    Ref: null
                })
            });
            if (CommonUtilities.StaticFields.EnableDoorDashWhiteLabelDelivery) { 
                // Delivery Enabled 
                setShowLocModal(false);
            } else { 
                // Pickup Only
                setShowLocModal(true);
            }
        } else {
            // Other Ordering (Kiosk, QR, Table, Static Web) - Static Location
            setUrlConfig((prevState) => { // Triggers useEffect [urlConfig]
                return({
                    ...prevState,
                    MemberId: null,
                    LoyaltyMemberId: null,
                    DeviceUID: null
                })
            });
            // Static Locations
            setShowLocModal(false);
        }

        // --- Both Web Order and Other Variations ---
        setAlertModal((prevState) => {
            return({
                ...prevState,
                ClearMemberDetails: true,
                IsInitialLocation: true,
                StartNewOrder: true // Trigger useEffect to navigate to Menu with reset url params
            })
        });
        
        // --- Reset Terms ---
        setTermsObjToPost((prevState) => {
            return({
                PostToServer: false, // Reset useEffect
                PostObject: {
                    ...prevState.PostObject
                }
            })
        });

        // --- Reset Order Status ---
        setOrderStatusDetails((prevState) => {
            return({
                ...prevState,
                OpenOrders: [
                    {
                        "cartSummaryRecId": "",
                        "memberId": "",
                        "totalInc": "",
                        "totalEx": "",
                        "totalTax": "",
                        "locationName": "",
                        "businessAddressLine1": "",
                        "businessAddressLine2": "",
                        "businessStateName": "",
                        "businessPostCode": "",
                        "businessPhone": "",
                        "gpsLatitude": "",
                        "gpsLongitude": "",
                        "locationNumber": "",
                        "paymentDate": "",
                        "paymentStatus": "",
                        "orderStatus": "",
                        "orderStatusID": "",
                        "saleOrderTypeID": "",
                        "saleOrderTypeName": "",
                        "paymentReferenceNumber": ""
                    }
                ],
                IsFirstRun: true
            })
        });

        // --- Clear view param and return to menu ---
        navigate(`${basePath.current}?s8view=menu&token=${urlConfig.DeviceUID}&loc=${urlConfig.LocationQuery}&memberid=${urlConfig.MemberId}&tablenum=${urlConfig.TableNumber}&type=${urlConfig.Type}&menutype=${urlConfig.MenuType}&wardinfo=${urlConfig.WardInfo}&ref=${urlConfig.Ref}`);
    }

    // ====================== end: Order Status ====================== 
    
    // ====================== begin: Routes ======================

    // --- Payment provider redirect: render view directly via ?s8view= query param ---
    // This avoids server-side 404s when payment providers redirect back to the injection page
    const viewParam = searchParams.get('s8view');

    if (viewParam === 'orderstatus') {
        return (
            <OrderStatusPage
                errorModal={errorModal}
                handleCloseErrorModal={handleCloseErrorModal}
                orderStatusDetails={orderStatusDetails}
                handleStartNewOrderClickOrderStatus={handleStartNewOrderClickOrderStatus}
                urlConfig={urlConfig}
            />
        );
    }

    if (viewParam === 'cart') {
        return (
            <CartPage
                errorModal={errorModal}
                handleCloseErrorModal={handleCloseErrorModal}
                offlineModal={offlineModal}
                handleCloseOfflineModal={handleCloseOfflineModal}
                cartDetails={cartDetails}
                cartError={cartError}
                currentLocation={currentLocation}
                orderPayBody={orderPayBody}
                handleBackClickCart={handleBackClickCart}
                handlePickupTimeChange={handlePickupTimeChange}
                handleCalendarChange={handleCalendarChange}
                handleQuantityChange={handleQuantityChange}
                handleRemoveProdFromCart={handleRemoveProdFromCart}
                handleRemoveVoucherFromCart={handleRemoveVoucherFromCart}
                handleCartNotes={handleCartNotes}
                handleValidateFulfillmentTime={handleValidateFulfillmentTime}
                handleValidateCart={handleValidateCart}
                urlConfig={urlConfig}
                showCustomerDetailsModal={showCustomerDetailsModal}
                customerDetailsFormData={customerDetailsFormData}
                handleShowCustomerDetailsModal={handleShowCustomerDetailsModal}
                handleCloseCustomerDetailsModal={handleCloseCustomerDetailsModal}
                handleChangeCustDetails={handleChangeCustDetails}
                handleSubmitCustDetails={handleSubmitCustDetails}
                loyaltyIsLoggedIn={loyaltyMember.MenuNav.IsLoggedIn}
                pickupOrDelivery={pickupOrDelivery}
            />
        );
    }

    return (
        <Routes>
            <Route path="*" element={
                <MenuPage
                    // --- Generic ---
                    alertModal={alertModal}
                    handleConfirmChangeLocation={handleConfirmChangeLocation}
                    handleCancelChangeLocation={handleCancelChangeLocation}
                    errorModal={errorModal}
                    handleCloseErrorModal={handleCloseErrorModal}
                    offlineModal={offlineModal}
                    handleCloseOfflineModal={handleCloseOfflineModal}
                    // --- Menu Navigation ---
                    fullMenuData={fullMenuData}
                    menuError={menuError}
                    handleScrollspySelectorClick={handleScrollspySelectorClick}
                    urlConfig={urlConfig}
                    // Cart
                    amountInCart={amountInCart}
                    handleViewCart={handleViewCart}
                    // Location
                    showLocModal={showLocModal}
                    locationList={locationList}
                    locationLoading={locationLoading}
                    locationSearchInputRef={locationSearchInputRef}
                    handleFocusSearchLocation={handleFocusSearchLocation}
                    getGeoLocation={getGeoLocation}
                    handleSearchLocation={handleSearchLocation}
                    handleClearSearchLocation={handleClearSearchLocation}
                    handleToggleOpenLocation={handleToggleOpenLocation}
                    handleSelectLocation={handleSelectLocation}
                    handleShowLocModal={handleShowLocModal}
                    handleCloseLocModal={handleCloseLocModal}
                    currentLocation={currentLocation}
                    locationError={locationError}
                    // Pickup - Shown in Location
                    handleBackPickupLocationsToMethodClick={handleBackPickupLocationsToMethodClick}
                    // Pickup / Delivery
                    pickupOrDelivery={pickupOrDelivery}
                    handleSelectMethodPickupDeliveryModal={handleSelectMethodPickupDeliveryModal}
                    handleBackAddressToMethodClick={handleBackAddressToMethodClick}
                    handleChangeAddressFields={handleChangeAddressFields}
                    handleSubmitAddressFields={handleSubmitAddressFields}
                    handlePickupDeliverySwitch={handlePickupDeliverySwitch}
                    handleCloseMethodChangeModal={handleCloseMethodChangeModal}
                    handleConfirmMethodChange={handleConfirmMethodChange}
                    // Ward
                    wardDetails={wardDetails}
                    handleChangeWardModalFields={handleChangeWardModalFields}
                    handleSubmitWardFields={handleSubmitWardFields}
                    // --- Loyalty Member ---
                    loyaltyMember={loyaltyMember}
                    handleClickOpenLoyaltyModal={handleClickOpenLoyaltyModal}
                    handleClickCloseLoyaltyModal={handleClickCloseLoyaltyModal}
                    // Login Page
                    handleClickLoginLoyaltyMobile={handleClickLoginLoyaltyMobile}
                    // Mobile Page
                    handleChangeMobileFields={handleChangeMobileFields}
                    handleNextValidateMobile={handleNextValidateMobile}
                    // OTP Page
                    handleBackOtpPage={handleBackOtpPage}
                    handleChangeOtpFields={handleChangeOtpFields}
                    handleResendOtpToMobile={handleResendOtpToMobile}
                    handleNextValidateOtp={handleNextValidateOtp}
                    // Voucher List Page
                    handleLoyaltyLogout={handleLoyaltyLogout}
                    handleSelectVoucher={handleSelectVoucher}
                    // Item Voucher Parent Page
                    handleBackItemVoucherParent={handleBackItemVoucherParent}
                    handleSelectGroupItemVoucherParent={handleSelectGroupItemVoucherParent}
                    handleAddVoucherToCartItemVoucherParent={handleAddVoucherToCartItemVoucherParent}
                    // Item Voucher Product List Page
                    handleBackItemVoucherProductList={handleBackItemVoucherProductList}
                    handleSelectProductItemVoucherProductList={handleSelectProductItemVoucherProductList}
                    // Item Voucher Child Details Page
                    handleBackItemVoucherChild={handleBackItemVoucherChild}
                    handleModChangeItemVoucherChild={handleModChangeItemVoucherChild}
                    handleAddToVoucherItemVoucherChild={handleAddToVoucherItemVoucherChild}
                    // --- Product Menu ---
                    productsLoading={productsLoading}
                    productData={productData}
                    handleProductMenuClick={handleProductMenuClick}
                    // --- Product Modal ---
                    // General
                    productModalDisplay={productModalDisplay}
                    addToCartError={addToCartError}
                    handleAddProductToCart={handleAddProductToCart}
                    handleProductModalClose={handleProductModalClose}
                    handleProductFormAddQuantity={handleProductFormAddQuantity}
                    handleProductFormSubtractQuantity={handleProductFormSubtractQuantity}
                    // Product
                    productDetails={productDetails}
                    productFormData={productFormData}
                    handleProductFormChange={handleProductFormChange}
                    handleModFormChange={handleModFormChange}
                    // Product Combo
                    productComboChildDetails={productComboChildDetails}
                    productComboChildFormData={productComboChildFormData}
                    addToProductComboError={addToProductComboError}
                    handleProductComboChildFormChange={handleProductComboChildFormChange}
                    handleProductComboModFormChange={handleProductComboModFormChange}
                    handleProductComboGroupSelect={handleProductComboGroupSelect}
                    handleBackProductComboGroupSelect={handleBackProductComboGroupSelect}
                    handleSelectProductInComboList={handleSelectProductInComboList}
                    handleBackProductComboChild={handleBackProductComboChild}
                    handleAddToProductCombo={handleAddToProductCombo}
                    // Modifier Combo
                    modifierComboDetails={modifierComboDetails}                
                    handleModifierComboSelect={handleModifierComboSelect}
                    handleBackModifierComboSelect={handleBackModifierComboSelect}
                    handleModifierComboGroupSelect={handleModifierComboGroupSelect}
                    handleBackModifierComboGroupSelect={handleBackModifierComboGroupSelect}
                    handleModifierChangeModComboGroup={handleModifierChangeModComboGroup}
                    handleModifierComboAddSelectionToGroup={handleModifierComboAddSelectionToGroup}
                    handleModComboAddComboToProduct={handleModComboAddComboToProduct}
                    handleRemoveModifierComboFromProduct={handleRemoveModifierComboFromProduct}
                    errorModifierCombo={errorModifierCombo}
                />
            } />
            <Route path="/cart" element={<CartPage
                // --- Generic ---
                errorModal={errorModal}
                handleCloseErrorModal={handleCloseErrorModal}
                offlineModal={offlineModal}
                handleCloseOfflineModal={handleCloseOfflineModal}
                // --- Cart Page Details ---
                cartDetails={cartDetails}
                cartError={cartError}
                currentLocation={currentLocation}
                orderPayBody={orderPayBody}
                handleBackClickCart={handleBackClickCart}
                handlePickupTimeChange={handlePickupTimeChange}
                handleCalendarChange={handleCalendarChange}
                handleQuantityChange={handleQuantityChange}
                handleRemoveProdFromCart={handleRemoveProdFromCart}
                handleRemoveVoucherFromCart={handleRemoveVoucherFromCart}
                handleCartNotes={handleCartNotes}
                handleValidateFulfillmentTime={handleValidateFulfillmentTime}
                handleValidateCart={handleValidateCart}
                urlConfig={urlConfig}
                // --- Customer Details Modal ---
                showCustomerDetailsModal={showCustomerDetailsModal}
                customerDetailsFormData={customerDetailsFormData}
                handleShowCustomerDetailsModal={handleShowCustomerDetailsModal}
                handleCloseCustomerDetailsModal={handleCloseCustomerDetailsModal}
                handleChangeCustDetails={handleChangeCustDetails}
                handleSubmitCustDetails={handleSubmitCustDetails}
                // --- Loyalty ---
                loyaltyIsLoggedIn={loyaltyMember.MenuNav.IsLoggedIn}
                // --- Pickup / Delivery ---
                pickupOrDelivery={pickupOrDelivery}
            />} />
            <Route path="/orderstatus" element={<OrderStatusPage
                // --- Generic ---
                errorModal={errorModal}
                handleCloseErrorModal={handleCloseErrorModal}
                // --- Order Status Details ---
                orderStatusDetails={orderStatusDetails}
                handleStartNewOrderClickOrderStatus={handleStartNewOrderClickOrderStatus}
                urlConfig={urlConfig}
            />} />
        </Routes>
    )
    // ====================== end: Routes ======================
}